/**
 * Object operations: how to run, clean, maintain and set up the things in a room, step by step, so a person can walk
 * into somebody else's kitchen, coffee bar, bathroom or laundry and do it right (owner, 2026-09-29). Every step is one
 * short Easy line (easy.ts lintEasy, docs/STANDARDS.md S15); the longer sourced wording sits in `detail` under "More".
 *
 * Sources: every op cites research rows as "<research file>#<row id>" (docs/source/research/<file>.md). New rows for
 * this file live in docs/source/research/object-operations.md (FC Food Code 2022, BV Breville BES870, OC coffee makers,
 * OK commercial kitchen makers and OSHA, OH home appliance makers and agencies). Ops that reuse an existing guide take
 * its Easy steps word for word (task guides: taskguides.ts; home guides: home/guides.ts), so wording stays the same
 * everywhere. Where makers differ, the op is `basis: "maker"` with a "Check your machine's manual" step and the common
 * range with its sources. Where nothing is sourced, the op is `basis: "suggested"` and its citations say why.
 *
 * Gates (test/objectops-data.test.ts):
 * | OO1 Shape | every object has an icon and at least one op; ids unique; café objects have open and close ops |
 * | OO2 Sourced | every citation resolves to a research row with a quote, or is a "Suggested: <reason>" line on a suggested op |
 * | OO3 No invented numbers | every number in a step, detail, interval, timer or param default appears in a cited quote |
 * | OO4 Easy | every step passes lintEasy, has a picture, and safety steps carry their full wording in `detail` |
 * | OO5 Honest basis | law ops cite a Food Code or Utah row; OSHA ops cite an OSHA row |
 * | OO6 No contradiction | sanitizer strengths and dish machine temperatures match sanitizer.ts and Food Code rows |
 * Brand names are examples named in the sources, not endorsements.
 */
import { isWarningStep, stepsForChoice, warningsFirst, type EasyIcon, type EasyStep } from "./easy.ts";
import { TASK_GUIDES } from "./taskguides.ts";
import { HOME_GUIDES } from "./home/guides.ts";
import { OBJECTOPS_UT } from "./states/ut/objectops-data.ts";
import { perStateTable } from "./states/access.ts";
import type { StateWords, UsState } from "./states/types.ts";
import { wordsFor } from "./states/words.ts";
import { stateOrDefault } from "./states/index.ts";

const WORDS: Record<UsState, StateWords<typeof OBJECTOPS_UT>> = perStateTable("OBJECTOPS", OBJECTOPS_UT);
/** The state's words for this module; a null slot leaves that sentence or source out. */
export const objectopsWordsFor = (state?: UsState) => wordsFor(WORDS, state);
type ObjectopsWords = ReturnType<typeof objectopsWordsFor>;
/** " (x)" or nothing; " x" or nothing: compose a state part into a sentence without leaving a gap. */
const par = (x: string | null) => (x ? ` (${x})` : "");
const sp = (x: string | null) => (x ? ` ${x}` : "");

export type ObjectCategory = "coffee" | "cooking" | "cold" | "hot_holding" | "dish" | "sink" | "cleaning_tool" | "bath" | "bedroom" | "laundry" | "electronics" | "furniture" | "outdoor" | "other";
export type OpKind = "operate" | "clean" | "maintain" | "setup";
export type Basis = "law" | "osha" | "maker" | "suggested";
/** When in the day an op happens, so an Open / Run / Close sequence can be put together from the ops. */
export type OpPhase = "open" | "run" | "close" | "anytime";

export interface OpParam {
  key: string;
  label: string;
  unit?: string;
  example?: string;
  /** e.g. "bean" → one value per bean. */
  perItem?: string;
  /** A sourced starting value ("15–18"), used until the person in charge sets their own. */
  default?: string;
  /** The research row the default comes from ("breville#BV10" style citation). */
  defaultSource?: string;
}
export interface OpStep {
  text: string;
  icon?: string;
  timerSec?: number;
  ask?: { kind: "yesno" | "number" | "choice"; label: string; unit?: string; choices?: string[] };
  safety?: boolean;
  /** Full sourced wording under "More". */
  detail?: string;
}
/**
 * An optional how-to video from a maker or a retailer (owner, 2026-09-30, Clive Coffee's "Essential Home Espresso Care in
 * Under 10 Minutes"). A plain link that opens outside the app, never an embed (privacy and the CSP), crediting the channel by
 * name. It carries NO step text: our steps come from written sources; the video is only "watch how". A timestamp is used
 * only where the video's own description names the chapter (`chapter`). `source` is a row of research/maker-supplies.md.
 */
export interface OpVideo {
  title: string;
  channel: string;
  /** Who made it: the maker, or a retailer (Clive Coffee), shown as such. */
  by: "maker" | "retailer";
  url: string;
  startSec?: number;
  /** The chapter the timestamp is, as the video's description names it ("Daily Machine Upkeep"). */
  chapter?: string;
  source: string;
}
export interface ObjectOp {
  id: string;
  /** Optional "Watch how" videos from the maker or a retailer (a plain link, no step text from them). */
  videos?: OpVideo[];
  /** "Turn on", "Backflush with cleaner". */
  title: string;
  kind: OpKind;
  steps: OpStep[];
  /** "Daily at close": sourced. */
  interval?: string;
  basis: Basis;
  citations: string[];
  params?: OpParam[];
  phase?: OpPhase;
  /** The task guide (taskguides.ts) these steps come from, when they are reused word for word. */
  taskGuide?: string;
  /** The home guide (home/guides.ts) these steps come from, when they are reused word for word. */
  homeGuide?: string;
  /**
   * Who services it, when a contractor, dealer or inspector does the work and staff keep the proof (fire extinguisher, bait
   * stations, grease trap, generator, propane, water filter). The op's own steps say it in Easy words; this is the same
   * thing as data: who, the proof to keep, and the app's record check it goes with (a templates.ts id), when there is one.
   * Nothing here invents a cleaning step for staff (test/cleaning-guide-gaps.test.ts G2).
   */
  service?: { who: string; proof?: string; check?: string };
}
export interface ObjectType {
  id: string;
  name: string;
  category: ObjectCategory;
  /** An icon both apps draw (Icons.tsx and mobile icons.tsx), or a new name listed in ICONS_NEEDED. */
  icon: string;
  /** For search. */
  aliases: string[];
  homes: boolean;
  business: boolean;
  /** surfaces.ts guide for the same thing. */
  surfaceId?: string;
  ops: ObjectOp[];
}

/** Shown in a param with no sourced default at a business. */
export const ASK_PIC = "Ask the person in charge";
/** Shown in a param with no sourced default at home. */
export const CHECK_MANUAL = "Check your machine's manual";

/** Icons both apps already draw (the names in apps/web Icons.tsx that apps/mobile icons.tsx also has). */
export const SHARED_ICONS = ["check", "x", "today", "do", "report", "more", "phone", "text", "bell", "pin", "cart", "book", "search", "clock", "thermo", "box", "info", "bug", "drop", "sick", "bolt", "flask", "wrench", "stop", "trash", "spray", "soap", "fridge", "bandage", "towel", "sign", "write", "microwave", "moon"] as const;

/** New object pictures the apps need to draw (each app maps a missing one to "box" until drawn). */
export const ICONS_NEEDED: string[] = [
  "espresso", "grinder", "coffee-maker", "kettle", "cold-brew", "pitcher", "scale", "french-press",
  "stove", "oven", "fryer", "griddle", "toaster", "blender", "slicer", "air-fryer", "pressure-cooker", "hood", "pan", "mixer",
  "ice", "freezer", "steam-table", "dishwasher", "sink", "disposal", "mop", "vacuum", "sponge",
  "toilet", "shower", "mirror", "bed", "closet", "washer", "dryer", "alarm", "table", "floor", "extinguisher", "water-heater", "fan", "counter",
  // More equipment (2026-09-30): drawn by the art pass (Gemini icons); until then each app shows "box".
  "cutting-board", "can-opener", "dumpster", "bait-station", "water-tank", "generator", "propane", "grease-trap", "warmer", "steam-kettle",
  "roaster", "chafing-dish", "rice-cooker", "waffle", "panini", "soda-fountain", "tea-brewer", "urn", "syrup-pump", "whipped-cream",
  "frother", "tap", "water-filter", "charbroiler", "salamander", "prep-table", "food-processor", "immersion-blender", "sous-vide", "display-case",
];

/* ───────────────────────── helpers ───────────────────────── */

const S = (icon: EasyIcon, text: string, extra: Omit<OpStep, "icon" | "text"> = {}): OpStep => ({ icon, text, ...extra });
/** A risky step: short line, full sourced wording in detail. */
const W = (icon: EasyIcon, text: string, detail: string, extra: Omit<OpStep, "icon" | "text" | "safety" | "detail"> = {}): OpStep => ({ icon, text, safety: true, detail, ...extra });

const rows = (file: string) => (...ids: string[]) => ids.map((id) => `${file}#${id}`);
const OO = rows("object-operations");
const BT = rows("barista-tools");
const HCA = rows("home-cleaning-appliances");
const HLB = rows("home-laundry-bedroom");
const HMB = rows("home-maintenance-bathroom");
const HK = rows("hotel-housekeeping");
const TGR = rows("task-guides");
const SS = rows("sanitizer-selection");
const OSH = rows("osha-housekeeping");
const HFS = rows("home-food-safety");
const HYG = rows("hygiene-and-kits");
const KA = rows("kitchenaid-appliances");
const DD = rows("dishwasher-and-dishes");
const SUGG = (reason: string) => `Suggested: ${reason}`;

/** Clive Coffee's care video: chapters (1:37 Daily Machine Upkeep, 2:15 Weekly & Monthly Machine Cleaning) from its own description (research/maker-supplies.md). */
const CLIVE_CARE = (startSec: number, chapter: string): OpVideo => ({
  title: "Essential Home Espresso Care in Under 10 Minutes", channel: "Clive Coffee", by: "retailer", url: "https://www.youtube.com/watch?v=jepo6o-HgyQ", startSec, chapter, source: "maker-supplies#MSV1",
});

type OpInit = Omit<ObjectOp, "id" | "steps" | "title" | "kind" | "basis" | "citations">;
/** Warnings first (easy.ts warningsFirst) for an equipment op: its never / don't lines come before its first action. */
const opWarningsFirst = (steps: OpStep[]): OpStep[] => {
  const warn = (x: OpStep) => isWarningStep({ icon: (x.icon ?? "check") as EasyIcon, say: x.text });
  return [...steps.filter(warn), ...steps.filter((x) => !warn(x))];
};
const op = (id: string, title: string, kind: OpKind, basis: Basis, citations: string[], steps: OpStep[], more: OpInit = {}): ObjectOp =>
  ({ id, title, kind, steps: opWarningsFirst(steps), basis, citations, ...more });

const easyToStep = (e: EasyStep, safetyDetail: string): OpStep => ({
  icon: e.icon, text: e.say,
  ...(e.waitSeconds ? { timerSec: e.waitSeconds } : {}),
  ...(e.check ? { ask: { kind: "yesno" as const, label: e.check.ask }, detail: `If not: ${e.check.ifNo}` } : {}),
  ...(e.icon === "stop" ? { safety: true, detail: safetyDetail } : {}),
});

/** An op whose steps are a task guide's Easy steps, word for word, with the guide's citations. */
function fromTask(id: string, key: string, kind: OpKind, basis: Basis, more: OpInit & { title?: string; extraCitations?: string[]; safetyAt?: Record<number, string> } = {}): ObjectOp {
  const g = TASK_GUIDES[key];
  if (!g?.easy) throw new Error(`No task guide ${key}`);
  const never = (g.never ?? []).join(" ") || (g.why ?? g.steps.join(" "));
  const { title, extraCitations, safetyAt, ...rest } = more;
  // `safetyAt`: a reused step that is risky (e.g. "throw away the first espresso") gets the safety flag and its label wording.
  const steps = g.easy.steps.map((e, i) => { const st = easyToStep(e, never); return safetyAt?.[i] ? { ...st, safety: true, detail: safetyAt[i] } : st; });
  return { id, title: title ?? g.easy.title, kind, basis, citations: [...g.citations, ...(extraCitations ?? [])], steps, taskGuide: key, ...rest };
}

/** "home-cleaning-appliances B1" → "home-cleaning-appliances#B1"; other citation kinds are dropped. */
const homeCite = (c: string): string | undefined => { const m = /^([a-z-]+) ([A-Z]+\d+)$/.exec(c); return m ? `${m[1]}#${m[2]}` : undefined; };

/** An op whose steps are a home guide's Easy steps, word for word, with the guide's citations. */
function fromHome(id: string, guideId: string, kind: OpKind, more: OpInit & { title?: string; basis?: Basis; extraCitations?: string[]; untimed?: number[]; /** A guide with a choice: the way this op shows (an op is a plain list, so the choice step itself is left out). */ way?: string } = {}): ObjectOp {
  const g = HOME_GUIDES.find((x) => x.id === guideId);
  if (!g) throw new Error(`No home guide ${guideId}`);
  const never = g.sections.find((s) => s.title === "Never")?.items.join(" ") || g.easy.why;
  const cites = g.citations.map(homeCite).filter((c): c is string => !!c);
  const { title, basis, extraCitations, untimed, way, ...rest } = more;
  const easySteps = g.easy.steps.some((x) => x.choose) ? stepsForChoice(g.easy.steps, way).filter((x) => !x.choose) : g.easy.steps;
  const b: Basis = basis ?? (g.suggested || !cites.length ? "suggested" : "maker");
  return {
    id, title: title ?? g.easy.title, kind, basis: b,
    citations: [...cites, ...(extraCitations ?? []), ...(b === "suggested" ? [SUGG(g.suggested?.reason ?? "The home guide has no maker or agency source for these steps.")] : [])],
    // `untimed`: steps whose timer no cited quote gives (the words stay; the timer button goes).
    steps: (g.easy.steps.some((x) => x.choose) ? warningsFirst(easySteps.map((x) => ({ ...x, only: undefined }))) : easySteps).map((e, i) => easyToStep(untimed?.includes(i) ? { ...e, waitSeconds: undefined } : e, never)), homeGuide: guideId, ...rest,
  };
}

/* ───────────────────────── shared safety wording ───────────────────────── */

const NEVER_MIX = "Never mix household bleach (or any disinfectants) with any other cleaners or disinfectants (CDC). Bleach plus ammonia makes chloramine gas; bleach plus acids such as vinegar and some toilet bowl cleaners makes chlorine gas (Poison Control).";
const HAND_SINK_ONLY = "A handwashing sink is kept reachable at all times and is used only for handwashing (Food Code 5-205.11). A dish sink is never used for handwashing (Food Code 4-501.16).";
const MOP_WATER = "Never clean tools, fill buckets, or dump mop water in a food prep sink, hand sink or dish sink (Food Code 6-501.14, task-guides TG16).";

/* ───────────────────────── the catalog ───────────────────────── */

const DISH_AND_HOUSE: ObjectType[] = [
  /* ═════════════ DISH ═════════════ */
  {
    id: "three-compartment-sink", name: "Three-compartment sink", category: "dish", icon: "sink", homes: false, business: true, surfaceId: "three-compartment-sink",
    aliases: ["3 compartment sink", "three sink", "dish sink", "pot sink", "wash rinse sanitize"],
    ops: [
      op("sink3.setup", "Set up the three sinks", "setup", "law", [...OO("FC11", "FC13", "FC17", "FC5", "FC6", "FC8"), ...TGR("TG12"), ...SS("S2", "S26")], [
        S("clean", "Clean and sanitize all three sinks first."),
        S("soap", "Fill sink one with soapy water.", { detail: "Wash water at least 110°F, or the temperature on the detergent label (Food Code 4-501.19). The wash sink holds detergent used by its label (4-501.17)." }),
        S("thermo", "Check the soapy water is 110°F or hotter.", { ask: { kind: "number", label: "Wash water temperature", unit: "°F" } }),
        S("water", "Fill sink two with clean rinse water."),
        S("chemical", "Fill sink three with sanitizer as its label says."),
        S("check", "Test sink three with the matching strip.", { ask: { kind: "yesno", label: "Is the strip the right color for your label?" }, detail: "Quat: 200–400 ppm per label (Steramine 200–400 ppm), water 75°F or warmer, hardness 500 mg/L or less (Food Code 4-501.114(C)). Chlorine 50–99 mg/L needs 100°F water (75°F at pH 8 or less); never under 50. Check with a test kit (4-501.116)." }),
      ], { phase: "open", interval: "Before use, and at least every 24 hours if used", taskGuide: "tubs" }),
      fromTask("sink3.wash", "wash", "operate", "law", { phase: "run", title: "Wash dishes by hand", extraCitations: OO("FC14", "FC16") }),
      op("sink3.change", "Change dirty sink water", "clean", "law", [...OO("FC12"), ...SS("S2")], [
        S("check", "Look at the water in each sink."),
        S("water", "Drain a sink that is dirty or greasy."),
        S("water", "Refill it the same way as before."),
        S("check", "Test the sanitizer again with the strip."),
      ], { phase: "run", interval: "When the water is dirty or greasy" }),
      op("sink3.close", "Close down the sinks", "clean", "law", [...OO("FC11", "FC18"), ...TGR("TG16", "TG9")], [
        S("water", "Drain all three sinks."),
        S("clean", "Clean the sinks and drainboards."),
        W("stop", "Never wash hands in the dish sink.", HAND_SINK_ONLY),
        W("stop", "Never dump mop water in the dish sink.", MOP_WATER),
      ], { phase: "close", interval: "At least every 24 hours if used" }),
    ],
  },
  {
    id: "dish-machine-high-temp", name: "Dish machine (hot water sanitizing)", category: "dish", icon: "dishwasher", homes: false, business: true, surfaceId: "dishwasher",
    aliases: ["dishwasher", "dish machine", "warewasher", "high temp dishwasher", "hobart", "hot water dishwasher"],
    ops: [
      op("dmh.check", "Check the temperatures", "operate", "law", OO("FC1", "FC3", "FC4", "FC9", "FC10", "FC34"), [
        S("sign", "Read the data plate on the machine."),
        S("power", "Run one cycle with an empty rack."),
        S("thermo", "Read the wash temperature on the gauge.", { ask: { kind: "number", label: "Wash temperature", unit: "°F" } }),
        S("thermo", "Read the final rinse temperature on the gauge.", { ask: { kind: "number", label: "Final rinse temperature", unit: "°F" } }),
        S("check", "Check both match the data plate.", { detail: "Wash at least 165°F (stationary rack, single temperature), 150°F (stationary rack, dual temperature), 160°F (single-tank conveyor, dual temperature) or 150°F (multitank conveyor) (Food Code 4-501.110(A)). Final rinse at the manifold at least 165°F (stationary rack, single temperature) or 180°F (all other machines), and not more than 194°F (4-501.112). Rinse pressure within the data plate range, 5 to 30 psi (4-501.113)." }),
        W("stop", "Stop using it if a number is too low.", "Run the machine only by its data plate and the maker's instructions (Food Code 4-501.15). If the wash or rinse is below the data plate, switch to the three-compartment sink until it is repaired."),
      ], {
        phase: "open", interval: "At opening, before the first rack",
        params: [
          { key: "washTemp", label: "Wash temperature, from the data plate", unit: "°F", example: ASK_PIC },
          { key: "rinseTemp", label: "Final rinse temperature", unit: "°F", default: "180 or hotter, 194 at most (165 on a single-temperature stationary rack)", defaultSource: "object-operations#FC3" },
        ],
      }),
      op("dmh.close", "Clean the machine at close", "clean", "law", OO("FC11", "FC10", "OK90", "OK91"), [
        S("power", "Turn the machine off as its manual says."),
        S("water", "Drain the machine as its manual says.", { detail: "Hobart AM16 example: press the Power button for 5 seconds (until progress bar is complete); the machine will automatically drain." }),
        S("clean", "Take out and clean the scrap screens.", { detail: "Remove and empty the strainer basket, pan and standpipe (Hobart)." }),
        S("clean", "Clean the wash arms and the inside.", { detail: "Do not bang wash arms or rinse arms to clean (Hobart)." }),
        S("check", "Leave the door open to dry.", { detail: "Leave machine door open to allow interior to air out and dry (Hobart)." }),
      ], { phase: "close", interval: "At least every 24 hours if used" }),
      op("dmh.delime", "Delime the dish machine", "maintain", "maker", OO("OK92", "OK93", "OK94", "FC10"), [
        S("sign", "Read the machine's manual for its delime steps."),
        S("clean", "Clean the strainer basket first."),
        S("gloves", "Wear the gloves and goggles the label names."),
        S("chemical", "Add the delime chemical as its label says.", { detail: "Hobart AM16: delime chemical for a 10.5-gallon wash tank; the unit runs a 10-minute wash cycle, then a 1-minute wash cycle to flush any remaining delime chemical residue." }),
        S("power", "Run the delime cycle, then the flush cycle."),
        W("stop", "Never use home dishwasher soap in it.", "DO NOT use detergents formulated for residential dishwashers. Keep water hardness at 3 grains or less per gallon (Hobart)."),
      ], { interval: "As the machine's manual says" }),
    ],
  },
  {
    id: "dish-machine-low-temp", name: "Dish machine (chemical sanitizing)", category: "dish", icon: "dishwasher", homes: false, business: true, surfaceId: "dishwasher",
    aliases: ["low temp dishwasher", "chemical dishwasher", "dish machine", "warewasher", "chlorine dishwasher"],
    ops: [
      op("dml.check", "Check the machine", "operate", "law", [...OO("FC2", "FC5", "FC8", "FC9", "FC10")], [
        S("sign", "Read the data plate on the machine."),
        S("chemical", "Check the sanitizer container is not empty."),
        S("power", "Run one cycle with an empty rack."),
        S("thermo", "Read the wash temperature on the gauge.", { ask: { kind: "number", label: "Wash temperature", unit: "°F" } }),
        S("check", "Test the rinse water with a chlorine strip.", { ask: { kind: "yesno", label: "Does the strip match the data plate?" }, detail: "Chemical-sanitizing machines wash at 120°F or hotter (Food Code 4-501.110(B)). Chlorine at 50–99 mg/L needs water at 100°F, or 75°F at pH 8 or less; 25–49 mg/L needs 120°F (4-501.114(A)). Sanitizer strength is checked with a test kit (4-501.116)." }),
        W("stop", "Stop using it if the strip is too light.", "Run the machine only by its data plate and the maker's instructions (Food Code 4-501.15). If the sanitizer is weak or the wash is below 120°F, switch to the three-compartment sink until it is fixed."),
      ], {
        phase: "open", interval: "At opening, before the first rack",
        params: [
          { key: "washTemp", label: "Wash temperature", unit: "°F", default: "120 or hotter", defaultSource: "object-operations#FC2" },
          { key: "chlorine", label: "Sanitizer strength, from the data plate", unit: "ppm", example: ASK_PIC },
        ],
      }),
      op("dml.close", "Clean the machine at close", "clean", "law", OO("FC11", "FC10", "OK90", "OK91"), [
        S("power", "Turn the machine off as its manual says."),
        S("water", "Drain the machine as its manual says.", { detail: "Hobart AM16 example: press the Power button for 5 seconds (until progress bar is complete); the machine will automatically drain." }),
        S("clean", "Take out and clean the scrap screens.", { detail: "Remove and empty the strainer basket, pan and standpipe (Hobart)." }),
        S("clean", "Clean the wash arms and the inside.", { detail: "Do not bang wash arms or rinse arms to clean (Hobart)." }),
        S("check", "Leave the door open to dry.", { detail: "Leave machine door open to allow interior to air out and dry (Hobart)." }),
      ], { phase: "close", interval: "At least every 24 hours if used" }),
    ],
  },
  {
    id: "home-dishwasher", name: "Dishwasher (home)", category: "dish", icon: "dishwasher", homes: true, business: false,
    aliases: ["dishwasher", "dish washer", "load the dishwasher", "run the dishwasher"],
    ops: [
      fromHome("hdw.load", "load-dishwasher", "operate", { interval: "Each load" }),
      fromHome("hdw.filter", "dishwasher-filter", "maintain", { interval: "About once a month (Whirlpool)" }),
    ],
  },
  {
    id: "drying-rack", name: "Dish drying rack or mat", category: "dish", icon: "towel", homes: true, business: false,
    aliases: ["drying rack", "dish rack", "drying mat", "dish mat"],
    ops: [fromHome("rack.clean", "drying-mat", "clean")],
  },

  /* ═════════════ SINKS ═════════════ */
  {
    id: "hand-sink", name: "Handwashing sink", category: "sink", icon: "soap", homes: false, business: true, surfaceId: "hand-sink",
    aliases: ["hand sink", "handwashing sink", "wash hands", "soap and towels"],
    ops: [
      op("hsink.stock", "Stock the hand sink", "setup", "law", [...OO("FC29", "FC30", "FC31"), ...TGR("TG27", "TG28")], [
        S("soap", "Fill the soap."),
        S("towel", "Fill the paper towels."),
        S("sign", "Check the handwashing sign is up."),
        S("water", "Run the water and feel that it gets warm.", { detail: "A hand sink gives water at least 85°F; a self-closing faucet runs at least 15 seconds (Food Code 5-202.12)." }),
        S("box", "Take away anything in or blocking the sink."),
        W("stop", "Never use this sink for dishes or food.", HAND_SINK_ONLY),
      ], { phase: "open", interval: "At opening and whenever soap or towels run low" }),
      fromTask("hsink.handwash", "handwash", "operate", "law", { phase: "run", title: "Set up hand washing" }),
      op("hsink.clean", "Clean the hand sink", "clean", "law", OO("FC30", "FC31", "FC33"), [
        S("box", "Take out anything that is in the sink."),
        S("clean", "Scrub the bowl and the faucet handles."),
        S("soap", "Refill the soap and paper towels."),
      ], { phase: "close", interval: "Often enough that dirt does not build up (Food Code 4-602.13)" }),
    ],
  },
  {
    id: "kitchen-sink-home", name: "Kitchen sink (home)", category: "sink", icon: "sink", homes: true, business: false,
    aliases: ["kitchen sink", "sink", "one sink", "wash vegetables"],
    ops: [
      fromHome("ksink.raw-veg", "one-sink-raw-to-veg", "operate"),
      op("ksink.clean", "Clean the kitchen sink", "clean", "maker", HFS("H24"), [
        S("soap", "Wash the sink with hot soapy water."),
        S("water", "Rinse it."),
        S("spray", "Put on a sanitizer as its label says."),
        S("check", "Do this every time raw meat touches the sink."),
      ], { interval: "After raw meat or poultry (USDA)" }),
    ],
  },
  {
    id: "garbage-disposal", name: "Garbage disposal", category: "sink", icon: "disposal", homes: true, business: false,
    aliases: ["garbage disposal", "disposal", "disposer", "insinkerator", "sink grinder", "disposal jam"],
    ops: [
      op("disposal.use", "Use the disposal", "operate", "maker", OO("OH76", "OH77", "OH78", "OH91"), [
        S("water", "Turn on the cold water."),
        S("power", "Turn on the disposal."),
        S("trash", "Put food scraps in a little at a time.", { detail: "Do NOT pre-load the disposal. Gradually feed food waste into the disposal (InSinkErator)." }),
        W("stop", "Never put grease or oil down the disposal.", "Never put fats, oils, or grease into a garbage disposal (InSinkErator)."),
        S("wait", "Wait for the grinding sound to stop."),
        S("water", "Let the cold water run 15 seconds more.", { timerSec: 15, detail: "Continue to run the cold water for 15 seconds after grinding is complete to flush the drain line (Whirlpool). InSinkErator says to let water run for a few seconds." }),
        S("power", "Turn off the disposal and the water."),
      ], { interval: "Each use", phase: "anytime" }),
      op("disposal.jam", "If the disposal is stuck", "operate", "maker", OO("OH79", "OH80", "OH92"), [
        W("power", "Turn off the disposal switch and the water.", "Turn the wall switch to the Off position (continuous-feed disposer), or remove the stopper (batch-feed disposer). Turn off the cold water (Whirlpool)."),
        S("wrench", "Turn the small wrench in the hole underneath.", { detail: "Insert an end of your wrenchette (provided with your disposer) into the center hole of the bottom. Work it back and forth until it turns one complete revolution, then take it out (Whirlpool)." }),
        W("hands", "Use long tongs, not your hand, to pull things out.", "Use long-handled tongs to remove the foreign object(s) (Whirlpool). The jam steps use the wrenchette and tongs, never a hand in the disposer."),
        S("wait", "Wait 3 to 5 minutes for the motor to cool.", { timerSec: 300, detail: "Allow the motor to cool 3-5 minutes (Whirlpool)." }),
        S("wrench", "Press the red reset button under the disposal.", { detail: "Lightly push the red reset button on the bottom of the disposer (Whirlpool)." }),
        S("power", "Turn on the water, then the disposal."),
        S("check", "Check the breaker if it still will not run.", { detail: "If the motor remains inoperative, check the service panel for tripped breaker or fuses (Whirlpool)." }),
      ], { interval: "When it hums or will not turn" }),
      op("disposal.clean", "Freshen the disposal", "clean", "maker", HMB("HM20"), [
        S("water", "Run cool water."),
        S("food", "Put in citrus wedges one at a time to grind."),
        S("clean", "Pull out the baffle, rinse and wipe it, if yours comes out."),
        W("stop", "Never put fat, oil or grease down it.", "Never place any type of fats, oils, grease in a garbage disposal (InSinkErator)."),
      ]),
    ],
  },

  /* ═════════════ CLEANING TOOLS ═════════════ */
  {
    id: "sanitizer-bucket", name: "Sanitizer bucket and wiping cloths", category: "cleaning_tool", icon: "spray", homes: false, business: true, surfaceId: "wiping-cloth-bucket",
    aliases: ["sanitizer bucket", "red bucket", "wiping cloths", "rags", "cloth bucket"],
    ops: [
      op("sbucket.setup", "Mix the sanitizer bucket", "setup", "law", [...OO("FC5", "FC6", "FC8"), ...SS("S2", "S4", "S26"), ...TGR("TG31")], [
        S("gloves", "Put on gloves."),
        S("water", "Fill the bucket with water."),
        S("chemical", "Add sanitizer exactly as the label says."),
        S("towel", "Put the clean cloths in the bucket."),
        S("check", "Test it with the matching strip.", { ask: { kind: "yesno", label: "Is the strip the right color for your label?" }, detail: "Quat strips read in ppm quat: Steramine and Ecolab Multi-Quat labels say 200–400 ppm. Quat water 75°F or warmer (Food Code 4-501.114(C)). Chlorine at 50–99 mg/L needs 100°F water, or 75°F at pH 8 or less (4-501.114(A)). A test kit must be on hand (4-501.116)." }),
        S("box", "Set the bucket off the floor, away from food."),
      ], { phase: "open", interval: "At opening, and test it again during the day" }),
      fromTask("sbucket.care", "cloths-laundry", "operate", "law", { phase: "run" }),
      op("sbucket.close", "Empty the bucket at close", "clean", "law", OO("FC12", "FC45"), [
        S("water", "Dump the old sanitizer in the mop sink."),
        S("soap", "Wash, rinse and dry the bucket."),
        S("towel", "Send the used cloths to the laundry."),
      ], { phase: "close", interval: "At close, and when the water is dirty" }),
    ],
  },
  {
    id: "mop-bucket", name: "Mop and bucket", category: "cleaning_tool", icon: "mop", homes: true, business: true, surfaceId: "mop-sink",
    aliases: ["mop", "bucket", "mop bucket", "mopping", "floor"],
    ops: [
      fromTask("mop.floors", "close-floors", "clean", "law", { phase: "close", extraCitations: HK("HK17") }),
      fromTask("mop.store", "tool-separation", "maintain", "law", { phase: "close" }),
      fromHome("mop.home-tile", "floor-tile", "clean", { title: "Mop the tile floor at home" }),
    ],
  },
  {
    id: "vacuum", name: "Vacuum cleaner", category: "cleaning_tool", icon: "vacuum", homes: true, business: true,
    aliases: ["vacuum", "hoover", "vacuum cleaner", "carpet"],
    ops: [
      fromHome("vac.carpet", "vacuum-carpet", "operate"),
      op("vac.empty", "Empty the dirt cup", "maintain", "maker", OO("OH72", "OH94"), [
        W("power", "Unplug the vacuum.", "Unplug before cleaning or servicing. Do not use vacuum without dirt cup or filters in place (Hoover)."),
        S("box", "Take off the dirt cup."),
        S("trash", "Empty it into the trash."),
        S("box", "Put the dirt cup back on."),
      ], { interval: "Before the dirt reaches the MAX line (Hoover)" }),
      op("vac.clog", "Clear a clog", "maintain", "maker", OO("OH73", "OH74", "OH93", "OH94"), [
        W("power", "Unplug it, or take the battery out.", "Start by unplugging your vacuum. If you have a cordless vacuum, remove the battery (Hoover)."),
        S("trash", "Empty the dirt cup if it is full."),
        S("check", "Look in the pipe under the dirt cup."),
        S("water", "Rinse the filter if it is dirty.", { detail: "Check your filter and rinse or replace it if it's dirty (Hoover)." }),
        S("wait", "Let the filter dry at least 24 hours.", { detail: "Be sure to let your rinsable filter dry for at least 24 hours (Hoover)." }),
        S("check", "Look through the hose for a clog."),
        S("clean", "Push a clog out with a long blunt stick.", { detail: "Use a long, blunt object to push out any clogs (Hoover)." }),
      ]),
      op("vac.filter", "Clean the filter", "clean", "maker", OO("OH73", "OH74", "OH93", "OH94"), [
        W("power", "Unplug it, or take the battery out.", "Unplug before cleaning or servicing. If you have a cordless vacuum, remove the battery (Hoover)."),
        S("box", "Take out the filter."),
        S("sign", "Check your manual: rinse it or replace it."),
        S("water", "Rinse a washable filter in water.", { detail: "Check your filter and rinse or replace it if it's dirty (Hoover)." }),
        S("wait", "Let the filter dry at least 24 hours.", { detail: "Be sure to let your rinsable filter dry for at least 24 hours (Hoover)." }),
        W("stop", "Never run the vacuum without its filter.", "Do not use vacuum without dirt cup or filters in place (Hoover)."),
        S("box", "Put the filter back in."),
      ], { interval: "When the filter is dirty (Hoover)" }),
    ],
  },
  {
    id: "cloths-towels", name: "Kitchen towels and dishcloths", category: "cleaning_tool", icon: "towel", homes: true, business: false,
    aliases: ["dish towel", "kitchen towel", "dishcloth", "tea towel", "rag"],
    ops: [
      fromHome("ktowel.change", "dish-towels", "clean"),
      fromHome("ktowel.wash", "laundry-kitchen-towels", "clean"),
    ],
  },
  {
    id: "sponge", name: "Kitchen sponge", category: "cleaning_tool", icon: "sponge", homes: true, business: false,
    aliases: ["sponge", "scrubber", "dish sponge"],
    ops: [
      fromHome("sponge.clean", "sponge-sanitize", "clean", { untimed: [5], way: "microwave" }),
      fromHome("sponge.replace", "sponge-replace", "maintain"),
    ],
  },

  /* ═════════════ BATH ═════════════ */
  {
    id: "toilet", name: "Toilet", category: "bath", icon: "toilet", homes: true, business: true, surfaceId: "restroom-toilets",
    aliases: ["toilet", "commode", "loo", "bowl", "restroom", "bathroom"],
    ops: [
      fromHome("toilet.clean", "toilet", "clean", { interval: "Weekly (American Cleaning Institute)" }),
      fromTask("toilet.restroom", "restroom-clean", "clean", "law", { title: "Clean the restroom (business)", extraCitations: HK("HK3") }),
    ],
  },
  {
    id: "bathroom-sink", name: "Bathroom sink and counter", category: "bath", icon: "sink", homes: true, business: true, surfaceId: "restroom-sink",
    aliases: ["bathroom sink", "vanity", "basin", "lavatory", "faucet"],
    ops: [
      fromHome("bsink.clean", "bathroom-sink", "clean", { interval: "Weekly (American Cleaning Institute)" }),
      fromHome("bsink.aerator", "faucet-aerator", "maintain"),
      fromHome("bsink.trim", "beard-trimmings", "clean"),
    ],
  },
  {
    id: "shower-tub", name: "Shower and tub", category: "bath", icon: "shower", homes: true, business: false,
    aliases: ["shower", "tub", "bathtub", "bath", "shower door", "shower curtain", "grout"],
    ops: [
      op("shower.after", "After a shower", "operate", "maker", [...HMB("BS1", "BS2", "BM2", "BC2")], [
        S("power", "Turn on the fan or open the window."),
        S("water", "Rinse the tub or shower floor."),
        S("towel", "Wipe the walls with a towel or squeegee."),
        S("spray", "Spray daily shower cleaner, if you use it.", { detail: "Mist surfaces right after showering while the walls are wet and warm; no rinsing, wiping or scrubbing is needed (American Cleaning Institute). Check the label says it suits your tub or shower." }),
        S("check", "Leave the door or curtain open to dry."),
      ], { interval: "After each use", phase: "anytime" }),
      fromHome("shower.clean", "shower-tub", "clean", { interval: "Weekly (American Cleaning Institute)" }),
      fromHome("shower.curtain", "shower-curtain", "clean"),
      fromHome("shower.grout", "grout", "maintain"),
      op("shower.head", "Soak the shower head", "maintain", "maker", HMB("BH1", "BH2"), [
        S("chemical", "Pour white vinegar into a plastic bag."),
        S("box", "Put the bag over the shower head."),
        S("wrench", "Tie the bag on with a rubber band."),
        S("wait", "Leave it at least one hour, or overnight."),
        S("trash", "Take the bag off and throw it away."),
        S("water", "Run hot water through the shower head."),
      ], { interval: "At least once a week (American Standard)" }),
    ],
  },
  {
    id: "mirror", name: "Mirror", category: "bath", icon: "mirror", homes: true, business: true, surfaceId: "windows-glass",
    aliases: ["mirror", "bathroom mirror", "glass"],
    ops: [fromHome("mirror.clean", "mirrors", "clean", { interval: "Weekly (American Cleaning Institute)", extraCitations: HK("HK28") })],
  },
  {
    id: "towels", name: "Bath towels and mats", category: "bath", icon: "towel", homes: true, business: false,
    aliases: ["towel", "bath towel", "hand towel", "washcloth", "bath mat"],
    ops: [
      fromHome("towels.bath", "bath-towels", "clean"),
      fromHome("towels.hand", "hand-towels", "clean"),
      fromHome("towels.mat", "bath-mats", "clean"),
    ],
  },
  {
    id: "bath-fan", name: "Bathroom fan", category: "bath", icon: "fan", homes: true, business: false,
    aliases: ["bathroom fan", "exhaust fan", "vent fan", "extractor fan", "fan", "steam", "mold"],
    ops: [
      op("bathfan.shower", "Run the fan for a shower", "operate", "maker", OO("OH89", "OH90"), [
        S("power", "Turn on the fan or open the window.", { detail: "Run the bathroom fan or open the window when showering (EPA)." }),
        S("water", "Take your shower."),
        S("wait", "Leave the fan on 20 minutes after.", { timerSec: 1200, detail: "HVI recommends that the fan be left on for 20 minutes after use of the bathroom (HVI, an industry body; EPA gives no time)." }),
        S("power", "Turn the fan off."),
      ], { interval: "Each shower", phase: "anytime" }),
    ],
  },

  /* ═════════════ BEDROOM ═════════════ */
  {
    id: "bed", name: "Bed and bedding", category: "bedroom", icon: "bed", homes: true, business: false,
    aliases: ["bed", "sheets", "bedding", "pillowcase", "duvet", "comforter", "pillows", "make the bed"],
    ops: [
      fromHome("bed.sheets", "sheets", "clean", { interval: "Every one to two weeks (Cleveland Clinic)" }),
      fromHome("bed.make", "make-bed", "operate"),
      fromHome("bed.duvet", "duvet-cover", "clean"),
      fromHome("bed.comforter", "comforter", "clean"),
      fromHome("bed.pillows", "pillows", "clean"),
    ],
  },
  {
    id: "mattress", name: "Mattress", category: "bedroom", icon: "bed", homes: true, business: false,
    aliases: ["mattress", "mattress protector", "mattress pad"],
    ops: [
      fromHome("mattress.protector", "mattress-protector", "clean"),
      fromHome("mattress.clean", "mattress-clean", "clean"),
      fromHome("mattress.rotate", "mattress-rotate", "maintain"),
    ],
  },
  {
    id: "closet", name: "Closet and drawers", category: "bedroom", icon: "closet", homes: true, business: false,
    aliases: ["closet", "drawers", "dresser", "wardrobe", "clothes"],
    ops: [
      fromHome("closet.store", "closet-storage", "setup"),
      fromHome("closet.rotate", "closet-rotation", "maintain"),
    ],
  },

  /* ═════════════ LAUNDRY ═════════════ */
  {
    id: "washer", name: "Washing machine", category: "laundry", icon: "washer", homes: true, business: false,
    aliases: ["washer", "washing machine", "laundry", "wash clothes"],
    ops: [
      fromHome("washer.everyday", "laundry-everyday", "operate"),
      fromHome("washer.sick", "laundry-sick", "operate", { extraCitations: HYG("C2") }),
      fromHome("washer.jeans", "laundry-jeans", "operate"),
      fromHome("washer.clean", "washer-clean", "maintain", { interval: "Once a month (Whirlpool)" }),
      op("washer.load", "Load and start the washer", "operate", "maker", OO("OH59", "OH60", "OH61", "OH62", "OH63", "OH65"), [
        S("check", "Check all the pockets first.", { detail: "Check all pockets before washing for things like tissues or coins (Tide)." }),
        S("box", "Put the clothes in loosely.", { detail: "Top-load: clothes should be loosely added and only as high as the top row of holes in the wash basket (Whirlpool)." }),
        S("soap", "Pour detergent into the cap up to the label's line.", { detail: "Check the label for dosage instructions and pour the correct amount into the measuring cap. Use only high-efficiency (HE) detergents in high-efficiency machines. A pod goes directly into the drum before adding your laundry (Whirlpool)." }),
        W("stop", "Keep laundry pods shut in their box.", "Always keep the liquid laundry packets sealed in their original containers (CPSC)."),
        S("power", "Pick the cycle and press Start."),
      ], { interval: "Each load", phase: "anytime" }),
      op("washer.air", "Air out a front-load washer", "maintain", "maker", OO("OH64"), [
        S("box", "Take the clothes out when it stops."),
        S("check", "Leave the door open between loads.", { detail: "It is recommended to leave the door open between loads to help avoid the build-up of odor-causing residue (Whirlpool)." }),
      ], { interval: "Between loads", phase: "anytime" }),
    ],
  },

  /* ═════════════ FURNITURE AND OTHER ═════════════ */
  {
    id: "surfaces-dusting", name: "Tables, shelves and dressers", category: "furniture", icon: "table", homes: true, business: false,
    aliases: ["dust", "dusting", "shelves", "table", "dresser", "nightstand", "windowsill"],
    ops: [fromHome("dust.damp", "dusting", "clean")],
  },
  {
    id: "door-handles", name: "Light switches and door handles", category: "other", icon: "check", homes: true, business: true, surfaceId: "door-handles",
    aliases: ["light switch", "doorknob", "door handle", "handles", "high touch", "remote"],
    ops: [fromHome("touch.wipe", "high-touch", "clean")],
  },
  {
    id: "floors", name: "Floors", category: "other", icon: "floor", homes: true, business: false,
    aliases: ["floor", "hardwood", "wood floor", "vinyl", "tile floor", "carpet", "rug"],
    ops: [
      fromHome("floor.hardwood", "floor-hardwood", "clean"),
      fromHome("floor.vinyl", "floor-vinyl", "clean"),
      fromHome("floor.tile", "floor-tile", "clean"),
      fromHome("floor.bath", "bathroom-floor", "clean"),
      fromHome("floor.carpet-deep", "carpet-deep-clean", "maintain"),
    ],
  },
  {
    id: "kitchen-counter", name: "Kitchen counter", category: "other", icon: "counter", homes: true, business: true, surfaceId: "prep-counter",
    aliases: ["counter", "countertop", "prep table", "work table"],
    ops: [
      fromHome("counter.home", "counters", "clean", { title: "Clean the kitchen counter at home" }),
      fromTask("counter.business", "close-clean", "clean", "law", { phase: "close", extraCitations: OO("FC16", "FC19") }),
    ],
  },
  {
    id: "trash-can", name: "Trash can", category: "other", icon: "trash", homes: true, business: true, surfaceId: "trash-cans-indoor",
    aliases: ["trash", "garbage", "bin", "trash can", "garbage can", "liner", "trash bag"],
    ops: [
      fromHome("trash.out-home", "trash-out", "operate"),
      fromHome("trash.clean-home", "trash-can-clean", "clean"),
      fromTask("trash.close", "close-trash", "clean", "law", { phase: "close", extraCitations: HK("HK31", "HK32") }),
    ],
  },
  {
    id: "fire-extinguisher", name: "Fire extinguisher", category: "other", icon: "extinguisher", homes: true, business: true,
    aliases: ["fire extinguisher", "extinguisher", "fire"],
    ops: [
      fromTask("fire.check", "fire", "maintain", "osha", { interval: "Look it over every month" }),
      op("fire.who", "Who services it", "maintain", "osha", TGR("TG46", "TG47"), [
        S("phone", "Call the fire company for the yearly check."),
        S("write", "Read the tag and write down its date.", { detail: "OSHA: portable extinguishers get an annual maintenance check; the employer records the annual maintenance date and keeps the record one year after the last entry." }),
      ], { interval: "Every year (OSHA)", service: { who: "A fire protection company", proof: "The date on the extinguisher's tag", check: "fire_check" } }),
    ],
  },
  {
    id: "smoke-alarm", name: "Smoke and CO alarms", category: "electronics", icon: "alarm", homes: true, business: false,
    aliases: ["smoke alarm", "smoke detector", "carbon monoxide", "co alarm", "detector", "beeping"],
    ops: [
      op("alarm.test", "Test the alarms", "maintain", "maker", HMB("HM1", "HM3", "HM5"), [
        S("power", "Press the test button on the alarm."),
        S("check", "Wait for the loud beep.", { ask: { kind: "yesno", label: "Did it beep?" } }),
        S("phone", "Tell someone if it did not beep."),
        S("write", "Write down the date you tested it."),
      ], { interval: "At least once a month (NFPA, USFA)" }),
    ],
  },
  {
    id: "water-filter-pitcher", name: "Water filter pitcher", category: "other", icon: "drop", homes: true, business: false,
    aliases: ["brita", "water filter", "filter pitcher", "pitcher filter"],
    ops: [fromHome("pitcher.filter", "pitcher-filter", "maintain")],
  },
  {
    id: "ro-system", name: "Under-sink water filter (RO)", category: "other", icon: "drop", homes: true, business: false,
    aliases: ["reverse osmosis", "ro", "under sink filter", "water filter"],
    ops: [
      fromHome("ro.filters", "ro-filters", "maintain"),
      fromHome("ro.membrane", "ro-membrane", "maintain"),
    ],
  },
  {
    id: "water-heater", name: "Water heater", category: "other", icon: "water-heater", homes: true, business: false,
    aliases: ["water heater", "hot water heater", "hot water tank", "tank", "anode", "flush water heater"],
    ops: [
      op("wheater.service", "Book the yearly service", "maintain", "maker", HMB("HM14", "HM15", "HM16"), [
        S("write", "Find the date of the last service."),
        S("phone", "Call a plumber if a year has passed.", { detail: "A.O. Smith: service once a year, twice a year if you have hard water; it covers the relief valve test, anode check and sediment flush. Rheem: flush once a year, or every 6 months with hard water. DOE: flushing a quart of water from the storage tank every three months; inspecting the anode rod every three to four years. Read your owner's manual." }),
        S("write", "Write down the date of the service."),
      ], { interval: "Once a year; twice a year with hard water (A.O. Smith)" }),
    ],
  },
  {
    id: "dryer", name: "Clothes dryer", category: "laundry", icon: "dryer", homes: true, business: false,
    aliases: ["dryer", "clothes dryer", "tumble dryer", "dryer vent", "lint", "lint screen", "lint trap"],
    ops: [
      op("dryer.run", "Dry a load", "operate", "maker", OO("OH66", "OH67", "OH68", "OH69", "OH70", "OH71"), [
        S("trash", "Pull the lint off the screen with your fingers.", { detail: "Clean the lint filter before and after each cycle (USFA). Roll lint off the screen with your fingers (Whirlpool)." }),
        W("stop", "Never dry clothes with gasoline or paint thinner on them.", "Clothes that have come in contact with flammable substances, like gasoline, paint thinner, or similar solvents should be laid outside to dry (NFPA)."),
        S("box", "Put the wet clothes in the dryer."),
        S("check", "Close the dryer door."),
        S("power", "Pick the setting and hold Start down.", { detail: "The Start, Push to Start, or Hold to Start buttons should be pressed and held for two to five seconds to start the dryer (Whirlpool)." }),
        W("power", "Turn the dryer off if you go out or to bed.", "Turn the dryer off if you leave home or when you go to bed (NFPA)."),
      ], { interval: "Each load", phase: "anytime" }),
      op("dvent.screen", "Clean the lint screen", "clean", "maker", HMB("HM10", "HM11", "HM13"), [
        S("box", "Pull out the lint screen."),
        S("trash", "Pull the lint off with your fingers."),
        S("trash", "Throw the lint in the trash."),
        S("box", "Push the screen back in."),
        W("stop", "Never run the dryer without the lint screen.", "Never use the dryer without a lint filter. Clean the lint filter before or after drying each load of clothes, and remove lint that has collected around the drum (NFPA). Clean the lint screen/filter before or after drying each load of clothes (CPSC)."),
      ], { interval: "Before or after every load (CPSC, NFPA)", phase: "anytime" }),
      op("dvent.scrub", "Scrub the lint screen", "maintain", "maker", HMB("HM13"), [
        S("box", "Pull out the lint screen."),
        S("soap", "Scrub it with a nylon brush and soapy hot water."),
        S("water", "Rinse it well."),
        S("towel", "Let it dry all the way."),
        S("box", "Push the screen back in."),
      ], { interval: "Every six months, or sooner if clogged (Whirlpool)" }),
      op("dvent.vent", "Clean the dryer vent", "maintain", "maker", HMB("HM10", "HM11"), [
        S("phone", "Call a helper or a vent cleaning company."),
        S("clean", "Clean the lint out of the vent pipe."),
        S("check", "Check drying goes faster after."),
      ], { interval: "Once a year, or sooner if clothes take longer to dry (NFPA)" }),
    ],
  },
];

/* COFFEE_START */
const PULL_BACK = "Never remove the portafilter during the brewing operation as the machine is under pressure (Breville). Do not touch hot surfaces; use handles or knobs (Breville).";

const COFFEE: ObjectType[] = [
  {
    id: "espresso-machine", name: "Espresso machine", category: "coffee", icon: "espresso", homes: true, business: true, surfaceId: "espresso-group-heads",
    aliases: ["espresso machine", "espresso", "coffee machine", "la marzocco", "linea", "breville", "rancilio", "nuova simonelli", "group head", "portafilter"],
    ops: [
      op("espresso.turn-on", "Turn on and warm up", "operate", "maker", [...OO("OC1", "OC2", "OC4", "OC22", "OC23", "OC24", "OC3", "OC10", "BV1", "BV2", "BV3")], [
        S("water", "Check the water tank is full or the water is on.", { detail: "Always check the water level before use and replace water daily; use cold, filtered water (Breville). Never use the machine without water in the tank (Breville). Reservoir La Marzocco models: change the reservoir water daily." }),
        S("power", "Turn the main switch on.", { detail: "La Marzocco Linea PB: main switch to position 1. Linea Classic S: main switch to position 2 to heat. Breville: press POWER. Check your machine's manual." }),
        S("wait", "Wait until the machine says it is ready.", { detail: "Linea PB: the steam boiler reaches operating pressure within about 25 minutes; ready when the heating rectangles turn off and all the button lights turn on. Linea Classic S: 20 to 35 minutes, depending on size. Breville: the POWER light stops flashing." }),
        S("check", "Check the steam gauge is in its normal range.", { ask: { kind: "yesno", label: "Is the steam gauge in its normal range?" }, detail: "Linea PB steam boiler: 1.3-1.5 bar. Linea Classic S: 1.2 to 1.5 bar on the upper scale of the gauge." }),
        S("wrench", "Lock the portafilters in so they get hot.", { detail: "Leave the portafilters installed in the machine when not in use (La Marzocco)." }),
      ], {
        phase: "open", interval: "At opening",
        params: [{ key: "warmUp", label: "Warm-up time", unit: "minutes", default: "20 to 35 (La Marzocco Linea Classic S), about 25 (Linea PB)", defaultSource: "object-operations#OC23, object-operations#OC2" }],
      }),
      op("espresso.flush", "Flush after it sat overnight", "operate", "maker", OO("OC7", "OC8", "OC9"), [
        S("water", "Run water through each group for two minutes.", { timerSec: 120, detail: "If the machine has not been used for more than 8 hours, brew water through each group for at least two minutes (La Marzocco Linea PB)." }),
        W("spray", "Point each steam wand at the drip tray.", "Steam is very hot. Point the wand into the drip tray, away from hands, before you open it."),
        S("spray", "Run each steam wand for one minute.", { timerSec: 60 }),
        S("water", "Run hot water from the hot water tap.", { detail: "At least 1 liter for a 1/2 group machine, 2 liters for a 3 group machine, 3 liters for a 4 group machine (La Marzocco Linea PB)." }),
      ], { phase: "open", interval: "At opening, when it sat more than 8 hours" }),
      op("espresso.pull-shot", "Pull a shot", "operate", "maker", [...OO("OC11", "OC34", "BV7", "BV8", "BV9", "BV10", "BV11", "BV12", "BV15", "BV24")], [
        S("towel", "Wipe the basket dry."),
        S("food", "Fill the basket from the grinder."),
        S("check", "Put the basket on a scale to check the weight.", { ask: { kind: "number", label: "Coffee in the basket", unit: "g" } }),
        S("box", "Press the coffee flat and firm with the tamper.", { detail: "Tamp down firmly using approx. 30–40lbs (15–20kgs) of pressure; the same pressure every time matters more than the amount (Breville)." }),
        S("towel", "Wipe coffee off the rim of the basket."),
        S("water", "Run water through the group head for a moment."),
        S("wrench", "Lock the portafilter in and put a cup under it."),
        S("power", "Start the shot and watch the time.", { ask: { kind: "number", label: "Shot time", unit: "seconds" } }),
        S("stop", "Stop the shot at your target amount.", { ask: { kind: "number", label: "Espresso in the cup", unit: "g" }, detail: "La Marzocco: a double dose should dispense approximately 60ml / 2 fluid oz in approximately 25 seconds. Breville: a double basket gives about 2oz / 60ml, and a good shot takes 25–35 seconds." }),
        W("stop", "Never take the portafilter out while it brews.", PULL_BACK),
      ], {
        phase: "run", interval: "Each drink",
        params: [
          { key: "dose", label: "Coffee in the basket", unit: "g", perItem: "bean", default: "12-15 for a double (La Marzocco), 15–18 (Breville)", defaultSource: "object-operations#OC11, object-operations#BV10" },
          { key: "yield", label: "Espresso in the cup", unit: "ml", perItem: "bean", default: "about 60 for a double", defaultSource: "object-operations#OC34" },
          { key: "time", label: "Shot time", unit: "seconds", perItem: "bean", default: "about 25 (La Marzocco), 25–35 (Breville)", defaultSource: "object-operations#OC34, object-operations#BV12" },
        ],
      }),
      op("espresso.purge-group", "Rinse the group after each shot", "clean", "maker", [...BT("CL2"), ...OO("BV16", "OC10")], [
        S("trash", "Tap the used coffee into the knock box."),
        S("towel", "Wipe the basket clean."),
        S("water", "Run water through the group for one to three seconds.", { timerSec: 3 }),
        S("wrench", "Lock the portafilter back in to keep it hot."),
      ], { phase: "run", interval: "After each shot" }),
      op("espresso.steam-milk", "Steam milk", "operate", "maker", [...OO("BV19", "BV20", "BV21", "BV22", "OC19"), ...BT("CL3")], [
        S("fridge", "Pour cold milk just below the pitcher's spout."),
        S("spray", "Let out a short blast of steam into a towel."),
        S("food", "Put the wand tip just under the milk."),
        S("power", "Turn the steam on."),
        S("food", "Move the pitcher down a little to spin the milk."),
        S("thermo", "Stop when the pitcher is hot to touch.", { detail: "Breville: the milk is at the correct temperature (140–149°F or 60–65°C) when the jug is hot to touch. La Marzocco: bring the milk almost up to 149/158°F or 65/70°C." }),
        W("power", "Turn the steam off before you lift the pitcher.", "Turn off steam BEFORE taking the tip out of the milk (Breville). Steam and the wand are hot: keep hands clear of the tip."),
        S("towel", "Wipe the wand right away with its damp cloth."),
        S("spray", "Let out another short blast of steam."),
      ], {
        phase: "run", interval: "Each milk drink",
        params: [{ key: "milkTemp", label: "Milk temperature", unit: "°F", default: "140–149 (Breville), up to 149/158 (La Marzocco)", defaultSource: "object-operations#BV20, object-operations#OC19" }],
      }),
      op("espresso.wand-purge", "Purge and wipe the steam wand", "clean", "maker", [...BT("CL2", "CL3", "M5", "M16"), ...OO("OC18")], [
        S("spray", "Let out a short blast of steam first."),
        S("towel", "Wipe the wand right after steaming."),
        S("spray", "Let out another short blast of steam."),
      ], { phase: "run", interval: "Before and after every use" }),
      op("espresso.backflush-water", "Backflush with water", "clean", "maker", [...BT("CL4", "CL9", "M10"), ...TGR("TG88"), ...OO("OC13", "OC14", "OC15", "OC27", "OC33")], [
        S("wrench", "Put the blind basket in the portafilter."),
        S("wrench", "Lock the portafilter into the group head."),
        S("power", "Start the machine's backflush program, if it has one.", { detail: "Linea PB: press and hold T1 and T3 together; about 10 preset cycles with an interval of 4 seconds. Linea Classic S (AV): press T3+T1, then release T1; the display reads CLN." }),
        S("power", "Run the brew 10 seconds, then stop, 5 times.", { timerSec: 10, detail: "No program: brew 10 seconds, stop 10 seconds, repeat 5 times (Urnex Cafiza label timing). Linea PB paddle models: brew 3 sec, repeat ten times. Linea Classic S: stop after about 15-20 seconds and repeat until the water runs clear." }),
        W("stop", "Do one group at a time.", "RINSE ONLY ONE GROUP AT A TIME (La Marzocco): foam can overflow the drain box."),
        S("water", "Rinse the portafilter under the group head."),
        S("towel", "Wipe the shower screen with a clean cloth."),
      ], { phase: "close", interval: "Daily after making drinks (Clive)", videos: [CLIVE_CARE(97, "Daily Machine Upkeep")] }),
      fromTask("espresso.backflush-cleaner", "coffee-backflush", "clean", "maker", {
        title: "Backflush with cleaner", phase: "close", videos: [CLIVE_CARE(135, "Weekly & Monthly Machine Cleaning")], extraCitations: [...BT("L1", "CL9", "CL14", "M18"), ...OO("OC13", "OC15")],
        interval: "Check your machine's manual: every other week (Clive) to every 50 to 100 shots (E61)",
        safetyAt: { 6: "Make a cup of espresso to remove any traces of cleaner. Discard espresso - do not drink (Urnex Cafiza label)." },
      }),
      op("espresso.soak-baskets", "Soak portafilters and baskets", "clean", "maker", [...TGR("TG89"), ...BT("L2"), ...OO("OC16")], [
        S("chemical", "Mix 6 grams of Cafiza in each liter of hot water.", { detail: "Urnex Cafiza: 6 g per liter of hot water, 30 minutes. La Marzocco: 2 or 3 teaspoons of coffee-machine detergent in about 1/2 a litre of boiled water, about 30 minutes." }),
        S("wrench", "Take the baskets out of the portafilters."),
        W("stop", "Keep the portafilter handles out of the water.", "If soaking a portafilter, do not submerge the handle (Urnex Cafiza label)."),
        S("wait", "Soak the baskets for 30 minutes.", { timerSec: 1800 }),
        S("water", "Rinse the parts with fresh water."),
      ], { phase: "close" }),
      fromTask("espresso.wand-soak", "coffee-wand", "clean", "maker", { title: "Clean the steam wand", phase: "close", videos: [CLIVE_CARE(97, "Daily Machine Upkeep")] }),
      op("espresso.drip-tray", "Empty the drip tray", "clean", "maker", [...BT("M8", "M10", "M15"), ...OO("OC17", "OC28")], [
        S("box", "Pull out the drip tray and grill."),
        S("water", "Pour the water down a drain."),
        S("soap", "Wash the tray and grill with warm soapy water."),
        S("box", "Put the tray back in."),
      ], { phase: "close", interval: "Every night (Linea Classic S); at least twice a week (Linea PB)" }),
      op("espresso.turn-off", "Turn off at close", "operate", "maker", [...OO("OC20", "OC21", "BV1", "BV16", "BV17", "BV23"), ...BT("M15")], [
        S("check", "Do the backflush and cleaning first."),
        S("power", "Turn the machine off the way its manual says.", { detail: "Linea PB: press and hold T2 and T3 at the same time; the main switch goes to 0 only for servicing (La Marzocco). Breville: press POWER to off and unplug when left unattended." }),
        S("water", "Empty and rinse the water tank, if it has one."),
        W("stop", "Let it cool before you clean inside it.", "Do not touch hot surfaces. Use handles or knobs. Allow the espresso machine to cool down before moving or cleaning any parts (Breville)."),
      ], { phase: "close", interval: "At close" }),
      fromTask("espresso.descale", "coffee-descale", "maintain", "maker", { phase: "anytime" }),
      op("espresso.gasket", "Check the gasket and screen", "maintain", "maker", [...BT("CL7", "CL21", "M17", "M18")], [
        S("wrench", "Lock in the empty portafilter."),
        S("check", "Check it locks the same as always.", { ask: { kind: "yesno", label: "Does it lock like normal, with no leak?" } }),
        S("phone", "Call a technician if it leaks or turns too far."),
        S("write", "Write the date the gasket and screen were changed."),
      ], { interval: "Replace at least once a year (Clive)" }),
      op("espresso.screen", "Clean the shower screen", "maintain", "maker", [...BT("CL12", "CL15", "CL16", "CL21")], [
        S("power", "Turn the machine off and let it cool."),
        S("wrench", "Take off the shower screen as the manual says."),
        S("chemical", "Soak the screen in espresso cleaner."),
        S("clean", "Scrub it with a soft brush."),
        W("stop", "Never use a hard brush on the screen.", "We do not recommend using a brush with hard bristles on the shower screen (Clive)."),
        S("wrench", "Put the screen back on."),
      ], { interval: "Once a week (Clive)" }),
      op("espresso.water", "Test the water hardness", "maintain", "maker", [...BT("CL27", "CL24", "CL1")], [
        S("water", "Dip a water hardness strip in the machine's water."),
        S("check", "Check the strip against the chart on the box."),
        S("write", "Write down the result and the date."),
        S("phone", "Tell the person in charge if it is too hard."),
      ], { interval: "Every 3 months (Clive)" }),
      fromHome("espresso.home-rinse", "espresso-daily", "clean", { title: "Rinse the home espresso machine", videos: [CLIVE_CARE(97, "Daily Machine Upkeep")] }),
      fromHome("espresso.home-filter", "espresso-filter", "maintain"),
      fromHome("espresso.home-descale", "espresso-descale", "maintain", { title: "Descale the home machine" }),
    ],
  },
  {
    id: "grinder", name: "Coffee grinder", category: "coffee", icon: "grinder", homes: true, business: true, surfaceId: "coffee-grinder",
    aliases: ["grinder", "coffee grinder", "burr grinder", "mahlkonig", "e65s", "ek43", "eureka", "baratza", "hopper", "burrs"],
    ops: [
      op("grinder.open", "Turn on and fill", "operate", "maker", [...OO("OC43", "OC44", "OC49", "BV18", "BV25")], [
        S("food", "Fill the hopper with fresh beans.", { detail: "Fill to about 1 cm below the edge of the bean hopper (Mahlkönig EK43)." }),
        W("stop", "Put the lid on the hopper.", "Do not operate the grinder without the hopper lid in position. Keep fingers, hands, hair, clothing and utensils away from the hopper during operation (Breville)."),
        S("box", "Open the hopper gate.", { detail: "The sliding valve closes the bean container; pull it open to let beans in (Mahlkönig E65S)." }),
        S("power", "Turn the grinder on.", { detail: "Mahlkönig E65S: move the On/Off switch to position I; it is ready when the display shows the last recipe." }),
      ], { phase: "open", interval: "At opening" }),
      op("grinder.dial-in", "Dial in a new bean", "setup", "maker", [...OO("OC38", "OC39", "OC40", "OC41", "OC42", "OC47", "OC48", "BV10", "BV12", "BV13", "BV14")], [
        S("write", "Set the grind to this bean's saved setting.", { detail: "Mahlkönig E65S scale: 0 is finest, 8 is coarsest (EK43: 0 to 16)." }),
        S("write", "Set the dose time on the grinder.", { detail: "E65S: hold the controller, turn it to the time, press once to confirm. With no input for 5 seconds the change is cancelled." }),
        S("food", "Run the grinder into the basket.", { detail: "E65S: grinding starts when the portafilter goes in and stops when the time runs out." }),
        S("check", "Put the basket on a scale to check the weight.", { ask: { kind: "number", label: "Coffee in the basket", unit: "g" } }),
        S("write", "Change the time until the weight is right."),
        S("power", "Pull a shot and time it.", { ask: { kind: "number", label: "Shot time", unit: "seconds" } }),
        S("wrench", "Turn the grind finer if the shot ran fast.", { detail: "Extraction takes up to 20 secs: TURN GRIND SIZE FINER. More than 40 secs: GRIND SIZE COARSER. A good shot takes 25–35 secs (Breville). Change one thing at a time." }),
        W("stop", "Turn it finer only while the grinder runs.", "Reduce the grinding degree only while the grinder is in operation (Mahlkönig E65S and EK43)."),
        S("wrench", "Turn the grind coarser if the shot ran slow."),
        S("write", "Save the setting and time for this bean."),
      ], {
        interval: "Each new bean, and when shots run fast or slow",
        params: [
          { key: "doseTime", label: "Dose time", unit: "seconds", perItem: "bean", default: "2 single, 4 double (Mahlkönig E65S factory setting)", defaultSource: "object-operations#OC38" },
          { key: "grindSetting", label: "Grind setting", perItem: "bean", example: ASK_PIC },
          { key: "dose", label: "Target coffee in the basket", unit: "g", perItem: "bean", default: "12-15 for a double (La Marzocco), 15–18 (Breville)", defaultSource: "object-operations#OC11, object-operations#BV10" },
          { key: "time", label: "Target shot time", unit: "seconds", perItem: "bean", default: "25–35", defaultSource: "object-operations#BV12" },
        ],
      }),
      op("grinder.close", "Close down the grinder", "clean", "maker", [...BT("G9", "CL30", "CL33"), ...KA("G1"), ...OO("OC44")], [
        S("box", "Close the hopper gate."),
        S("power", "Turn the grinder off."),
        S("clean", "Clean the chute and outside with a dry brush."),
        W("stop", "Never wash the grinder body or burrs.", "Never wash or immerse the main body of the burr grinder, burrs, or power cord in water (KitchenAid). Do not wash with water, this will cause rusting and corrosion (Baratza, via Clive). Avoid a damp towel inside the grinder (Clive)."),
      ], { phase: "close", interval: "Daily" }),
      fromTask("grinder.clean", "coffee-grinder", "clean", "maker", { title: "Clean the hopper and burrs", interval: "Hopper daily (Mahlkönig); every two weeks at home (KitchenAid)", extraCitations: [...KA("G3"), ...OO("OC44")] }),
      op("grinder.tablets", "Run grinder cleaning tablets", "maintain", "maker", [...TGR("TG91"), ...BT("L6"), ...OO("OC45")], [
        S("box", "Empty all the beans from the grinder."),
        S("wrench", "Set the grinder to a medium setting."),
        S("chemical", "Run one jar cap of Grindz through it.", { detail: "Use 1 jar cap (35-40 g) for espresso grinders or 2 caps (70-80 g) for shop grinders; adjust grinder to medium setting (Urnex Grindz label)." }),
        S("food", "Run the same amount of beans through to purge."),
        W("stop", "Never use tablets in a superautomatic grinder.", "Do not use in superautomatic machine grinders (Urnex Grindz label)."),
        S("wrench", "Set the grind back to your setting."),
      ], { interval: "Every 2–5 days (Mahlkönig E65S)" }),
      op("grinder.burrs", "Check the burrs for wear", "maintain", "maker", [...BT("G3"), ...OO("OC78")], [
        S("write", "Keep track of how much coffee you grind."),
        S("phone", "Call a technician to check the burrs when due."),
      ], { interval: "Every 300 kg of coffee (Eureka); about 550 pounds (Moccamaster KM5)" }),
    ],
  },
  {
    id: "batch-brewer", name: "Batch coffee brewer", category: "coffee", icon: "coffee-maker", homes: false, business: true, surfaceId: "drip-brewer",
    aliases: ["batch brewer", "drip brewer", "bunn", "fetco", "coffee urn", "airpot", "thermal server", "brewed coffee"],
    ops: [
      op("batch.open", "Turn on and warm up", "operate", "maker", OO("OC56", "OC61", "OC62", "OC68", "OC69"), [
        S("power", "Turn the brewer on, if it was off.", { detail: "Fetco recommends leaving the power to the brewer on overnight. Check your brewer's manual." }),
        S("wait", "Wait until it says ready to brew.", { detail: "BUNN ICB: wait approximately twenty-five minutes; the display shows READY TO BREW, WATER TEMP 200°. BUNN CWTF: approximately twenty minutes." }),
        S("water", "Fill each server half way with hot water.", { detail: "Preheat servers half way with hot water and let them stand for at least 5 minutes before the first brew (Fetco)." }),
        S("wait", "Wait 5 minutes.", { timerSec: 300 }),
        S("water", "Pour the warming water out."),
      ], {
        phase: "open", interval: "At opening",
        params: [{ key: "waterTemp", label: "Brew water temperature", unit: "°F", default: "200 (BUNN factory setting)", defaultSource: "object-operations#OC62" }],
      }),
      op("batch.brew", "Brew a batch", "operate", "maker", OO("OC51", "OC57", "OC58", "OC67"), [
        S("box", "Put a clean filter in the clean, empty funnel."),
        S("food", "Add the ground coffee and make it level."),
        S("box", "Put the funnel into the brewer."),
        S("box", "Put an empty server under the funnel."),
        S("power", "Press the brew button for your batch size."),
        W("stop", "Wait until the dripping stops.", "Carefully remove the brew funnel and discard the grounds and filter only after visible dripping stops (BUNN). The funnel and coffee are hot."),
        S("trash", "Throw away the grounds and filter."),
      ], {
        phase: "run", interval: "Each batch",
        params: [{ key: "ratio", label: "Coffee for each liter of water", unit: "g", default: "55", defaultSource: "object-operations#OC51, object-operations#OC67" }],
      }),
      op("batch.close", "Clean the brewer at close", "clean", "maker", OO("OC70", "OC60", "OC69"), [
        S("soap", "Wash the brew basket in hot soapy water."),
        S("wrench", "Take off the sprayhead and wash it."),
        S("soap", "Wash the servers and their faucet parts."),
        S("chemical", "Soak the faucet parts in sanitizer for 5 minutes.", { timerSec: 300, detail: "BUNN Soft Heat: soak the faucet parts in non-chlorine sanitizer for a minimum of five (5) minutes, then air-dry overnight." }),
        S("check", "Let the parts air-dry overnight."),
        S("check", "Leave the brewer on if its manual says to."),
      ], { phase: "close", interval: "Every day (Fetco)" }),
      op("batch.sprayhead", "Clean the sprayheads", "maintain", "maker", OO("OC59", "OC70"), [
        S("wrench", "Take off the sprayhead."),
        S("clean", "Scrub the holes clear."),
        S("wrench", "Put it back on."),
        S("check", "Read the manual for deliming the brewer."),
      ], { interval: "Weekly (BUNN)" }),
    ],
  },
  {
    id: "milk-pitchers", name: "Milk pitchers", category: "coffee", icon: "pitcher", homes: true, business: true, surfaceId: "milk-pitchers",
    aliases: ["milk pitcher", "steaming pitcher", "milk jug", "frothing pitcher"],
    ops: [
      op("pitcher.open", "Set out clean pitchers", "setup", "law", [...OO("FC16", "FC24")], [
        S("box", "Take clean, dry pitchers from the rack."),
        S("check", "Check each pitcher is clean to sight and touch."),
        S("fridge", "Keep the milk in the fridge until you steam it."),
      ], { phase: "open", interval: "At opening" }),
      op("pitcher.wash", "Wash the milk pitchers", "clean", "law", [...OO("FC19", "FC16"), ...TGR("TG9", "TG10", "TG11"), ...BT("T17", "T19")], [
        S("trash", "Pour out leftover milk."),
        S("soap", "Wash the pitcher in hot soapy water."),
        S("water", "Rinse it with clean water."),
        S("spray", "Sanitize it as the sanitizer label says."),
        S("check", "Let it air-dry upside down."),
        W("stop", "Never dry it with a towel.", "After sanitizing, let it air-dry; never dry it with a cloth (Food Code 4-901.11). Check the pitcher maker: some coated pitchers are not dishwasher safe (Normcore)."),
      ], { phase: "close", interval: "At least every 4 hours in use, and at close" }),
      op("pitcher.soak", "Soak pitchers in milk cleaner", "clean", "maker", [...TGR("TG90")], [
        S("chemical", "Mix one Rinza tablet in 250 ml warm water."),
        S("wait", "Soak the pitcher at least 15 minutes.", { timerSec: 900 }),
        S("water", "Rinse it with clean water."),
      ], { phase: "close" }),
    ],
  },
  {
    id: "cold-brew", name: "Cold brew system", category: "coffee", icon: "cold-brew", homes: true, business: true, surfaceId: "cold-brew-containers",
    aliases: ["cold brew", "toddy", "iced coffee", "cold brew maker"],
    ops: [
      op("coldbrew.check", "Check the cold brew", "operate", "maker", [...OO("OC72")], [
        S("fridge", "Check the cold brew is in the fridge."),
        S("write", "Check the date on the container."),
        S("trash", "Throw it out after two weeks, or with no date.", { detail: "Refrigerate the extract for up to two weeks (Toddy)." }),
      ], { phase: "open", interval: "At opening" }),
      op("coldbrew.brew", "Start a batch", "setup", "maker", OO("OC71", "OC72"), [
        S("box", "Put the clean filter in the brewer."),
        S("food", "Add coarse ground coffee."),
        S("water", "Pour in cool filtered water."),
        S("wait", "Wait 20 minutes for the coffee to bloom.", { timerSec: 1200 }),
        S("food", "Stir it gently."),
        S("box", "Cover it and let it steep."),
        S("fridge", "Drain it and put the cold brew in the fridge."),
        S("write", "Label it with today's date."),
      ], {
        interval: "Each batch",
        params: [
          { key: "coffee", label: "Coarse coffee", unit: "pounds", default: "5 (Toddy Commercial)", defaultSource: "object-operations#OC71" },
          { key: "water", label: "Cool filtered water", unit: "quarts", default: "13 (Toddy Commercial)", defaultSource: "object-operations#OC71" },
          { key: "steep", label: "Steep time", unit: "hours", default: "8-24", defaultSource: "object-operations#OC72" },
        ],
      }),
      op("coldbrew.clean", "Clean the brewer and filter", "clean", "maker", [...BT("T12", "T13"), ...KA("CB1")], [
        S("trash", "Throw away the used grounds."),
        S("water", "Rinse the felt filter with water only.", { detail: "Rinse out the filter with water (no soap) (Toddy)." }),
        S("fridge", "Store the damp filter in a bag in the freezer."),
        S("soap", "Wash the brewing container by hand."),
        W("stop", "Check the maker before using the dishwasher.", "To avoid damage, do not wash in the dishwasher (KitchenAid cold brew maker). Toddy recommends hand-washing all reusable parts for longest life."),
      ], { phase: "close", interval: "After each batch" }),
    ],
  },
  {
    id: "kettle", name: "Electric kettle", category: "coffee", icon: "kettle", homes: true, business: true,
    aliases: ["kettle", "electric kettle", "gooseneck kettle", "hot water", "stagg"],
    ops: [
      op("kettle.boil", "Fill and boil water", "operate", "maker", OO("OH52", "OH53", "OH54"), [
        S("water", "Fill it between the MIN and MAX lines."),
        S("box", "Set it on its base."),
        S("power", "Turn it on."),
        S("wait", "Wait for it to turn itself off.", { detail: "Hamilton Beach: the kettle shuts off 30-90 seconds after the water boils." }),
        W("stop", "Keep the base and plug dry.", "Do not immerse kettle, power base, or plug in water or other liquid (Hamilton Beach)."),
      ], { phase: "open", interval: "At opening and when needed" }),
      op("kettle.first", "First use of a new kettle", "setup", "maker", OO("OC73"), [
        S("water", "Fill it to at least the MIN line."),
        S("power", "Let the water boil for one minute.", { timerSec: 60 }),
        S("trash", "Pour that water away."),
      ]),
      op("kettle.clean", "Wipe out the kettle", "clean", "maker", [...BT("T1"), ...KA("K4")], [
        S("power", "Unplug the kettle and let it cool."),
        S("soap", "Wipe the inside with a soapy cloth."),
        S("water", "Rinse it with clean water."),
        W("stop", "Never put the kettle in water.", "Never submerge the kettle; the kettle and base are not dishwasher safe (Fellow)."),
      ], { phase: "close", interval: "Daily (KitchenAid)" }),
      fromHome("kettle.descale", "kettle-descale", "maintain", { basis: "maker", extraCitations: [...KA("K1", "K4"), ...OO("OC74", "OH55")] }),
    ],
  },
  {
    id: "home-coffee-maker", name: "Drip coffee maker (home)", category: "coffee", icon: "coffee-maker", homes: true, business: false,
    aliases: ["coffee maker", "coffee pot", "mr coffee", "hamilton beach", "moccamaster", "drip coffee"],
    ops: [
      op("hcm.brew", "Make a pot of coffee", "operate", "maker", OO("OH56", "OH57", "OC75"), [
        S("water", "Fill the water tank to the cup line you want."),
        S("box", "Put a paper filter in the basket."),
        S("food", "Add one level tablespoon of coffee for each cup."),
        S("box", "Close the lid and put the pot on the plate."),
        S("power", "Turn it on."),
        S("power", "Turn it off when you are done.", { detail: "Hamilton Beach clock models: the keep-hot plate shuts off two hours after the brew cycle begins." }),
      ], {
        params: [{ key: "coffee", label: "Coffee for each cup", unit: "tablespoons", default: "1 level tablespoon (Hamilton Beach); 55 g for each liter (Moccamaster)", defaultSource: "object-operations#OH56, object-operations#OC75" }],
      }),
      op("hcm.clean", "Wash the basket and carafe", "clean", "maker", [...KA("C3", "C4")], [
        S("trash", "Throw away the used filter and grounds."),
        S("soap", "Wash the basket and carafe in warm soapy water."),
        S("water", "Rinse and dry them."),
        S("check", "Leave the lid open so it dries."),
      ], { interval: "Daily (KitchenAid)" }),
      fromHome("hcm.descale", "coffee-descale", "maintain", { extraCitations: [...KA("C1", "C2"), ...OO("OC66", "OC76")] }),
    ],
  },
  {
    id: "french-press", name: "French press", category: "coffee", icon: "french-press", homes: true, business: false,
    aliases: ["french press", "press pot", "cafetiere", "plunger"],
    ops: [
      op("fpress.brew", "Make French press coffee", "operate", "maker", OO("OC54"), [
        S("food", "Put medium-coarse ground coffee in the press."),
        S("water", "Pour in hot water."),
        S("box", "Put the lid on with the plunger up."),
        S("wait", "Wait 4 minutes.", { timerSec: 240 }),
        S("box", "Press the plunger down slowly."),
        S("food", "Pour the coffee."),
      ], {
        params: [
          { key: "coffee", label: "Coffee", unit: "g", default: "36", defaultSource: "object-operations#OC54" },
          { key: "water", label: "Water at 200°F", unit: "g", default: "660", defaultSource: "object-operations#OC54" },
        ],
      }),
      op("fpress.clean", "Wash the French press", "clean", "maker", [...BT("T11")], [
        S("trash", "Scoop the grounds into the trash."),
        S("soap", "Wash the glass and plunger in soapy water."),
        S("check", "Check the maker's label before using the dishwasher."),
      ], { interval: "After each use" }),
    ],
  },
  {
    id: "coffee-scale", name: "Coffee scale", category: "coffee", icon: "scale", homes: true, business: true,
    aliases: ["coffee scale", "espresso scale", "scale", "weighing scale", "tally", "acaia", "weigh"],
    ops: [
      op("scale.open", "Turn on and zero the scale", "operate", "maker", OO("OC79", "OC80"), [
        S("box", "Put the empty cup or basket on the scale."),
        S("power", "Press the button to turn it on and zero it.", { detail: "Fellow Tally Pro: short press to power on and tare, with your empty container on the weigh pan. Other scales: check your scale's manual." }),
        S("check", "Check the screen shows zero."),
      ], { phase: "open", interval: "At opening" }),
      op("scale.close", "Wipe and turn off the scale", "clean", "maker", [...OO("OC79", "OC81"), ...BT("T14", "T15", "T16")], [
        S("power", "Hold the button down to turn it off.", { detail: "Fellow Tally Pro: long press to power off." }),
        S("towel", "Wipe it with a damp cloth."),
        W("stop", "Never put the scale under running water.", "Never run Tally Pro under running water. Only clean with a damp cloth (Fellow). Never spray cleaner directly on the scale (Acaia)."),
        S("clean", "Keep coffee grounds off the buttons."),
      ], { phase: "close", interval: "At close; a wipe with mild soap at least once a month (Fellow)" }),
      op("scale.charge", "Charge the scale", "maintain", "maker", OO("OC81"), [
        S("power", "Plug the scale in to charge it."),
      ], { interval: "Every month with daily use (Fellow Tally Pro)" }),
    ],
  },
];
/* COFFEE_END */

/* KITCHEN_START */
const HOT_REHEAT = "Food that was cooked and cooled is reheated to 165°F for 15 seconds, within 2 hours, before it is held hot (Food Code 3-403.11).";
const TOO_WARM = "Food that must stay cold is held at 41°F or colder (Food Code 3-501.16). Move it to a unit that is cold enough and tell the person in charge.";
const GREASE_FIRE = "If you have a fire, slide the lid over the pan and turn off the burner. Never throw water on the fire. Call 9-1-1 or the local emergency number after you leave (NFPA).";
const GAS_SMELL = "If you smell gas when not using the cooktop, ensure that none of the surface burners has accidentally been turned on (Whirlpool). Do not touch any electrical switch; immediately call your gas supplier from a neighbor's phone (Whirlpool). Evacuate the premises immediately, leaving doors and windows open behind you if you can do so safely without delay (Enbridge Gas). If gas is burning, call 911; do not try to put out the fire (Enbridge Gas).";
const HOT_OIL = "Never add water to hot oil. Violent boiling can occur causing severe injury (Pitco). Do not pour or spill water or ice into oil, especially hot oil (OSHA). Do not move or strain hot oil containers; wait until the oil is cool (OSHA).";

/**
 * Every cold unit's "Check the temperature" op, by op id: a fridge (41°F, Food Code 3-501.16(A)(2)) or a freezer (frozen,
 * 3-501.11). Crawl fix 1 (2026-10-10): core things.ts plays these against the cold-holding log (cold_units), so the reading is
 * judged at the step and, at a business, saved as that log. Filled by coldCheck below; the op content itself is unchanged.
 */
export const COLD_CHECK_OPS: Record<string, "fridge" | "freezer"> = {};
/** The one thing to do now when a cold unit reads too warm, per cold_units rule id (the op's own "tell the person in charge" words). */
export const COLD_CHECK_FIX: Record<"fridge" | "freezer", string> = {
  fridge: TOO_WARM,
  freezer: "Stored frozen foods shall be maintained frozen (Food Code 3-501.11). Tell the person in charge and move food to a working freezer.",
};

/** One fridge/freezer temperature check (the unit's own thermometer, warmest part). */
const coldCheck = (id: string, what: string, limit: "41" | "frozen"): ObjectOp => {
  COLD_CHECK_OPS[id] = limit === "41" ? "fridge" : "freezer";
  return op(id, "Check the temperature", "operate", "law", limit === "41" ? OO("FC25", "FC28") : OO("FC36", "FC28"), [
  S("thermo", `Read the thermometer in the ${what}.`, { ask: { kind: "number", label: "Temperature", unit: "°F" } }),
  limit === "41" ? S("check", "Check it reads 41°F or colder.") : S("check", "Check the food is frozen hard."),
  W("phone", "Tell the person in charge if it is too warm.", limit === "41" ? TOO_WARM : "Stored frozen foods shall be maintained frozen (Food Code 3-501.11). Tell the person in charge and move food to a working freezer."),
], { phase: "open", interval: "At opening and during the day" });
};

/** Stay-in-the-kitchen and grease fire steps, shared by every stovetop. */
const stoveSafety = (id: string): ObjectOp => op(id, "Cook safely on the stove", "operate", "maker", OO("OH9", "OH10", "OH11", "OH12", "OH14"), [
  S("stop", "Stay in the kitchen while food cooks on top."),
  S("power", "Turn the burner off if you leave the kitchen."),
  W("stop", "Put a lid on a pan that catches fire.", GREASE_FIRE),
  W("power", "Turn the burner off under a burning pan.", GREASE_FIRE),
  W("stop", "Never throw water on a pan fire.", GREASE_FIRE),
  W("phone", "Get out and call 9-1-1 if the fire grows.", GREASE_FIRE),
], { phase: "run" });

const KITCHEN: ObjectType[] = [
  /* ═════════════ COLD ═════════════ */
  {
    id: "reach-in-fridge", name: "Reach-in refrigerator", category: "cold", icon: "fridge", homes: false, business: true, surfaceId: "reach-in-fridge",
    aliases: ["fridge", "refrigerator", "reach in", "cooler", "prep fridge", "under counter fridge", "undercounter fridge", "milk fridge", "beer cooler", "true"],
    ops: [
      coldCheck("reachin.temp", "warmest part of the fridge", "41"),
      fromTask("reachin.order", "fridge-order", "operate", "law", { phase: "run" }),
      op("reachin.close", "Close up the fridge", "operate", "law", [...OO("FC25", "FC35"), ...TGR("TG38")], [
        S("box", "Cover or wrap all food."),
        S("check", "Close the door all the way."),
        S("thermo", "Check it still reads 41°F or colder."),
      ], { phase: "close", interval: "At close" }),
      op("reachin.new", "Start a new or moved fridge", "setup", "maker", OO("OK103", "OK104", "OK105", "FC25"), [
        W("stop", "Stand it up 24 hours if it lay down.", "If it was laid on its side for more than four (4) hours, let the appliance sit upright for 24 hours before powering it (True)."),
        S("power", "Plug it in and turn it on."),
        S("wait", "Run it empty for 24 hours.", { detail: "Before loading product, run your TRUE appliance empty for 24 hours to verify proper operation (True)." }),
        S("thermo", "Check it reads 41°F or colder before loading food."),
      ], { interval: "When new or moved" }),
      fromTask("reachin.deep", "fridge-deep", "clean", "maker"),
      op("reachin.coil", "Clean the condenser coil", "maintain", "maker", OO("OK106", "OK107"), [
        S("power", "Unplug the fridge."),
        S("box", "Take off the front grill."),
        S("clean", "Clean the dust off the coil fins with a brush."),
        S("check", "Use a flashlight to check it is clear."),
        S("box", "Put the grill back and plug it in."),
      ], { interval: "Check monthly (True)" }),
      op("reachin.gasket", "Clean the door gaskets", "clean", "maker", OO("OK107"), [
        S("soap", "Wash the gaskets with warm soapy water."),
        W("stop", "Never scrape a gasket with a knife.", "Clean gaskets with warm soapy water. DO NOT use sharp tools or knives to scrape a gasket (True). Never use a pressure washer or hose on the unit."),
        S("towel", "Dry them."),
      ]),
    ],
  },
  {
    id: "walk-in", name: "Walk-in cooler", category: "cold", icon: "fridge", homes: false, business: true, surfaceId: "walk-in",
    aliases: ["walk in", "walk-in cooler", "cooler room", "cold room"],
    ops: [
      coldCheck("walkin.temp", "warmest part of the walk-in", "41"),
      fromTask("walkin.order", "fridge-order", "operate", "law", { phase: "run" }),
      op("walkin.floor", "Keep food off the floor", "operate", "law", OO("FC35"), [
        S("box", "Put every box and bag on a shelf."),
        S("check", "Check nothing sits on the floor."),
      ], { phase: "run", interval: "Each delivery and at close" }),
      op("walkin.close", "Close up the walk-in", "operate", "law", [...OO("FC25", "FC35")], [
        S("check", "Check nothing blocks the door from closing."),
        S("check", "Close the door all the way."),
        S("thermo", "Check it still reads 41°F or colder."),
      ], { phase: "close", interval: "At close" }),
      op("walkin.clean", "Clean the walk-in", "clean", "maker", OO("EQ265", "EQ266", "FC33"), [
        S("clean", "Wipe metal and door gaskets with mild detergent."),
        S("towel", "Dry them."),
        W("stop", "Never hose down the walk-in.", "Never use high pressure hose or water to clean walk-in. Condensate water must never be allowed to drip on the walk-in floor (Kolpak)."),
        S("clean", "Keep the floor clean and dry."),
      ], { interval: "Frequently (Kolpak)" }),
      op("walkin.door", "Check the door and gaskets", "maintain", "maker", OO("EQ262", "EQ263", "EQ264"), [
        S("check", "Check the door closes easily and the sweep gasket is whole."),
        S("check", "Look for frost or water at the door frame."),
        S("phone", "Call for service if frost builds there."),
        S("box", "Hang a strip curtain if the door stays open over 5 minutes."),
      ], { interval: "Monthly (Kolpak)" }),
      op("walkin.coil", "Book the coil cleaning", "maintain", "maker", OO("EQ269"), [
        S("check", "Check it defrosts right each month."),
        S("phone", "Get the evaporator coil cleaned every 6 months."),
        S("phone", "Get the condenser coil cleaned every 3 months."),
      ], { interval: "Defrost monthly; evaporator every 6 months; condenser every 3 months (Kolpak)" }),
    ],
  },
  {
    id: "freezer", name: "Freezer", category: "cold", icon: "freezer", homes: true, business: true, surfaceId: "freezer",
    aliases: ["freezer", "chest freezer", "upright freezer", "reach-in freezer"],
    ops: [
      coldCheck("freezer.temp", "freezer", "frozen"),
      op("freezer.home-temp", "Check the home freezer", "operate", "maker", [...HFS("H2", "H3"), ...OO("OH82", "OH83")], [
        S("thermo", "Read the thermometer at the front of the freezer.", { ask: { kind: "number", label: "Temperature", unit: "°F" } }),
        S("check", "Check it reads 0°F or colder."),
        S("phone", "Tell someone if it is warmer."),
      ], { interval: "At least once a week (FDA)" }),
      op("freezer.clean", "Clean the freezer", "clean", "law", OO("FC33", "FC36"), [
        S("fridge", "Move the food to another working freezer."),
        S("clean", "Wipe out spills and loose ice."),
        S("towel", "Dry it."),
        S("fridge", "Put the food back while it is still frozen."),
      ], { interval: "Often enough that dirt does not build up (Food Code 4-602.13)" }),
    ],
  },
  {
    id: "home-fridge", name: "Refrigerator (home)", category: "cold", icon: "fridge", homes: true, business: false,
    aliases: ["fridge", "refrigerator", "icebox", "home fridge"],
    ops: [
      fromHome("hfridge.temp", "fridge-temp", "operate", { extraCitations: OO("OH81", "OH83") }),
      op("hfridge.set", "Set the fridge dial", "setup", "maker", OO("OH81", "OH84", "OH85"), [
        S("check", "Find the fridge temperature control."),
        S("thermo", "Set it to 37°F, or the middle setting."),
        S("wait", "Wait 24 hours before you change it again."),
        S("thermo", "Check the thermometer reads 40°F or colder."),
      ], { params: [{ key: "fridgeTemp", label: "Fridge setting", unit: "°F", default: "37 (Whirlpool), never above 40 (FDA)", defaultSource: "object-operations#OH84, object-operations#OH81" }] }),
      fromHome("hfridge.place", "fridge-placement", "operate"),
      fromHome("hfridge.old", "use-by-check", "clean"),
      fromHome("hfridge.clean", "fridge-clean", "clean", { way: "no" }),
      fromHome("hfridge.spill", "fridge-clean", "clean", { way: "yes", title: "Clean up a fridge spill", interval: "After a spill" }),
      fromHome("hfridge.filter", "fridge-filter", "maintain"),
    ],
  },
  {
    id: "ice-machine", name: "Ice machine", category: "cold", icon: "ice", homes: false, business: true, surfaceId: "ice-machine",
    aliases: ["ice machine", "ice maker", "ice bin", "hoshizaki", "scotsman", "manitowoc"],
    ops: [
      op("ice.scoop", "Check the ice scoop", "operate", "law", [...OO("FC32", "OK95")], [
        S("check", "Check the scoop is outside the ice."),
        S("check", "Check its holder is clean."),
        S("soap", "Wash the scoop every day."),
      ], { phase: "open", interval: "Daily (Hoshizaki: wash the scoop daily)" }),
      op("ice.clean", "Clean and sanitize the machine", "clean", "maker", [...OO("OK95", "OK96", "OK97", "OK98", "OK99", "OK100", "OK101", "OK102", "FC20")], [
        W("gloves", "Put on waterproof gloves and goggles.", "Always wear liquid-proof gloves and goggles (Hoshizaki). Ice machine cleaner and scale remover contains acids. Acids can cause burns (Scotsman). Do not use ammonia type cleaners (Hoshizaki)."),
        S("box", "Take all the ice out of the bin."),
        S("check", "Read the machine's manual for its cleaning cycle."),
        S("chemical", "Run the cleaning cycle with the maker's cleaner.", { detail: "Hoshizaki: pour 14 fl. oz. (414 ml) of Hoshizaki Scale Away into the water tank, run WASH, stop after 30 min. Scotsman: press Clean, then pour 10 ounces (C0530 or C0630) or 12 ounces (C0830 or C1030) of Scotsman Clear 1; this takes 35 minutes." }),
        S("chemical", "Run the sanitizing cycle with the maker's sanitizer.", { detail: "Hoshizaki: 23 ml of an 8.25% sodium hypochlorite solution (chlorine bleach) into the water tank, WASH, after 45 min remove the front panel. Scotsman: 8 ounces of NuCalgon IMS II in 5 gallons of 105-115 degree F potable water makes a 200 ppm active quaternary solution." }),
        S("spray", "Sanitize the inside of the ice bin."),
        S("power", "Turn the machine back on to make ice."),
      ], { interval: "At least once a year (Hoshizaki); at least twice a year (Scotsman)" }),
      op("ice.filters", "Check the air and water filters", "maintain", "maker", OO("OK95", "OK102"), [
        S("check", "Check the air filter for dust."),
        S("water", "Rinse a dusty air filter and let it dry."),
        S("write", "Check the date on the water filter."),
        S("phone", "Get a new water filter when it is due."),
      ], { interval: "Air filters every two weeks (Hoshizaki); water filter after 6 months (Scotsman)" }),
    ],
  },

  /* ═════════════ HOT HOLDING ═════════════ */
  {
    id: "steam-table", name: "Steam table or hot well", category: "hot_holding", icon: "steam-table", homes: false, business: true,
    aliases: ["steam table", "hot well", "food warmer", "bain marie", "hot holding", "wells"],
    ops: [
      op("steam.open", "Fill and preheat", "operate", "maker", OO("OK75", "OK76", "OK78"), [
        S("water", "Put about 1 inch of hot tap water in the well.", { detail: "Wells: add approximately 1” of hot tap water before turning the warmer on. Wells allows wet or dry use but not a switch between them until it cools to room temperature; check your warmer's manual." }),
        S("power", "Turn the warmer on to preheat."),
        S("wait", "Wait about 15 minutes.", { timerSec: 900 }),
        S("thermo", "Set the temperature control."),
        S("box", "Put the food in a pan in the well, never loose."),
      ], { phase: "open", interval: "At opening" }),
      op("steam.check", "Check the hot food", "operate", "law", OO("FC25", "FC26", "FC28"), [
        S("thermo", "Put a probe thermometer in the middle of the food.", { ask: { kind: "number", label: "Food temperature", unit: "°F" } }),
        S("check", "Check it reads 135°F or hotter."),
        W("phone", "Tell the person in charge if it is colder.", "Hot food is held at 135°F or above (Food Code 3-501.16). Cooked and cooled food that is reheated for hot holding reaches 165°F for 15 seconds, within 2 hours (Food Code 3-403.11); the warmer holds food, it does not reheat it."),
      ], { phase: "run", interval: "During service" }),
      op("steam.water", "Add water to the well", "operate", "maker", OO("OK77"), [
        W("stop", "Never add water to a well that ran dry.", "Do not add water to the warmer if it has run dry. Turn it OFF and allow to cool to room temperature before adding water. Never put ice in the pan (Wells)."),
        S("power", "Turn a dry well off and let it cool."),
        S("water", "Add hot water to a well that is low."),
      ], { phase: "run" }),
      op("steam.close", "Clean the well at close", "clean", "maker", OO("OK79"), [
        S("power", "Turn the warmer off and let it cool."),
        S("water", "Drain the water."),
        S("soap", "Wipe the well with detergent and a plastic pad."),
        S("water", "Rinse it with warm water and vinegar.", { detail: "Rinse solution: 10 parts warm water to 4 parts vinegar (Wells). No steel wool." }),
        S("towel", "Dry it."),
      ], { phase: "close", interval: "Daily (Wells)" }),
      op("steam.delime", "Delime the well", "maintain", "maker", OO("OK80"), [
        S("water", "Fill the well with water and heat it to the top setting.", { detail: "Heat water to maximum temperature (190ºF or higher) (Wells)." }),
        S("chemical", "Add delime cleaner as its label says."),
        S("power", "Turn it off and cover it."),
        S("wait", "Let it soak at least one hour.", { timerSec: 3600 }),
        S("water", "Drain and rinse it."),
      ], { interval: "Weekly (Wells)" }),
    ],
  },

  /* ═════════════ COOKING ═════════════ */
  {
    id: "fryer", name: "Deep fryer", category: "cooking", icon: "fryer", homes: false, business: true, surfaceId: "fryer",
    aliases: ["fryer", "deep fryer", "frymaster", "pitco", "fry oil", "fries"],
    ops: [
      op("fryer.open", "Fill and turn on", "operate", "maker", OO("OK1", "OK2", "OK3", "OK4", "OK5", "OK18", "OK20", "OK25"), [
        W("gloves", "Put on the apron, heat gloves and goggles.", "Always wear apron, heat resistant gloves for skin protection and goggles for eye protection (Pitco)."),
        W("stop", "Never turn on a fryer with an empty pot.", "Never operate this appliance with an empty frypot. The frypot must be filled with water or oil/shortening before lighting the burners (Frymaster)."),
        S("food", "Fill cold oil only to the bottom oil line.", { detail: "Do not fill cold oil any higher than the bottom line; overflow may occur as heat expands the oil (Frymaster). Pitco: fill to about the MIN mark. Solid shortening: pre-melt it (Frymaster) or cut it into cubes no larger than one inch and pack it around the heat tubes (Pitco)." }),
        S("power", "Turn the fryer on as its manual says.", { detail: "Frymaster controllers run a melt cycle when the frypot is below 180°F. Standing pilot: wait at least 5 minutes with the gas off, then light it and hold the knob about 60 seconds (Frymaster)." }),
        S("thermo", "Set the temperature the food maker gives."),
        S("check", "Check the hot oil is at the top oil line."),
      ], { phase: "open", interval: "At opening" }),
      op("fryer.safe", "Fry safely", "operate", "osha", OO("OK22", "OK23", "OK24", "OK27", "OK31", "OK32", "OK33", "OK34", "OK35"), [
        W("stop", "Keep water and ice away from the oil.", HOT_OIL),
        S("stop", "Keep drinks away from the fryer."),
        W("stop", "Never reach over or lean on a fryer.", "Avoid reaching over or climbing on top of fryers and other hot surfaces. Use gloves and scrapers and other cleaning tools with handles (OSHA)."),
        S("trash", "Tell the person in charge if oil smokes or foams.", { detail: "Replace oil that is smoking, or foams when cooking; old oil has a reduced flash point and is prone to surge boiling (Pitco). Keep the oil to a maximum of 374°F (Pitco)." }),
        W("stop", "Use the Class K extinguisher on an oil fire.", "Extinguish hot oil/grease fires by using a class K fire extinguisher (OSHA). Never use water."),
        S("people", "Let only workers 16 and older cook.", { detail: "Child labor laws do not permit workers younger than 16 to cook, except at soda fountains, lunch counters, snack bars, and cafeteria serving counters (OSHA)." }),
      ], { phase: "run", interval: "Every shift" }),
      op("fryer.filter", "Filter the oil", "clean", "maker", OO("OK7", "OK9", "OK25", "OK26"), [
        W("gloves", "Put on the apron, heat gloves and goggles.", "The oil to be filtered is at or near 350°F (177°C). Wear all appropriate safety equipment when draining and filtering (Frymaster)."),
        S("box", "Put a clean filter paper and hold-down ring in.", { detail: "Sprinkle one cup of filter powder evenly over the paper; do not use filter powder with a filter pad (Frymaster)." }),
        S("check", "Read the fryer's manual and do its filter steps."),
        S("check", "Check the oil is back at the top line."),
      ], { phase: "close", interval: "At least daily, with a clean filter each day (Pitco)" }),
      op("fryer.close", "Shut down at close", "operate", "maker", OO("OK10", "OK17", "OK28"), [
        S("power", "Turn the fryer power switch off."),
        S("power", "Turn the gas valve off."),
        S("box", "Put the covers on the fry pots."),
        W("trash", "Empty the crumb tray into a metal can.", "The crumb tray must be emptied into a fire-proof container at the end of frying operations each day (Frymaster): oil-soaked crumbs can catch fire on their own."),
        W("stop", "Never leave the fryer on standby overnight.", "NEVER leave the appliance in standby mode for prolonged periods or overnight (Pitco)."),
      ], { phase: "close", interval: "At close" }),
      op("fryer.change", "Change the oil", "maintain", "maker", OO("OK8", "OK24", "OK31"), [
        S("power", "Turn the fryer off."),
        W("wait", "Wait until the oil cools to 100°F.", "Allow oil/shortening to cool to 100°F (38°C) before draining into an appropriate container for disposal (Frymaster). Do not move or strain hot oil containers; wait until the oil is cool (OSHA)."),
        S("thermo", "Check the oil temperature with a thermometer."),
        S("trash", "Drain the oil into the disposal container."),
      ], { interval: "When oil smokes or foams (Pitco)" }),
      op("fryer.boilout", "Boil out the fry pot", "maintain", "maker", OO("OK11", "OK12", "OK13", "OK14", "OK15", "OK29"), [
        S("water", "Fill the empty pot with cold water and cleaner.", { detail: "Frymaster: fill to the bottom OIL-LEVEL line with cold water and automatic dishwasher liquid, set the thermostat to 195°F. Pitco: fill with water, set 200°F, and follow the boil-out product." }),
        S("power", "Heat it and let it simmer for 1 hour.", { timerSec: 3600 }),
        S("power", "Turn it off and let it cool."),
        S("water", "Add 2 gallons of cold water, then drain it."),
        W("stop", "Never drain boil-out water into the oil filter.", "Do not drain boil-out solution into a shortening disposal unit, a built-in filtration unit, or a portable filter unit (Frymaster)."),
        S("water", "Rinse the pot twice with clean water."),
        W("towel", "Dry the pot fully with a clean towel.", "Dry the frypot thoroughly with a clean, dry towel (Frymaster): water left in the pot splatters when hot oil goes in."),
      ], { interval: "Each time the oil is changed (Frymaster)" }),
    ],
  },
  {
    id: "griddle", name: "Griddle or flat-top", category: "cooking", icon: "griddle", homes: false, business: true, surfaceId: "griddle",
    aliases: ["griddle", "flat top", "flattop", "plancha", "star griddle", "vulcan griddle"],
    ops: [
      op("griddle.open", "Light and preheat", "operate", "maker", OO("OK37", "OK42", "OK43", "OK44", "OK50"), [
        S("power", "Turn all the controls off first."),
        S("power", "Turn on the power or gas as the manual says."),
        W("stop", "Stop and call for help if a pilot won't light.", "Star: turn off the main valve and wait 5 minutes to clear gas before relighting. Vulcan: if the pilot fails to ignite after approximately 90 seconds, the light flashes red; switch power off and on to retry. In the event a gas odor is detected, shut down units at the main shutoff valve and contact the gas company (Vulcan)."),
        S("power", "Turn the burners on one at a time."),
        S("wait", "Preheat about 20 minutes before cooking.", { timerSec: 1200 }),
      ], { phase: "open", interval: "At opening" }),
      op("griddle.season", "Season a new griddle", "setup", "maker", OO("OK36", "OK38", "OK41"), [
        S("thermo", "Heat the griddle to 350°F."),
        S("food", "Wipe on a thin film of cooking oil with a cloth."),
        S("wait", "Leave it on for half an hour.", { detail: "This film should remain on the hot griddle surface 1/2 hour (Star)." }),
        S("towel", "Wipe it off and do it once more."),
      ], { interval: "New griddle, and after a deep clean" }),
      op("griddle.run", "Empty the grease drawer", "clean", "maker", OO("OK40", "OK48"), [
        S("gloves", "Put on heat gloves."),
        W("box", "Pull out the grease drawer slowly.", "Use great care when handling a grease pan full of hot grease (Star). Empty the grease drawer as needed throughout the day (Vulcan)."),
        S("trash", "Empty it into the grease container."),
        S("box", "Push the drawer back in."),
      ], { phase: "run", interval: "As needed through the day, and wash it daily" }),
      op("griddle.close", "Clean the griddle at close", "clean", "maker", [...OO("OK39", "OK45", "OK40", "OK48"), ...TGR("TG9")], [
        S("power", "Turn the griddle off."),
        S("wait", "Let it cool to 275 to 300°F.", { detail: "Turn the griddle off and allow it to cool down between 275°F -300°F, apply room temperature water and clean it with a griddle scraper (Vulcan)." }),
        S("water", "Pour on room temperature water."),
        S("clean", "Scrape it clean with the griddle scraper."),
        W("stop", "Never use steel wool on a griddle.", "DO NOT use steel wool because of the danger of steel slivers getting into the food (Star). Never use a water jet on the griddle (Vulcan)."),
        S("soap", "Wash the grease drawer."),
      ], { phase: "close", interval: "Daily (Vulcan)" }),
      op("griddle.deep", "Deep clean the griddle", "maintain", "maker", OO("OK46", "OK47"), [
        S("wait", "Let the griddle cool until warm, not hot."),
        S("clean", "Scrub it with a griddle brick or screen."),
        W("stop", "Never use a brick on a chrome griddle.", "Never use an abrasive scouring pad or griddle brick on a chrome plate surface (Vulcan)."),
        S("water", "Flush off all the soap with clear water."),
        S("food", "Do the seasoning steps again."),
      ], { interval: "Weekly (Vulcan)" }),
    ],
  },
  {
    id: "slicer", name: "Meat and deli slicer", category: "cooking", icon: "slicer", homes: false, business: true, surfaceId: "slicer",
    aliases: ["slicer", "deli slicer", "meat slicer", "hobart", "berkel", "cheese slicer"],
    ops: [
      op("slicer.use", "Slice safely", "operate", "osha", OO("OK65", "OK71", "OK72", "OK73", "OK74"), [
        W("people", "Let only workers 18 and older use it.", "Follow the child labor laws that do not permit workers younger than 18 to operate, set up, adjust, clean, oil, or repair power-driven food slicers (OSHA). The federal Hazardous Occupations Order covers deli slicing of meat, cheese, bread or vegetables (U.S. Department of Labor)."),
        S("gloves", "Put on a cut-proof glove.", { detail: "Use any protective clothing provided by the employer such as steel mesh or Kevlar gloves (OSHA)." }),
        W("stop", "Never take off or go around the guard.", "Do not bypass safety guards (OSHA). Rotating knife: use the meat grip, and unplug the machine before cleaning, servicing or removing parts (Hobart)."),
        W("hands", "Use the meat grip, never your fingers.", "Rotating knife. Use meat grip (Hobart). Always use push sticks or tamps to feed or remove food (OSHA)."),
        S("power", "Turn it off when you are done."),
      ], { phase: "run" }),
      op("slicer.clean", "Take apart and clean", "clean", "law", [...OO("OK64", "OK66", "OK67", "OK68", "OK71", "FC19", "FC16")], [
        W("power", "Unplug it and close the gauge plate.", "Unplug the machine power cord and turn the index knob fully clockwise to close the gauge plate before cleaning the slicer (Hobart). Turn off and unplug the machine before disassembling and cleaning (OSHA)."),
        S("gloves", "Put on a cut-proof glove."),
        S("box", "Take off the parts the manual says."),
        S("soap", "Wash, rinse and sanitize the parts in the sink."),
        W("stop", "Wipe the blade from the middle out.", "Clean around the ring guard with a moistened, folded paper towel between the ring guard and the knife; wash, rinse and sanitize both sides of the knife by wiping outward from the center. Never clean any part of the slicer with steel pads (Hobart)."),
        S("spray", "Sanitize the slicer body and blade."),
        S("check", "Let every part air-dry before you put it back."),
      ], { phase: "close", interval: "At least every 4 hours in use, and at close" }),
      op("slicer.sharpen", "Sharpen the blade", "maintain", "maker", OO("OK69"), [
        S("clean", "Clean the slicer first."),
        S("check", "Read the manual and do its sharpening steps."),
        S("clean", "Clean the slicer again after."),
      ], { interval: "Only when necessary (Hobart)" }),
      op("slicer.oil", "Oil the slide rod", "maintain", "maker", OO("OK70"), [
        S("power", "Unplug the slicer."),
        S("wrench", "Put 4 to 5 drops of the slicer oil on each side."),
      ], { interval: "Once a month (Hobart)" }),
    ],
  },
  {
    id: "blender-commercial", name: "Blender (commercial)", category: "cooking", icon: "blender", homes: false, business: true, surfaceId: "blender-jars",
    aliases: ["blender", "vitamix", "blendtec", "smoothie", "frappe"],
    ops: [
      op("cblender.rinse", "Rinse after each drink", "clean", "maker", OO("OK83"), [
        S("water", "Rinse the container right after use."),
        S("stop", "Never let a drink dry in the container."),
      ], { phase: "run", interval: "After each use" }),
      op("cblender.sanitize", "Clean and sanitize the container", "clean", "maker", [...OO("OK81", "OK82", "OK83"), ...HCA("A16")], [
        S("soap", "Run it with warm water and a few drops of dish soap."),
        S("water", "Rinse the container."),
        S("chemical", "Mix 1.5 teaspoons of bleach in 2 quarts of water."),
        S("water", "Fill the container 3/4 with the bleach mix."),
        S("power", "Run it on high for 30 seconds.", { timerSec: 30 }),
        S("wait", "Turn it off and wait 1 1/2 minutes."),
        S("water", "Pour it out and run it empty 5 seconds.", { timerSec: 5 }),
        W("stop", "Do not rinse. Let it air-dry.", "Do not rinse after sanitizing. Microwaving and automatic dishwashing are not recommended. Do not soak the blade assembly (Vitamix)."),
      ], { phase: "close", interval: "At close, and between nut and nut-free drinks" }),
      op("cblender.check", "Check the pad and drive socket", "maintain", "maker", OO("OK84"), [
        S("power", "Unplug the blender."),
        S("check", "Check the centering pad lies flat."),
        S("check", "Check the drive socket is not worn."),
        W("stop", "Never put on or take off the jar while it runs.", "Never put the container on or take it off while the motor is running (Vitamix)."),
      ], { interval: "Pad every 5 months; drive socket every 3 – 4 months (Vitamix)" }),
    ],
  },
  {
    id: "convection-oven", name: "Convection oven (commercial)", category: "cooking", icon: "oven", homes: false, business: true, surfaceId: "oven",
    aliases: ["convection oven", "commercial oven", "blodgett", "vulcan oven", "range oven", "deck oven"],
    ops: [
      op("coven.open", "Turn on and preheat", "operate", "maker", OO("OK53", "OK55", "OK59", "OK60"), [
        S("power", "Turn the fan switch on.", { detail: "This switch must be flipped to ON, otherwise the oven will not operate (Vulcan). The fan must be operating for the oven to heat (Blodgett)." }),
        S("thermo", "Set the temperature a little above the cook temperature.", { detail: "Blodgett: preheat 50°F above the cook temperature, 100°F above for frozen product. Vulcan range oven: 25 minutes to 400°F." }),
        S("wait", "Wait until the ready light goes out."),
        S("thermo", "Set it back to the cook temperature."),
      ], { phase: "open", interval: "At opening" }),
      op("coven.use", "Bake safely", "operate", "maker", OO("OK63", "OK58", "OK61"), [
        W("stop", "Never put foil or a pan on the oven floor.", "Never place a pan or aluminum foil on the bottom of the oven: it blocks the airflow (Blodgett)."),
        S("gloves", "Use oven mitts to move hot pans."),
        W("phone", "Call the gas company from outside if you smell gas.", "DO NOT try to light any appliance. DO NOT touch any electrical switches. Use an exterior phone to call your gas supplier immediately (Blodgett)."),
        W("wait", "Wait five minutes before you relight it.", "A complete five minute shutdown must be observed before the oven is relighted (Blodgett)."),
      ], { phase: "run" }),
      op("coven.close", "Turn off at close", "operate", "maker", OO("OK53", "OK54"), [
        S("power", "Turn the oven dial off."),
        S("power", "Turn the fan switch off."),
      ], { phase: "close", interval: "At close" }),
      op("coven.clean", "Clean the oven", "clean", "maker", OO("OK62"), [
        S("wait", "Let the oven get cold."),
        S("gloves", "Put on gloves."),
        S("spray", "Spray oven cleaner as its label says."),
        S("wait", "Leave it 10 to 20 minutes, or the label's time."),
        W("stop", "Keep oven cleaner off the fan and the probe.", "Keep caustic cleaner off the probe, element and blower wheel (Blodgett). Always clean the unit when it is cold."),
        S("water", "Wipe off all the cleaner with clean water."),
      ], { phase: "close" }),
    ],
  },
  {
    id: "commercial-range", name: "Commercial gas range", category: "cooking", icon: "stove", homes: false, business: true, surfaceId: "range-stovetop",
    aliases: ["commercial range", "restaurant stove", "burners", "vulcan range", "garland", "wolf range"],
    ops: [
      op("crange.light", "Light the burners", "operate", "maker", OO("OK51", "OK50", "OK52"), [
        S("check", "Check the pilots are lit."),
        S("power", "Turn a burner knob on."),
        W("stop", "Turn everything off if a burner won't light.", "If burners fail to light, turn off all valves, and call an authorized installation or service person (Vulcan). Do not attempt to relight a pilot without waiting 5 minutes."),
        W("phone", "Shut the main gas off if you smell gas.", "In the event a gas odor is detected, shut down units at main shutoff valve and contact the local gas company or gas supplier for service (Vulcan)."),
      ], { phase: "open" }),
      stoveSafety("crange.safe"),
      op("crange.close", "Shut down at close", "operate", "maker", OO("OK54"), [
        S("power", "Turn every burner knob off."),
        S("check", "Check the pilots are still lit, or the gas is off."),
      ], { phase: "close", interval: "At close" }),
      op("crange.grates", "Wash and oil the grates", "clean", "maker", OO("OK57"), [
        S("power", "Shut off the gas to the burners."),
        S("soap", "Wash the cast-iron grates with mild soap."),
        S("towel", "Dry them."),
        S("food", "Wipe on a light coat of oil."),
        S("power", "Heat them on LOW at least 15 minutes before use.", { timerSec: 900 }),
      ], { phase: "close", interval: "Daily (Vulcan)" }),
    ],
  },
  {
    id: "gas-range", name: "Gas stove (home)", category: "cooking", icon: "stove", homes: true, business: false,
    aliases: ["gas stove", "gas range", "burner", "stovetop", "hob", "cooktop"],
    ops: [
      op("gas.light", "Light a burner", "operate", "maker", OO("OH1", "OH2", "OH3"), [
        S("food", "Put the pan on the burner first."),
        S("power", "Push the knob in and turn it to LITE."),
        S("check", "Wait until you see a flame.", { detail: "All surface burners click, but only the burner with the knob at LITE makes a flame (Whirlpool)." }),
        W("power", "Turn the knob off if there is no flame.", "If you do not hear the igniter click, turn off the burner (Whirlpool). Do not keep trying."),
        S("power", "Turn the knob to the flame size you need."),
      ]),
      stoveSafety("gas.safe"),
      op("gas.smell", "If you smell gas", "operate", "maker", OO("OH4", "OH5", "OH6", "OH7", "OH8"), [
        S("check", "Check that every burner knob is off."),
        W("stop", "Never touch a light switch.", GAS_SMELL),
        W("stop", "Never light a match or the stove.", GAS_SMELL),
        W("people", "Leave the home now. Leave the door open.", GAS_SMELL),
        W("phone", "Call the gas company from outside.", GAS_SMELL),
        W("phone", "Call 911 if gas is burning.", GAS_SMELL),
      ]),
      op("gas.off", "Turn the stove off", "operate", "maker", OO("OH10"), [
        S("power", "Turn every burner knob to OFF."),
        S("check", "Check that no flame is left."),
      ]),
      fromHome("gas.clean", "stovetop-gas", "clean"),
    ],
  },
  {
    id: "electric-coil-range", name: "Electric coil stove", category: "cooking", icon: "stove", homes: true, business: false,
    aliases: ["electric stove", "coil stove", "coil burner", "electric range"],
    ops: [
      op("coil.use", "Cook on a coil burner", "operate", "maker", OO("OH16", "OH15"), [
        S("food", "Pick a pan that fits the burner.", { detail: "Pots should not overlap the burner by more than 1/2 inch (Whirlpool)." }),
        S("power", "Turn the knob to the heat you need."),
        S("power", "Turn the knob to OFF when you are done."),
        W("stop", "Never touch the coil after cooking.", "An H or HOT will display in the heat setting window when any surface cooking area is too hot to touch, even after it is turned off (Whirlpool)."),
      ]),
      stoveSafety("coil.safe"),
      fromHome("coil.clean", "stovetop-coil", "clean"),
    ],
  },
  {
    id: "glass-cooktop", name: "Glass cooktop", category: "cooking", icon: "stove", homes: true, business: false,
    aliases: ["glass top stove", "smooth top", "ceramic cooktop", "radiant cooktop"],
    ops: [
      op("glass.use", "Cook on a glass cooktop", "operate", "maker", OO("OH15"), [
        S("food", "Put the pan on the ring."),
        S("power", "Turn the control to the heat you need."),
        S("power", "Turn the control to OFF when you are done."),
        W("stop", "Never touch the glass while it says HOT.", "An H or HOT will display in the heat setting window when any surface cooking area is too hot to touch, even after the surface cooking area(s) is turned off (Whirlpool)."),
      ]),
      stoveSafety("glass.safe"),
      fromHome("glass.clean", "stovetop-glass", "clean"),
    ],
  },
  {
    id: "induction-cooktop", name: "Induction cooktop", category: "cooking", icon: "stove", homes: true, business: true,
    aliases: ["induction", "induction hob", "induction burner"],
    ops: [
      op("induction.pan", "Check a pan works", "setup", "maker", OO("OH17", "OH18"), [
        S("check", "Put a magnet on the bottom of the pan."),
        S("check", "Use the pan if the magnet sticks firmly.", { detail: "If the magnet sticks firmly, the cookware is induction compatible (KitchenAid). Non-ferromagnetic materials such as glass, ceramic, copper and aluminum will not work (Whirlpool)." }),
      ]),
      op("induction.use", "Cook on induction", "operate", "maker", OO("OH19", "OH15"), [
        S("food", "Put the pan in the middle of the ring."),
        S("power", "Turn the zone on and set the heat."),
        S("check", "Use a bigger pan if the zone does not start.", { detail: "If the pan is too small, the surface cooking area does not sense the pan and will not activate (Whirlpool)." }),
        S("power", "Turn the zone off when you are done."),
        W("stop", "Never touch the glass while it says HOT.", "An H or HOT will display when any surface cooking area is too hot to touch, even after it is turned off (Whirlpool)."),
      ]),
      stoveSafety("induction.safe"),
      fromHome("induction.clean", "stovetop-induction", "clean"),
    ],
  },
  {
    id: "oven", name: "Oven (home)", category: "cooking", icon: "oven", homes: true, business: false,
    aliases: ["oven", "range oven", "wall oven", "bake", "self clean"],
    ops: [
      op("oven.preheat", "Preheat and bake", "operate", "maker", OO("OH20", "OH21", "OH22", "OH13", "OH14"), [
        S("box", "Check nothing is stored inside the oven."),
        S("thermo", "Set the temperature and press start."),
        S("wait", "Wait for the beep that says it is hot.", { detail: "Whirlpool: ovens with a visible element preheat to 350°F in 12-15 minutes (+/-) 5 minutes." }),
        S("gloves", "Use oven mitts to put food in and take it out."),
        S("power", "Turn the oven off when you are done."),
        W("stop", "Never use the oven to heat your home.", "Never use an oven to heat your home (USFA)."),
        W("stop", "Keep the door shut if there is a fire inside.", "For an oven fire, turn off the heat and keep the door closed (NFPA). Call 9-1-1 after you leave."),
      ]),
      op("oven.selfclean", "Run the self-clean", "clean", "maker", KA("O1", "O2", "O3", "O6"), [
        S("box", "Take out the racks, pans and foil."),
        S("box", "Take anything plastic off the cooktop."),
        W("stop", "Never use oven cleaner in a self-clean oven.", "You should not use any commercial oven cleaner (KitchenAid): it may damage the interior and weaken the self-clean."),
        W("stop", "Never touch the oven door gasket.", "Do not clean, rub, damage or move the oven door gasket (KitchenAid)."),
        S("people", "Keep pets out of the kitchen."),
        S("power", "Start the self-clean cycle as the manual says."),
        W("stop", "Stay home while it runs.", "Never leave the oven unattended during a clean (KitchenAid). Self-clean may take 3-6 hours; steam clean about 1 to 1.5 hours."),
      ], { interval: "Before the oven is heavily soiled (KitchenAid)" }),
    ],
  },
  {
    id: "microwave", name: "Microwave", category: "cooking", icon: "microwave", homes: true, business: true, surfaceId: "microwave",
    aliases: ["microwave", "micro", "nuke", "amana"],
    ops: [
      op("micro.heat", "Heat food (business)", "operate", "law", [...OO("FC27", "OK108")], [
        S("box", "Put the food in a microwave-safe dish."),
        S("box", "Cover the dish."),
        S("power", "Heat it, and stir or turn it halfway."),
        S("thermo", "Check the food is 165°F in every part.", { ask: { kind: "number", label: "Food temperature", unit: "°F" } }),
        S("wait", "Let it stand covered for 2 minutes.", { timerSec: 120 }),
        W("stop", "Never run it empty or with metal inside.", "Do not operate equipment without load or food in oven cavity. Do not use metal utensils in oven (Amana)."),
      ], { phase: "run" }),
      op("micro.heat-home", "Heat food at home", "operate", "maker", OO("OH23", "OH24", "OH25", "OH26", "OH27", "OH28", "OH29"), [
        S("box", "Use a dish that says microwave-safe."),
        W("stop", "Never put foil or metal in the microwave.", "No food completely covered by aluminum foil or in a covered metal pan should be put in a microwave oven (USDA). Operating the oven empty or when food is completely wrapped in foil can damage it (USDA)."),
        S("box", "Cover the food with a lid or microwave wrap."),
        S("power", "Heat it, then stir it."),
        S("gloves", "Use oven mitts to take the dish out."),
        S("wait", "Let it stand at least 3 minutes.", { timerSec: 180 }),
        S("thermo", "Check leftovers reach 165°F with a thermometer."),
      ]),
      op("micro.broken", "If it sparks or door breaks", "operate", "osha", OO("OK111", "OH30"), [
        S("power", "Turn the microwave off right away."),
        S("power", "Unplug it."),
        S("phone", "Tell the person in charge.", { detail: "If you notice any sparking inside the microwave, immediately turn off the microwave, unplug it, report it to the supervisor, and do not use it (OSHA). Don't operate a microwave oven if the door does not close firmly or is bent, warped, or otherwise damaged (FDA)." }),
        W("stop", "Never use it again until it is fixed.", "Do not use a microwave that sparks or whose door does not close firmly (OSHA, FDA)."),
      ]),
      fromHome("micro.clean-home", "microwave", "clean", { title: "Clean the microwave" }),
      op("micro.close", "Clean the microwave at close", "clean", "law", [...OO("FC23", "OK109"), ...KA("MW2", "MW3")], [
        S("power", "Turn it off and let the turntable cool."),
        W("stop", "Never wash a hot glass turntable.", "Let the glass turntable cool fully before washing: the glass could shatter (KitchenAid)."),
        S("soap", "Wipe the inside and door seals with mild soapy water."),
        W("stop", "Never pour water into the microwave.", "Never pour water into microwave oven bottom. No ammonia or citrus cleaners (Amana)."),
        S("water", "Wipe off the soap with a damp cloth."),
      ], { phase: "close", interval: "At least every 24 hours (business)" }),
      op("micro.filter", "Clean the air filter", "maintain", "maker", OO("OK110"), [
        S("check", "Check the air vents for grease."),
        S("box", "Take out the air filter as the manual says."),
        S("soap", "Wash it, dry it and put it back."),
      ], { interval: "Check monthly (Amana)" }),
    ],
  },
  {
    id: "toaster", name: "Toaster", category: "cooking", icon: "toaster", homes: true, business: true,
    aliases: ["toaster", "bread toaster", "hamilton beach"],
    ops: [
      op("toaster.use", "Make toast", "operate", "maker", OO("OH31", "OH32", "OH33", "OH35"), [
        S("power", "Plug the toaster in."),
        S("food", "Put the bread in the slots."),
        S("box", "Push the lever down to start."),
        W("stop", "Never put a fork, knife or foil inside.", "Oversized foods, metal foil packages, or utensils must not be inserted in a toaster as they may involve a risk of fire or electric shock (Hamilton Beach)."),
        S("power", "Unplug it when you are done."),
      ]),
      fromHome("toaster.tray", "toaster-tray", "clean", { interval: "After each use (KitchenAid); weekly with daily use (Hamilton Beach)", extraCitations: OO("OH34") }),
    ],
  },
  {
    id: "blender-home", name: "Blender (home)", category: "cooking", icon: "blender", homes: true, business: false,
    aliases: ["blender", "smoothie", "ninja", "nutribullet", "instant ace"],
    ops: [
      op("hblender.use", "Use the blender", "operate", "maker", OO("OH49", "OH50", "OH51"), [
        S("food", "Put the food in the jar."),
        S("box", "Put the lid on tight."),
        S("power", "Plug it in and blend."),
        W("stop", "Keep hands and spoons out while it runs.", "Keep hands and utensils out of the pitcher while blending. Always operate the blender with the lid in place (Instant)."),
        S("power", "Unplug it before you take it apart."),
      ]),
      fromHome("hblender.clean", "blender", "clean", { extraCitations: KA("B1", "B6") }),
    ],
  },
  {
    id: "air-fryer", name: "Air fryer", category: "cooking", icon: "air-fryer", homes: true, business: false,
    aliases: ["air fryer", "airfryer", "instant vortex", "ninja", "cosori"],
    ops: [
      op("afryer.use", "Use the air fryer", "operate", "maker", [...OO("OH36", "OH37", "OH38", "OH40"), ...KA("T5")], [
        S("box", "Set it 5 inches away from the wall."),
        S("food", "Fill the basket no more than 3/4 full."),
        W("stop", "Never line the basket with foil.", "Don't line it with foil: it restricts airflow and may overheat (KitchenAid)."),
        S("power", "Set the time and heat, then start."),
        S("food", "Turn the food when the screen says Turn Food."),
        S("stop", "Stay home while it runs."),
      ]),
      fromHome("afryer.clean", "air-fryer", "clean", { interval: "After each use (KitchenAid)", extraCitations: [...KA("T5"), ...OO("OH39")] }),
    ],
  },
  {
    id: "pressure-cooker", name: "Pressure cooker (Instant Pot)", category: "cooking", icon: "pressure-cooker", homes: true, business: false,
    aliases: ["instant pot", "pressure cooker", "multicooker", "electric pressure cooker"],
    ops: [
      op("pcook.start", "Fill and start", "operate", "maker", OO("OH41", "OH42", "OH43"), [
        S("check", "Check the sealing ring is in place and not torn."),
        S("food", "Fill the pot no higher than the PC MAX line."),
        S("food", "Fill rice and beans only to the 1/2 line."),
        S("box", "Close and lock the lid."),
        S("power", "Set the program and start it."),
      ]),
      op("pcook.release", "Let the steam out safely", "operate", "maker", OO("OH44", "OH45", "OH46", "OH47"), [
        S("wait", "Wait for the pressure to come down by itself.", { detail: "Natural release: steam comes out gradually over time (up to 40 minutes or more) after cooking stops (Instant)." }),
        W("hands", "Keep your hands and face away from the vent.", "After cooking, set steam release switch to Vent position and wait for all pressure to release (Instant). The steam is very hot."),
        S("check", "Open it only when the float valve drops down."),
        W("stop", "Never force the lid open.", "DO NOT attempt to force the lid off the Instant Pot cooker base (Instant)."),
      ]),
      op("pcook.ring", "Replace the sealing ring", "maintain", "maker", OO("OH48", "OH43"), [
        S("box", "Take out the old sealing ring."),
        S("box", "Put in a new ring all the way around."),
        S("write", "Write down the date you changed it."),
      ], { interval: "Every 12 to 18 months (Instant)" }),
    ],
  },
  {
    id: "range-hood", name: "Range hood", category: "cooking", icon: "hood", homes: true, business: true, surfaceId: "hood-filters",
    aliases: ["range hood", "vent hood", "exhaust hood", "hood filter", "grease filter"],
    ops: [
      op("hood.filter", "Wash the grease filter", "clean", "maker", [...HMB("HM19"), ...KA("HD1")], [
        S("power", "Turn the hood and stove off."),
        S("wait", "Wait until the hood is cool."),
        S("box", "Take out the grease filter."),
        S("soap", "Wash it in warm soapy water or the dishwasher."),
        S("towel", "Let it dry, then put it back."),
      ]),
      op("hood.charcoal", "Change the charcoal filter", "maintain", "maker", [...HMB("HM19"), ...KA("HD1")], [
        S("check", "Check if your hood has a charcoal filter."),
        S("box", "Take out the old charcoal filter."),
        S("trash", "Throw it away. It can't be washed."),
        S("box", "Put in a new one."),
      ], { interval: "Up to 6 months with normal use (Whirlpool)" }),
      fromTask("hood.pro", "hood-pro", "maintain", "law", { title: "Get the hood cleaned (business)" }),
    ],
  },
  {
    id: "cast-iron-pan", name: "Cast iron pan", category: "cooking", icon: "pan", homes: true, business: false,
    aliases: ["cast iron", "skillet", "lodge", "frying pan"],
    ops: [
      fromHome("castiron.wash", "cast-iron-wash", "clean"),
      fromHome("castiron.rust", "cast-iron-rust", "maintain"),
    ],
  },
  {
    id: "stand-mixer", name: "Stand mixer", category: "cooking", icon: "mixer", homes: true, business: true, surfaceId: "mixer",
    aliases: ["stand mixer", "kitchenaid", "mixer", "dough hook", "beater"],
    ops: [
      op("mixer.clean", "Clean the mixer", "clean", "maker", KA("M4", "M17"), [
        S("power", "Unplug the mixer."),
        S("box", "Take off the bowl and beater."),
        S("check", "Check your manual for what can go in the dishwasher."),
        S("soap", "Wash the rest by hand in hot soapy water."),
        S("towel", "Dry each part well."),
        S("clean", "Wipe the mixer with a soft damp cloth."),
        S("box", "Store the mixer standing up."),
      ], { interval: "After each use" }),
      op("mixer.dime", "Check the beater height", "maintain", "maker", KA("M6"), [
        S("power", "Unplug the mixer."),
        S("check", "Check the gap between the beater and the bowl."),
        S("check", "Read the manual to set the gap."),
      ], { interval: "Regularly (KitchenAid)" }),
    ],
  },
];
/* KITCHEN_END */

/* EQUIPMENT_START: more equipment (2026-09-30). Coverage audit by business type: object-operations.md "Equipment by business type". */
/** The equipment guides: the only ones with state words (the truck rule, county notes, altitude). */
const buildEquipment = (U: ObjectopsWords): ObjectType[] => [
  /* ═════════════ EVERY KITCHEN: THERMOMETER, BOARDS, CAN OPENER ═════════════ */
  {
    id: "probe-thermometer", name: "Food thermometer", category: "other", icon: "thermo", homes: true, business: true, surfaceId: "thermometer-probe",
    aliases: ["probe thermometer", "food thermometer", "thermometer", "thermapen", "meat thermometer", "calibrate thermometer"],
    ops: [
      fromTask("probe.cook", "probe", "operate", "law", { phase: "run" }),
      fromTask("probe.hold", "holding", "operate", "law", { phase: "run", title: "Check hot and cold food" }),
      op("probe.clean", "Clean the probe", "clean", "law", OO("FC40", "FC16", "EQ283"), [
        S("soap", "Wash the probe with soap and water."),
        S("water", "Rinse it with clean water."),
        S("spray", "Sanitize the probe."),
        S("wait", "Let it air-dry."),
        S("box", "Put it away clean."),
      ], { phase: "run", interval: "Before you use it and before you put it away (Food Code 4-602.11)" }),
      op("probe.cal", "Check it in ice water", "maintain", "law", OO("FC38", "FC39", "EQ277", "EQ278", "EQ279", "EQ280", "EQ281"), [
        S("box", "Fill a cup to the top with crushed ice."),
        S("water", "Add cold water to just below the top of the ice."),
        S("thermo", "Put the probe in the middle, 2 inches deep."),
        S("check", "Keep it off the sides and bottom."),
        S("wait", "Wait at least 30 seconds.", { timerSec: 30 }),
        S("thermo", "Check it reads 32°F.", { ask: { kind: "number", label: "Reading in ice water", unit: "°F" }, detail: "A Fahrenheit food thermometer is accurate to ±2°F (Food Code 4-203.11). ThermoWorks: a proper ice bath is 32.0°F; a cup of ice water can be 12 or more degrees too high." }),
        S("wrench", "Fix it as its maker says, or replace it."),
        W("stop", "Don't check it in boiling water here.", `Boiling water reads 212 degrees F only at sea level; at altitude it boils lower by at least 2 and perhaps as much as 5 degrees F (USDA FSIS).${sp(U.altitudeNote)}`),
      ], { interval: "As its maker says, and when a reading seems wrong (Food Code 4-502.11)" }),
    ],
  },
  {
    id: "cutting-boards", name: "Cutting boards", category: "other", icon: "cutting-board", homes: true, business: true, surfaceId: "cutting-board-plastic",
    aliases: ["cutting board", "cutting boards", "chopping board", "color coded boards", "board"],
    ops: [
      op("boards.clean", "Wash and sanitize a board", "clean", "law", OO("FC42", "FC19", "FC16", "FC13"), [
        S("trash", "Scrape the food off the board."),
        S("soap", "Wash it in hot soapy water.", { detail: "Wash water at least 110°F, or the temperature on the detergent label (Food Code 4-501.19)." }),
        S("water", "Rinse it with clean water."),
        S("spray", "Sanitize it."),
        S("wait", "Let it air-dry."),
      ], { phase: "run", interval: "Every 4 hours in use, and when you change foods (Food Code 4-602.11)" }),
      op("boards.switch", "Switch foods safely", "operate", "law", OO("FC42"), [
        W("stop", "Clean the board before a different raw meat.", "Clean food-contact surfaces before each use with a different type of raw animal food such as beef, fish, lamb, pork, or poultry (Food Code 4-602.11(A)(1))."),
        W("stop", "Clean the board before food that won't be cooked.", "Clean food-contact surfaces each time there is a change from working with raw foods to working with ready-to-eat foods (Food Code 4-602.11(A)(2))."),
        S("clean", "Clean it any time something dirty touches it."),
      ], { phase: "run" }),
      op("boards.check", "Check boards for deep cuts", "maintain", "law", OO("FC41"), [
        S("check", "Look for deep cuts and stains that won't wash out."),
        S("box", "Take a scarred board out of use."),
        S("trash", "Get it resurfaced, or throw it away."),
      ]),
    ],
  },
  {
    id: "can-opener", name: "Can opener", category: "other", icon: "can-opener", homes: false, business: true, surfaceId: "can-opener",
    aliases: ["can opener", "edlund", "table can opener", "opener"],
    ops: [
      op("canopener.clean", "Clean the can opener", "clean", "law", OO("FC43", "FC19", "FC16", "EQ273", "EQ274"), [
        S("clean", "Clean food off the gear, knife and can stop with a brush."),
        S("box", "Take off the blade and gear as its manual says."),
        S("soap", "Wash the parts in hot soapy water."),
        S("water", "Rinse them."),
        S("spray", "Sanitize them."),
        S("clean", "Wipe the base and the bar."),
        S("wait", "Let the parts air-dry, then put them back."),
        S("food", "Wipe a little vegetable oil on the knife and gear."),
      ], { phase: "close", interval: "Daily (Edlund); at least every 4 hours in use (Food Code 4-602.11)" }),
      op("canopener.blade", "Check the blade", "maintain", "law", OO("FC43", "EQ275", "EQ276"), [
        S("check", "Look at the blade for nicks and dull spots."),
        W("stop", "Look for metal bits on the lids you cut.", "Cutting or piercing parts of can openers shall be kept sharp to minimize the creation of metal fragments that can contaminate food when the container is opened (Food Code 4-501.11(C)). Throw away food with metal bits in it."),
        W("stop", "Never sharpen the blade. Replace it.", "A worn knife can cause metal slivers. Never resharpen. Blade is reversible and should be replaced every 2500 cans (Edlund)."),
        S("wrench", "Turn the blade over or replace it every 2500 cans."),
      ], { interval: "Every 2500 cans (Edlund)" }),
    ],
  },

  /* ═════════════ OUTSIDE AND PESTS ═════════════ */
  {
    id: "dumpster", name: "Dumpster and trash area", category: "outdoor", icon: "dumpster", homes: false, business: true, surfaceId: "dumpster-area",
    aliases: ["dumpster", "trash enclosure", "garbage bin outside", "refuse", "recycling bin", "dumpster area"],
    ops: [
      op("dumpster.close", "Close up the dumpster", "operate", "law", OO("FC48", "FC49", "EQ346"), [
        S("trash", "Put every bag inside the dumpster."),
        S("check", "Close the lids all the way."),
        S("check", "Check the drain plug is in."),
      ], { phase: "close", interval: "Every time you take trash out" }),
      op("dumpster.clean", "Clean up the trash area", "clean", "law", OO("FC50", "FC51"), [
        S("trash", "Pick up trash and litter on the ground."),
        S("box", "Take away anything that is not trash."),
        S("clean", "Sweep the pad."),
        S("phone", "Ask for more pickups if it overflows or smells."),
      ], { interval: "Often enough that dirt and smells don't build up (Food Code 5-501.116)" }),
    ],
  },
  {
    id: "bait-station", name: "Pest bait stations", category: "other", icon: "bait-station", homes: false, business: true,
    aliases: ["bait station", "bait box", "rodent bait", "mouse trap", "rat trap", "pest control", "pest"],
    ops: [
      op("bait.look", "Walk past the bait stations", "operate", "law", [...OO("FC52", "FC53", "EQ348", "EQ351"), ...TGR("TG41", "TG42")], [
        S("bug", "Look at each bait station as you walk by."),
        S("check", "Check its lid is shut."),
        W("stop", "Never open or move a bait station.", "Rodent bait shall be contained in a covered, tamper-resistant bait station (Food Code 7-206.12). Only a certified pest control operator puts out or services bait (Food Code 7-202.12)."),
        S("phone", "Tell the person in charge if one is open or broken."),
        S("write", "Write down droppings or other signs of pests."),
      ], { interval: "Routinely (Food Code 6-501.111)" }),
      fromTask("bait.service", "pest-service", "maintain", "law"),
      op("bait.hire", "Hire the pest company", "setup", "law", OO("EQ353", "EQ350", "FC52"), [
        S("check", "Ask to see the pest company's license."),
        S("check", "Ask that all rodent bait sits in locked stations."),
        S("write", "Keep each service report."),
      ]),
      op("bait.who", "Who services it", "maintain", "law", [...TGR("TG41", "TG42"), ...OO("EQ353")], [
        S("people", "Let only a licensed pest company set or service bait.", { detail: "Pesticides are used only as the label allows in a food business and under the applicator's certification (Food Code 7-202.12). Rodent bait sits in a covered, tamper-resistant bait station (7-206.12). EPA: ask to see the company's license." }),
        S("write", "Keep each service report."),
      ], { service: { who: "A licensed pest control company", proof: "Each service report", check: "pest_check" } }),
    ],
  },
  {
    id: "service-sink", name: "Mop sink", category: "sink", icon: "sink", homes: false, business: true,
    aliases: ["mop sink", "service sink", "janitor sink", "utility sink", "floor sink"],
    ops: [
      op("msink.use", "Use the mop sink", "operate", "law", [...OO("FC45", "FC46"), ...TGR("TG16")], [
        S("water", "Fill mop buckets only at the mop sink."),
        S("water", "Dump dirty mop water only in the mop sink."),
        W("stop", "Never dump mop water in a toilet.", "Toilets and urinals may not be used as a service sink for the disposal of mop water and similar liquid waste (Food Code 5-203.13(B)). " + MOP_WATER),
        S("towel", "Hang the mop up to air-dry."),
      ], { phase: "close", interval: "Every time you mop" }),
      op("msink.clean", "Clean the mop sink", "clean", "law", OO("FC33"), [
        S("water", "Rinse the sink after you dump the bucket."),
        S("clean", "Scrub off dirt that stays behind."),
      ], { phase: "close", interval: "Often enough that dirt does not build up (Food Code 4-602.13)" }),
    ],
  },
  /* ═════════════ FOOD TRUCK ═════════════ */
  {
    id: "fresh-water-tank", name: "Fresh water tank", category: "other", icon: "water-tank", homes: false, business: true,
    aliases: ["fresh water tank", "potable water tank", "water tank", "drinking water hose", "clean water tank"],
    ops: [
      fromTask("fwtank.fill", "mfb-water", "operate", "law", { phase: "open" }),
      op("fwtank.caps", "Put caps on the water fittings", "operate", "law", [...OO("EQ304", "EQ305", "EQ308", "EQ303", "FC55")], [
        S("check", "Put caps on the tank inlet and outlet when not in use."),
        S("check", "Put caps on the hose ends, or join them together."),
        S("box", "Store the drinking water hose away from waste hoses."),
        W("stop", "Never fill the tank with a garden hose.", `Use only a hose designed and intended to convey potable water; it is never used for wastewater or toxic chemicals${par(U.truckRuleLabel)}.${sp(U.hoseCountyNote)}`),
      ], { phase: "close", interval: "Every time you fill it" }),
      op("fwtank.sanitize", "Flush and sanitize the tank", "clean", "law", [...OO("EQ306", "EQ307", "FC54")], [
        S("check", "Plan a flush when new, after repairs, or after 30 days unused.", { detail: `Flush and sanitize any water tank, pump, and hoses before placing into service after initial purchase, construction, repair, modification, and periods of nonuse of 30 days or more, and as often as necessary${par(U.truckRuleLabel)}.${sp(U.flushCountyNote)}` }),
        S("phone", "Ask your health department how to mix the sanitizer."),
        S("water", "Flush the tank, pump and hoses with drinking water."),
        S("chemical", "Sanitize the tank, pump and hoses."),
        S("water", "Drain it and refill with drinking water."),
        S("write", "Write down the date you did it."),
      ], { interval: `When new, after repairs, and after 30 days unused${U.flushIntervalTail ?? ""}` }),
    ],
  },
  {
    id: "waste-water-tank", name: "Waste water tank", category: "other", icon: "water-tank", homes: false, business: true,
    aliases: ["waste water tank", "grey water tank", "gray water tank", "wastewater tank", "dump tank"],
    ops: [
      op("wwtank.dump", "Dump the waste tank", "clean", "law", [...OO("EQ312", "EQ313", "EQ314", "EQ316", "FC57")], [
        S("water", "Dump waste water only at the commissary or approved site."),
        W("stop", "Never dump it on the ground or in a storm drain.", `Wastewater leaves a mobile food business only at an approved commissary, an approved waste servicing area, or into a wastewater transport vehicle${par(U.truckRuleLabel)}.${sp(U.dumpFeeNote)}`),
        S("water", "Flush and drain the tank while you are there."),
        S("check", "Close the drain valve."),
        S("write", "Write the visit in your commissary log."),
      ], { phase: "close", interval: "At each commissary visit" }),
      op("wwtank.size", "Check the tank size", "setup", "law", [...OO("EQ310", "EQ311", "FC56")], [
        S("check", "Check the waste tank is 15% bigger than the fresh tank.", { detail: `Sized 15% larger in capacity than the water supply tank, sloped to a drain 1 inch or greater with a shut-off valve${par(U.truckRuleLabel)}.${sp(U.wasteTankCountyNote)}` }),
        S("check", "Check the drain has a shut-off valve."),
      ]),
    ],
  },
  {
    id: "generator", name: "Generator", category: "outdoor", icon: "generator", homes: false, business: true,
    aliases: ["generator", "honda generator", "inverter generator", "power generator", "genset"],
    ops: [
      op("gen.place", "Set up the generator", "setup", "maker", [...OO("EQ318", "EQ319", "EQ320", "EQ321", "EQ330")], [
        W("stop", "Run it only outside, never inside the truck.", "Breathing carbon monoxide can KILL YOU IN MINUTES. Never operate a generator inside any type of vehicle, trailer, or any enclosed or partly enclosed space (Honda)."),
        S("check", "Set it more than 20 feet from windows, doors and vents.", { detail: "CDC: only use generators outside, more than 20 feet away from any windows, doors, and vents. Honda: keep it at least 3 feet from buildings and vehicles, and do not enclose it." }),
        S("check", "Point the exhaust away from people and buildings."),
        W("stop", "Move it if the CO alarm stops the engine.", "If the CO-MINDER detects a high carbon monoxide concentration, the engine will stop and the CO ALARM indicator will flash for about five minutes. Change the operating location before restarting (Honda)."),
      ], { phase: "open", interval: "Every time you set up" }),
      op("gen.start", "Start the generator", "operate", "maker", OO("EQ325"), [
        S("check", "Check your generator's manual for its start steps."),
        S("power", "Unplug everything from the generator."),
        S("power", "Turn the fuel cap vent on."),
        S("power", "Close the choke if the engine is cold."),
        S("power", "Turn the engine switch on and pull the starter."),
        S("power", "Open the choke slowly as it warms up."),
      ], { phase: "open" }),
      op("gen.fuel", "Refuel safely", "operate", "maker", [...OO("EQ322", "EQ323", "EQ324")], [
        W("stop", "Never fuel it while the public is near.", `No fueling during event or with public present${par(U.truckFireChecklist)}.`),
        S("power", "Turn it off and let the engine cool."),
        S("check", "Fill it outside on level ground."),
        S("stop", "Don't overfill the tank."),
        S("towel", "Wipe up any spilled fuel."),
        S("check", "Move it 10 feet away before you start it."),
      ], { phase: "run" }),
      op("gen.stop", "Stop the generator", "operate", "maker", OO("EQ326"), [
        S("power", "Turn off or unplug everything on it."),
        S("power", "Turn the engine switch off."),
        S("wait", "Let the engine cool."),
        S("power", "Turn the fuel cap vent off."),
      ], { phase: "close" }),
      op("gen.oil", "Check and change the oil", "maintain", "maker", [...OO("EQ327", "EQ328", "EQ329")], [
        S("check", "Check the oil level before each use."),
        S("check", "Check the air filter before each use."),
        S("wrench", "Change the oil after the first 20 hours."),
        S("wrench", "Change it every 6 months or 100 hours after that."),
        S("write", "Log the hours you run it."),
      ], { interval: "First month or 20 hours, then every 6 months or 100 hours (Honda)" }),
      op("gen.who", "Who services it", "maintain", "maker", OO("EQ354", "EQ328"), [
        S("sign", "Check your manual for the jobs the dealer does."),
        S("wrench", "Take it to the dealer for valve and fuel tank work.", { detail: "Honda: valve clearance, the combustion chamber, the fuel tank and filter, and the fuel tube should be serviced by your servicing dealer, unless you have the proper tools and are mechanically proficient." }),
        S("write", "Log the hours you run it.", { detail: "For commercial use, log hours of operation to determine proper maintenance intervals (Honda)." }),
      ], { service: { who: "The generator's servicing dealer", proof: "Your log of the hours it ran" } }),
    ],
  },
  {
    id: "propane-tank", name: "Propane tanks", category: "outdoor", icon: "propane", homes: false, business: true,
    aliases: ["propane", "propane tank", "lp tank", "lpg", "gas cylinder", "propane tanks"],
    ops: [
      op("propane.check", "Check the propane tanks", "maintain", "law", [...OO("EQ331", "EQ332", "EQ333", "EQ334", "EQ336")], [
        S("check", "Check the tanks are outside or in an outside cabinet.", { detail: `Containers installed outside or in vapor-tight cabinets accessible from outside; LPG cylinders shall not be installed, transported, or stored inside the vehicle (${[U.truckFireChecklist, "NFPA 58 6.26.3"].filter(Boolean).join(", ")}).` }),
        S("check", "Check each tank is strapped down tight."),
        S("check", "Check the shutoff valve is easy to reach."),
        S("check", "Check the gas detector and No Smoking sign are in place."),
        W("stop", "Never swap or fill tanks with the public near.", `No fueling or tank exchange during event or with public present${par(U.truckFireChecklist)}.`),
      ], { phase: "open", interval: "Before each event" }),
      op("propane.smell", "If you smell gas", "operate", "maker", OO("OK50", "EQ335"), [
        W("stop", "Turn off the tank valve if you smell gas.", `In the event a gas odor is detected, shut down units at main shutoff valve and contact the local gas company or gas supplier for service (Vulcan). The LPG system shall be leak free${par(U.truckFireChecklist)}.`),
        S("phone", "Call your gas supplier."),
        S("stop", "Don't cook until it is fixed."),
      ], { phase: "run" }),
      op("propane.close", "Close the propane", "operate", "law", OO("EQ335"), [
        S("power", "Close the tank valves."),
        S("check", "Keep the truck away from heat, flames and open pits."),
      ], { phase: "close", interval: "When closed or parked" }),
      // A truck's yearly fire inspection checks the propane where the state's fire rule says so (Utah R710-14-5, -6); elsewhere
      // only the gas supplier line, which is the maker's own (Vulcan OK50), is said.
      op("propane.who", "Who services it", "maintain", U.truckFireChecklist ? "law" : "maker", U.truckFireChecklist ? OO("EQ355", "EQ356", "EQ331", "OK50") : OO("OK50"), [
        ...(U.truckFireChecklist ? [
          S("check", "Get the truck's fire inspection every year.", { detail: `The fire inspector uses the checklist the State Fire Prevention Board approved, which checks the propane containers and piping, and gives a sticker valid for one year from the inspection${par(U.truckFireChecklist)}.` }),
          S("sign", "Keep the sticker inside the rear-most door."),
        ] : []),
        S("phone", "Call your gas supplier for service.", { detail: "In the event a gas odor is detected, shut down units at the main shutoff valve and contact the local gas company or gas supplier for service (Vulcan)." }),
      ], U.truckFireChecklist ? { interval: "Every year (fire inspection)", service: { who: "The fire inspector checks it; your gas supplier services it", proof: "The fire inspection sticker" } } : { service: { who: "Your gas supplier" } }),
    ],
  },

  /* ═════════════ OUTSIDE: GREASE ═════════════ */
  {
    id: "grease-trap", name: "Grease trap", category: "outdoor", icon: "grease-trap", homes: false, business: true, surfaceId: "grease-trap",
    aliases: ["grease trap", "grease interceptor", "fog", "used oil", "grease hauler"],
    ops: [
      op("grease.daily", "Keep grease out of drains", "operate", "suggested", [...OO("EQ342", "EQ343", "EQ344", "FC47"), SUGG(`City best practices (${U.trapUtility ? `${U.trapUtility} and the restaurant association guide it hosts` : "the restaurant association guide"}), not a Food Code rule; your sewer district's own rule may differ.`)], [
        S("trash", "Scrape plates and pans into the trash before rinsing."),
        W("stop", "Never pour grease or oil down a drain.", `Do not pour cooking oil into the drain${par(U.trapUtility)}. Collect waste oil and store for recycling in a tightly closed container. Clean up spills immediately.`),
        S("box", "Pour used oil into a closed container for pickup."),
        S("soap", "Wash greasy mats only in a sink that goes to the trap."),
        S("towel", "Wipe up grease spills now."),
      ], { phase: "close" }),
      op("grease.pump", "Get the grease trap pumped", "maintain", "suggested", [...OO("EQ338", "EQ339", "EQ340", "EQ341", "FC47"), SUGG(`${U.trapUtility ? `${U.trapUtility} and restaurant` : "Restaurant"} association best practices; your sewer district sets the real schedule.`)], [
        S("check", "Check how full the trap is."),
        S("phone", "Call a licensed pumper when it is 25 percent full.", { detail: `When the thickness of floating FOG and settled solids layers exceeds 25 percent of the device depth, the device needs maintenance (restaurant association guide${U.trapUtility ? `, hosted by ${U.trapUtility}` : ""}).` }),
        S("clean", "Clean an indoor grease trap at least once a week."),
        S("check", "Check the baffles inside are still in place."),
        S("write", "Keep every pump-out record for five years."),
      ], { interval: `Indoor traps weekly${par(U.trapCity)}; interceptors typically four times a year (restaurant association)` }),
      op("grease.who", "Who services it", "maintain", "suggested", [...OO("EQ339", "EQ340"), SUGG(`${U.trapUtility ?? "City"} best practices, not a Food Code rule; your sewer district sets the real rule.`)], [
        S("phone", "Call a licensed contractor to pump and clean the trap.", { detail: `A licensed contractor should be used to completely pump and clean your grease removal device${par(U.trapUtility)}.` }),
        S("write", "Keep every pump-out record for five years."),
      ], { service: { who: "A licensed grease pumping contractor", proof: "Each pump-out record" } }),
    ],
  },
  /* ═════════════ COLD ═════════════ */
  {
    id: "walk-in-freezer", name: "Walk-in freezer", category: "cold", icon: "freezer", homes: false, business: true,
    aliases: ["walk-in freezer", "walk in freezer", "freezer room", "kolpak", "norlake"],
    ops: [
      coldCheck("wfreezer.temp", "walk-in freezer", "frozen"),
      op("wfreezer.floor", "Keep food off the floor", "operate", "law", OO("FC35"), [
        S("box", "Put every box and bag on a shelf."),
        S("check", "Check nothing sits on the floor."),
      ], { phase: "run", interval: "Each delivery and at close" }),
      op("wfreezer.close", "Close up the walk-in freezer", "operate", "law", OO("FC36", "FC44"), [
        S("check", "Check nothing blocks the door."),
        S("check", "Close the door all the way."),
        S("check", "Check the door seal is not torn."),
        S("thermo", "Check the food is still frozen hard."),
      ], { phase: "close", interval: "At close" }),
      op("wfreezer.door", "Check the door and gaskets", "maintain", "maker", OO("EQ262", "EQ263", "EQ264"), [
        S("check", "Check the door closes easily and the sweep gasket is whole."),
        S("check", "Look for frost or water at the door frame."),
        S("phone", "Call for service if frost builds there."),
        S("box", "Hang a strip curtain if the door stays open over 5 minutes."),
      ], { interval: "Monthly (Kolpak)" }),
      op("wfreezer.clean", "Clean the walk-in freezer", "clean", "maker", OO("EQ265", "EQ266"), [
        S("clean", "Wipe metal and door gaskets with mild detergent."),
        S("towel", "Dry them."),
        W("stop", "Never hose down the walk-in.", "Never use high pressure hose or water to clean walk-in. Condensate water must never be allowed to drip on the walk-in floor (Kolpak)."),
        S("clean", "Keep the floor clean and dry."),
      ], { interval: "Frequently (Kolpak)" }),
      op("wfreezer.coil", "Book the coil cleaning", "maintain", "maker", OO("EQ268", "EQ269"), [
        S("check", "Check it defrosts right each month."),
        S("phone", "Get the evaporator coil cleaned every 6 months."),
        S("phone", "Get the condenser coil cleaned every 3 months."),
      ], { interval: "Defrost monthly; evaporator every 6 months; condenser every 3 months (Kolpak)" }),
    ],
  },
  /* ═════════════ HOT HOLDING ═════════════ */
  {
    id: "hot-holding-cabinet", name: "Hot-holding cabinet", category: "hot_holding", icon: "warmer", homes: false, business: true,
    aliases: ["hot holding cabinet", "hot box", "warming cabinet", "holding cabinet", "cres cor", "metro c5", "cvap", "proofer"],
    ops: [
      op("hhc.open", "Turn on and preheat", "operate", "maker", OO("EQ100", "EQ101", "EQ102", "EQ107", "EQ108", "EQ109"), [
        S("power", "Turn the power switch on."),
        S("water", "Fill the water pan with hot tap water, if it has one.", { detail: "Metro C5: fill the stainless water pan to 1/2\" from the top with clean HOT tap water." }),
        S("thermo", "Turn the temperature control to the top setting.", { detail: "Cres Cor: thermostat to #10, preheat 30 minutes. Metro C5: temperature control to 10; heat-up from 72°F to 160°F takes about 45 minutes." }),
        S("wait", "Preheat it for 30 minutes.", { timerSec: 1800 }),
        S("thermo", "Set it back to the holding setting.", { detail: "Cres Cor: #8 (about 190°F). Metro C5: setting 6-8 is typical for 150°F to 160°F." }),
      ], { phase: "open", interval: "At opening" }),
      op("hhc.load", "Load hot food", "operate", "law", OO("FC25", "FC26", "FC28", "EQ110", "EQ101"), [
        W("stop", "Put in only food that is already hot.", "This is not a rethermalization cabinet; food must be at the appropriate temperature before being placed into it (Metro). Food that was cooked and cooled is reheated to 165°F for 15 seconds within 2 hours before hot holding (Food Code 3-403.11)."),
        S("thermo", "Check the food is 135°F or hotter.", { ask: { kind: "number", label: "Food temperature", unit: "°F" }, detail: "Hot food is held at 135°F or above (Food Code 3-501.16). Cres Cor calls 140°F or higher proper holding temperature." }),
        S("thermo", "Read the cabinet's own thermometer."),
        S("check", "Close the door."),
        W("phone", "Tell the person in charge if food is under 135°F.", "Hot food is held at 135°F or above (Food Code 3-501.16). The cabinet holds food; it does not reheat it."),
      ], { phase: "run", interval: "Each time food goes in, and during service" }),
      op("hhc.water", "Check the water pan", "operate", "maker", OO("EQ107", "EQ111"), [
        S("check", "Check the water level every 3 hours."),
        S("water", "Add hot tap water to fill it."),
        W("stop", "Let the water cool before you empty it.", "Water inside this cabinet's pan is hot during use! Turn off and allow the water to cool before emptying the pan. Water dripping from open doors can be a slip hazard (Metro)."),
      ], { phase: "run" }),
      op("hhc.close", "Turn off at close", "operate", "maker", OO("EQ103", "EQ112"), [
        S("power", "Turn the thermostat off."),
        S("power", "Turn the power switch off."),
      ], { phase: "close", interval: "At close" }),
      op("hhc.clean", "Clean the cabinet", "clean", "maker", OO("EQ104", "EQ105", "EQ113", "EQ114"), [
        S("power", "Unplug it and let it cool."),
        S("water", "Empty the water pan once it is cool."),
        S("box", "Take out the slide racks."),
        S("clean", "Wipe it with a damp cloth and mild cleaner."),
        W("stop", "Never spray water into the controls.", "Do not wash the cabinet with a water jet or high-pressure water. Do not spray or pour water into the control module (Metro). Do NOT use abrasives or harsh chemicals (Cres Cor)."),
        S("water", "Rinse off all the cleaner."),
        S("towel", "Dry it with a towel."),
      ], { phase: "close", interval: "Regularly, so dirt does not build up (Cres Cor)" }),
    ],
  },
  {
    id: "soup-warmer", name: "Soup warmer", category: "hot_holding", icon: "warmer", homes: false, business: true,
    aliases: ["soup warmer", "soup kettle", "soup well", "round warmer", "cooker warmer", "nemco", "apw"],
    ops: [
      op("soup.open", "Fill and preheat", "operate", "maker", OO("EQ116", "EQ117", "EQ123", "EQ125"), [
        S("water", "Pour water in the well as the manual says.", { detail: "Nemco: pour 2-1/3 cups of water into the well. APW Wyott RCW: add 120°-140° hot water, 3 cups (RCW-7) or 5 cups (RCW-11)." }),
        W("stop", "Never run it without water in the well.", "Do not operate without water in the well (Nemco). NEVER POUR COLD WATER INTO DRY HEATED UNITS (APW Wyott)."),
        S("box", "Cover the well with an empty inset or lid."),
        S("thermo", "Turn it to high to preheat.", { detail: "APW Wyott: preheat on High for approximately 10 minutes." }),
        S("food", "Put the hot soup in an inset, never in the well."),
      ], { phase: "open", interval: "At opening" }),
      op("soup.check", "Check the hot soup", "operate", "law", OO("FC25", "FC26", "EQ118", "EQ124"), [
        W("stop", "Put in only soup that is already hot.", "Cold food cannot be inserted for rethermalizing while hot food is held (APW Wyott). Food that was cooked and cooled is reheated to 165°F for 15 seconds within 2 hours before hot holding (Food Code 3-403.11)."),
        S("thermo", "Check the soup is 135°F or hotter.", { ask: { kind: "number", label: "Soup temperature", unit: "°F" }, detail: "Hot food is held at 135°F or above (Food Code 3-501.16). Nemco recommends above 140 degrees F; APW Wyott says never hold food below 150°F." }),
        S("check", "Keep the lid on when you are not serving."),
        S("water", "Add hot water when the well runs low."),
        W("phone", "Tell the person in charge if it is under 135°F.", "Hot food is held at 135°F or above (Food Code 3-501.16). The warmer holds food; it does not reheat it."),
      ], { phase: "run", interval: "During service" }),
      op("soup.close", "Clean the warmer at close", "clean", "maker", OO("EQ120", "EQ121", "EQ125", "EQ126"), [
        S("fridge", "Put leftover soup in the fridge."),
        S("power", "Unplug it and let it cool."),
        S("water", "Empty the hot water carefully."),
        S("clean", "Wipe the well with a damp soapy cloth."),
        W("stop", "Never put the warmer in water.", "Do not immerse in water or other liquid. Do not clean with a water jet. DO NOT USE STEEL WOOL – USE NYLON PADS (Nemco)."),
        S("clean", "Use a nylon pad, never steel wool."),
      ], { phase: "close", interval: "After every use; change the water every 24 hours in continuous use (Nemco)" }),
      op("soup.descale", "Descale the well", "maintain", "maker", OO("EQ122"), [
        S("chemical", "Mix 1 teaspoon white vinegar in 1 cup of water."),
        S("wait", "Let it stand in the well."),
        S("water", "Rinse it out. Do it again if needed."),
      ], { interval: "When scale builds up (Nemco)" }),
    ],
  },
  {
    id: "heat-lamp", name: "Heat lamp or strip warmer", category: "hot_holding", icon: "warmer", homes: false, business: true,
    aliases: ["heat lamp", "strip warmer", "glo-ray", "hatco", "food warmer", "pass heat lamp"],
    ops: [
      op("lamp.open", "Turn on the heat lamp", "operate", "maker", OO("EQ141", "EQ142", "EQ143"), [
        S("power", "Turn the heat lamp on."),
        S("wait", "Wait 15 to 20 minutes for it to heat up."),
        S("food", "Put only hot food under it.", { detail: "This unit is for holding pre-heated food product only (Hatco)." }),
      ], { phase: "open", interval: "At opening" }),
      op("lamp.hold", "Check food under the lamp", "operate", "law", OO("FC25", "EQ143"), [
        S("thermo", "Check the food is 135°F or hotter.", { ask: { kind: "number", label: "Food temperature", unit: "°F" } }),
        W("phone", "Tell the person in charge if it is under 135°F.", "Hot food is held at 135°F or above (Food Code 3-501.16)."),
        W("stop", "Don't touch the lamp. It gets hot.", "BURN HAZARD: Some exterior surfaces on unit will get hot. Use caution when touching these areas (Hatco)."),
      ], { phase: "run", interval: "During service" }),
      op("lamp.close", "Turn off the heat lamp", "operate", "maker", OO("EQ144"), [
        S("power", "Turn the heat lamp off."),
      ], { phase: "close", interval: "At close" }),
      op("lamp.clean", "Clean the heat lamp", "clean", "maker", OO("EQ145", "EQ146", "EQ147"), [
        S("power", "Turn it off, unplug it and let it cool."),
        S("clean", "Wipe the metal with a damp soft cloth."),
        S("towel", "Wipe it dry."),
        W("stop", "Never spray or soak the heat lamp.", "DO NOT submerge or saturate with water. Do not steam clean or use excessive water (Hatco)."),
        S("clean", "Clean plastic guards with mild soap, not glass cleaner."),
      ], { phase: "close", interval: "Daily (Hatco)" }),
    ],
  },
  {
    id: "steam-kettle", name: "Steam kettle", category: "cooking", icon: "steam-kettle", homes: false, business: true,
    aliases: ["steam kettle", "steam jacketed kettle", "tilting kettle", "groen", "soup kettle big"],
    ops: [
      op("skettle.check", "Check the kettle before use", "operate", "maker", OO("EQ179"), [
        S("check", "Check the jacket water is mid sight glass."),
        S("check", "Check the gauge reads 20 to 30 below zero when cold.", { detail: "If the gauge does not show 20 to 30 inches of mercury (Hg) vacuum, see Jacket Vacuum in the manual (Groen)." }),
        S("phone", "Tell the person in charge if either one is off."),
      ], { phase: "open", interval: "Every day (Groen)" }),
      op("skettle.use", "Cook in the kettle safely", "operate", "maker", OO("EQ181", "EQ182", "EQ184"), [
        W("stop", "Never heat an empty kettle.", "DO NOT HEAT AN EMPTY KETTLE. EXCESSIVE STEAM PRESSURE COULD DEVELOP (Groen)."),
        S("check", "Keep liquid 2 to 3 inches below the rim."),
        W("gloves", "Wear a mitt and apron to tilt it.", "WEAR PROTECTIVE OVEN MITT AND PROTECTIVE APRON. STAND TO LEFT OR RIGHT SIDE OF KETTLE. DO NOT STAND DIRECTLY IN POUR PATH (Groen)."),
        S("people", "Stand to the side, out of the pour path."),
        S("box", "Take off the lid and basket before you tilt."),
      ], { phase: "run" }),
      op("skettle.close", "Shut down the kettle", "operate", "maker", OO("EQ185"), [
        S("power", "Turn the main power switch off."),
      ], { phase: "close", interval: "At close" }),
      op("skettle.clean", "Clean and sanitize the kettle", "clean", "maker", OO("EQ186", "EQ187", "EQ189"), [
        S("power", "Turn the power off."),
        S("trash", "Scrape out the food and flush it."),
        S("soap", "Scrub it with hot soapy water and a brush."),
        W("stop", "Never use steel wool or metal tools.", "Do NOT use abrasive materials or metal tools that might scratch the surface. Do NOT use steel wool (Groen)."),
        S("water", "Rinse it with hot water and drain it."),
        S("spray", "Sanitize it just before you use it.", { detail: "Groen: use a solution equivalent to one that supplies 200 parts per million available chlorine; do not leave sanitizer on the stainless." }),
      ], { phase: "close", interval: "After use; at least every 12 hours in continuous use (Groen)" }),
      op("skettle.valve", "Test the pressure relief valve", "maintain", "maker", OO("EQ192"), [
        S("check", "Run the kettle at 15 PSI."),
        S("check", "Hold the test ring at least five seconds."),
        W("stop", "Stop using the kettle if the valve fails.", "At least twice a month, test the pressure relief valve with the kettle operating at 15 PSI, by holding the test ring for at least five seconds (Groen). A kettle whose valve fails the test is not used until it is repaired."),
      ], { interval: "At least twice a month (Groen)" }),
    ],
  },

  /* ═════════════ CHURCH KITCHEN AND SERVING LINE ═════════════ */
  {
    id: "roaster-oven", name: "Roaster oven", category: "hot_holding", icon: "roaster", homes: true, business: true,
    aliases: ["roaster", "roaster oven", "nesco", "electric roaster", "turkey roaster"],
    ops: [
      op("roaster.preheat", "Preheat the roaster", "operate", "maker", OO("EQ127"), [
        S("power", "Turn the knob to MIN before you plug it in."),
        S("power", "Plug it in."),
        S("thermo", "Set the temperature."),
        S("wait", "Preheat 15 minutes with the lid on.", { timerSec: 900 }),
      ], { phase: "open" }),
      op("roaster.hold", "Keep food hot in the roaster", "operate", "law", OO("FC25", "FC26", "EQ128", "EQ130"), [
        S("thermo", "Heat cold food to 165°F within 2 hours.", { detail: "Food cooked and cooled, then reheated for hot holding: 165°F for 15 seconds, within 2 hours (Food Code 3-403.11). Hamilton Beach: at 400°F the roaster warms refrigerated soup to serving temperature in about 2 hours." }),
        S("thermo", "Check it stays 135°F or hotter.", { ask: { kind: "number", label: "Food temperature", unit: "°F" } }),
        S("food", "Stir it now and then."),
        W("gloves", "Use mitts. Steam can burn.", "Accessible surfaces can be hot when in use. Escaping steam may cause burns. Use hot mitts to protect hands when removing roasting pan (Hamilton Beach)."),
      ], { phase: "run" }),
      op("roaster.clean", "Clean the roaster", "clean", "maker", OO("EQ131", "EQ132", "EQ133"), [
        S("power", "Turn the knob to MIN and unplug it."),
        S("wait", "Let it cool."),
        S("soap", "Wash the lid, rack and pan in hot soapy water."),
        S("clean", "Soak burned food, then scrub with a nylon pad."),
        S("clean", "Wipe the base with a damp cloth."),
        W("stop", "Never put the base or cord in water.", "Disconnect power before cleaning. Do not immerse cord, plug, or base in any liquid (Hamilton Beach)."),
      ], { phase: "close", interval: "After each use" }),
    ],
  },
  {
    id: "chafing-dish", name: "Chafing dishes", category: "hot_holding", icon: "chafing-dish", homes: true, business: true,
    aliases: ["chafing dish", "chafing dishes", "chafer", "warming tray", "buffet warmer", "sterno"],
    ops: [
      op("chafer.setup", "Set up a chafing dish", "setup", "maker", OO("EQ134", "EQ137", "EQ140"), [
        S("water", "Fill the water pan with hot water about 1 inch deep."),
        S("check", "Check the chafer's manual for how many fuel cans."),
        S("box", "Put the fuel cans in the holders under it."),
        S("check", "Use a long match or lighter to light the cans."),
        W("stop", "Never light one can from another.", "NEVER use a lit can to light another. ALWAYS place the can securely underneath the chafing dish before lighting (Sterno)."),
        S("food", "Set the food pans in the water pan."),
      ], { phase: "open" }),
      op("chafer.hold", "Keep the food hot", "operate", "law", OO("FC25", "FC26", "FC61", "EQ134"), [
        S("food", "Put in food that is already hot."),
        S("thermo", "Check the food is 135°F or hotter.", { ask: { kind: "number", label: "Food temperature", unit: "°F" } }),
        S("water", "Add hot water to the pan when it runs low."),
        W("phone", "Tell the person in charge if food is under 135°F.", "Hot food is held at 135°F or above (Food Code 3-501.16). Holding by time instead needs written steps ahead of time; the food is marked with the time 4 hours after it left temperature control and served or thrown away by then (Food Code 3-501.19)."),
      ], { phase: "run", interval: "During service" }),
      op("chafer.fuel", "Handle chafing fuel safely", "operate", "maker", OO("EQ135", "EQ136", "EQ138"), [
        S("check", "Open fuel cans with a spoon, never a knife."),
        W("stop", "Never carry a chafer with lit cans.", "NEVER carry lit cans. NEVER carry chafing dishes or beverage urns with lit cans (Sterno)."),
        S("stop", "Keep napkins, cloths and hair away from the flame."),
        S("check", "Swap in new cans before the old ones burn out.", { detail: "Sterno burn times: Safe Heat 2, 4, 6 HR; Ethanol Gel 2 HR; Handy Wick 4, 5, 6 HR; Stem Wick 6 HR." }),
      ], { phase: "run" }),
      op("chafer.close", "Put out the fuel", "operate", "maker", OO("EQ139"), [
        S("check", "Put out each flame with its cap or a snuffer."),
        W("stop", "Never blow out the flame or use your hands.", "ALWAYS use a can cap, snuffer paddle, regulator or saucer to extinguish flame. NEVER use hands to extinguish flame (Sterno)."),
        S("wait", "Wait until the cans cool before you touch them."),
        S("hands", "Wash your hands after you touch fuel."),
      ], { phase: "close", interval: "At the end of service" }),
      op("chafer.clean", "Wash the chafing dishes", "clean", "law", OO("FC16", "FC22"), [
        S("wait", "Let the pans and water cool."),
        S("water", "Empty the water pan."),
        S("soap", "Wash, rinse and sanitize the food pans."),
        S("clean", "Wipe the frame and water pan clean."),
      ], { phase: "close", interval: "After each use" }),
    ],
  },
  {
    id: "rice-cooker", name: "Rice cooker", category: "cooking", icon: "rice-cooker", homes: true, business: true,
    aliases: ["rice cooker", "zojirushi", "aroma", "rice warmer"],
    ops: [
      op("rice.cook", "Cook rice", "operate", "maker", OO("EQ148", "EQ151"), [
        S("check", "Put in rice and water as the manual says."),
        S("power", "Close the lid and start it."),
        S("wait", "Wait for the beep. It keeps the rice warm."),
        S("food", "Stir the rice right away to loosen it."),
        W("stop", "Never touch the steam vent.", "Do not touch the steam vent. Doing so may cause burns or scalding (Zojirushi)."),
      ], { phase: "run" }),
      op("rice.warm", "Keep rice warm safely", "operate", "law", OO("FC25", "EQ149", "EQ150"), [
        S("thermo", "Check held rice is 135°F or hotter.", { ask: { kind: "number", label: "Rice temperature", unit: "°F" }, detail: "Hot food is held at 135°F or above (Food Code 3-501.16). Zojirushi's Extended Keep Warm holds rice at about 140° F." }),
        W("stop", "Never keep rice warm more than 12 hours.", "Do not keep rice warm for more than 12 hours. Do not keep rice which has already been cooled or add cold rice to warm rice (Zojirushi)."),
        S("stop", "Never add cold rice to warm rice."),
      ], { phase: "run" }),
      op("rice.clean", "Clean the rice cooker", "clean", "maker", OO("EQ152", "EQ153", "EQ154"), [
        S("power", "Press Reset and unplug it."),
        S("wait", "Let it cool."),
        S("soap", "Wash the inner pan with a soft sponge."),
        S("clean", "Wipe the body with a damp cloth."),
        W("stop", "Never put the cooker body in water.", "Do not clean with thinner, benzene, bleach, abrasive cleansers. Do not use brushes or abrasive cleansers on the inner pan (Zojirushi). Never immerse the cooker."),
      ], { phase: "close", interval: "After each use" }),
    ],
  },
  {
    id: "waffle-maker", name: "Waffle maker", category: "cooking", icon: "waffle", homes: true, business: true,
    aliases: ["waffle maker", "waffle iron", "belgian waffle"],
    ops: [
      op("waffle.use", "Make waffles", "operate", "maker", OO("EQ156", "EQ157", "EQ158"), [
        S("power", "Plug it in and set the dial."),
        S("wait", "Preheat with the lid closed until READY lights."),
        S("food", "Spray the grids with cooking spray."),
        S("food", "Pour in the batter and close the lid."),
        S("wait", "Cook 3 to 6 minutes, until steaming stops."),
        S("food", "Take the waffle out with a wood or plastic tool."),
        W("gloves", "Use an oven mitt to open it.", "Always use an oven mitt to protect hand when opening a hot waffle maker. Escaping steam can burn (Hamilton Beach)."),
      ], { phase: "run" }),
      op("waffle.clean", "Clean the waffle maker", "clean", "maker", OO("EQ159", "EQ160", "FC22"), [
        S("power", "Unplug it and let it cool."),
        S("clean", "Wipe the grids with a damp soapy cloth."),
        S("clean", "Wipe them again with a clean damp cloth."),
        W("stop", "Never use steel wool or scouring pads.", "Do not use steel wool, scouring pads, or abrasive cleansers. Do not immerse the cord, plug, or base in water or any other liquid (Hamilton Beach)."),
        S("stop", "Never put the base or cord in water."),
        S("box", "Let it cool fully before you put it away."),
      ], { phase: "close", interval: "After use; at least every 24 hours in a business (Food Code 4-602.12)" }),
    ],
  },

  /* ═════════════ CAFÉ SANDWICH LINE ═════════════ */
  {
    id: "panini-press", name: "Panini press", category: "cooking", icon: "panini", homes: false, business: true,
    aliases: ["panini press", "panini grill", "sandwich press", "contact grill", "clamshell grill"],
    ops: [
      op("panini.open", "Turn on and heat up", "operate", "maker", OO("EQ161", "EQ163"), [
        S("power", "Turn the grill on."),
        S("wait", "Wait about 30 minutes to heat up.", { timerSec: 1800 }),
        S("food", "Wipe on a light coat of salt-free release agent.", { detail: "Star: daily, a light coat of SALT-FREE release agent in the morning." }),
      ], { phase: "open", interval: "At opening" }),
      op("panini.season", "Season a new grill", "setup", "maker", OO("EQ162", "EQ163"), [
        S("power", "Heat the new grill about 45 minutes in an airy spot."),
        S("thermo", "Set it to 300°F."),
        S("food", "Wipe on salt-free release agent."),
        S("wait", "Wait 20 minutes.", { timerSec: 1200 }),
        S("towel", "Wipe it with a warm damp cloth."),
      ], { interval: "New grill, and after a carbon clean" }),
      op("panini.close", "Clean the grill at close", "clean", "maker", OO("EQ165", "EQ166", "EQ167", "FC22"), [
        S("clean", "Scrape stuck bits off with a spatula."),
        S("power", "Turn it off and unplug it."),
        S("clean", "Wipe it with a warm damp cloth and mild soap."),
        S("towel", "Dry it."),
        S("trash", "Empty and wash the grease drawer."),
        W("stop", "Never put ice or water on the plates.", "DO NOT IMMERSE OR LET THE UNIT STAND IN WATER. DO NOT USE ICE ON THE COOKING SURFACE! DO NOT USE SHARP OBJECTS TO REMOVE CARBON BUILD-UP (Star)."),
      ], { phase: "close", interval: "At the end of the day (Star); at least every 24 hours (Food Code 4-602.12)" }),
      op("panini.carbon", "Clean off carbon", "maintain", "maker", OO("EQ168", "EQ167"), [
        S("chemical", "Use a carbon remover safe for aluminum, as its label says."),
        S("food", "Do the seasoning steps again."),
        S("stop", "Never scrape carbon with sharp tools."),
      ], { interval: "When carbon builds up (Star)" }),
    ],
  },
  {
    id: "conveyor-toaster", name: "Conveyor toaster", category: "cooking", icon: "toaster", homes: false, business: true,
    aliases: ["conveyor toaster", "toast qwik", "bun toaster", "hatco toaster", "commercial toaster"],
    ops: [
      op("ctoast.open", "Turn on the toaster", "operate", "maker", OO("EQ169", "EQ171"), [
        S("check", "Check the crumb tray is in."),
        S("power", "Turn the power switch on."),
        S("check", "Set the color knob to medium."),
        S("wait", "Wait 15 to 20 minutes to warm up."),
      ], { phase: "open", interval: "At opening" }),
      op("ctoast.use", "Toast safely", "operate", "maker", OO("EQ171", "EQ172"), [
        W("stop", "Never toast buttered or coated bread in it.", "Toasting of coated or buttered products such as garlic bread, cheese sandwiches, and sweet rolls is NOT recommended. Residue could ignite inside the toaster (Hatco)."),
        W("stop", "Never put a knife or fork inside.", "Never insert a knife, fork, or similar object into toasting chamber (Hatco)."),
      ], { phase: "run" }),
      op("ctoast.close", "Turn off the toaster", "operate", "maker", OO("EQ173"), [
        S("power", "Turn the power switch off."),
      ], { phase: "close", interval: "At close" }),
      op("ctoast.clean", "Clean the toaster", "clean", "maker", OO("EQ174", "EQ176"), [
        S("power", "Turn it off, let it cool and unplug it."),
        S("box", "Take out the crumb tray once it is cool."),
        S("soap", "Wash the crumb and toast trays in soapy water."),
        S("water", "Rinse them and let them air-dry."),
        W("stop", "Never spray or soak the toaster.", "DO NOT submerge or saturate with water. Do not steam clean or use excessive water on unit. Use non-abrasive cleaners and cloths only (Hatco)."),
      ], { phase: "close", interval: "Daily (Hatco)" }),
      op("ctoast.filter", "Clean the fan filter", "maintain", "maker", OO("EQ177"), [
        S("box", "Find the fan filter under the toaster."),
        S("water", "Rinse it in hot water."),
        S("wait", "Let it dry fully, then put it back."),
      ], { interval: "Weekly (Hatco)" }),
    ],
  },
  /* ═════════════ DRINKS ═════════════ */
  {
    id: "soda-fountain", name: "Soda fountain", category: "cold", icon: "soda-fountain", homes: false, business: true, surfaceId: "beverage-nozzles",
    aliases: ["soda fountain", "soda machine", "fountain drinks", "bag in box", "drink dispenser", "pop machine", "cornelius", "lancer"],
    ops: [
      op("soda.open", "Check the soda fountain", "operate", "maker", OO("EQ6"), [
        S("check", "Check the drinks taste, smell and fizz right."),
        S("check", "Check the CO2 tank level."),
        S("write", "Check the dates on the syrup boxes."),
      ], { phase: "open", interval: "Daily (Cornelius)" }),
      op("soda.close", "Clean the nozzles at close", "clean", "law", OO("EQ1", "EQ2", "EQ3", "EQ4", "EQ5", "FC20"), [
        S("box", "Take off the nozzles and diffusers."),
        S("soap", "Wash them in warm soapy water and rinse."),
        S("spray", "Spray them with sanitizer inside and out.", { detail: "Cornelius: 2 packets (4 oz) of Stera Sheen Green Label in 2 gallons of 80 to 100 °F water. Beverage nozzles are cleaned as often as the maker says (Food Code 4-602.11(E))." }),
        S("wait", "Put them back and let them air-dry."),
        S("soap", "Wash the cup rest and wipe the outside."),
        S("water", "Pour warm soapy water down the drain."),
        S("stop", "Never use metal scrapers or rough pads on it."),
      ], { phase: "close", interval: "Daily (Cornelius)" }),
      op("soda.lines", "Sanitize the syrup lines", "maintain", "maker", OO("EQ7", "EQ8", "EQ9"), [
        S("people", "Let only trained staff do the syrup lines."),
        S("chemical", "Make about 5 gallons of sanitizer in a pail."),
        S("water", "Run the sanitizer through every valve."),
        S("wait", "Leave it in the lines 15 minutes.", { timerSec: 900 }),
        S("water", "Run the valves until only syrup comes out."),
      ], { interval: "Monthly (Cornelius)" }),
      op("soda.co2", "Change the CO2 tank safely", "maintain", "maker", OO("EQ10", "EQ11", "EQ12", "EQ13", "EQ14"), [
        W("stop", "Keep every CO2 tank chained so it can't fall.", "Always secure the CO2 cylinder with a safety chain to prevent it from falling over (Cornelius). Store cylinders upright with the valve protection cap in place; never put CO2 tanks inside the cooler or walk-in (Brewers Association)."),
        S("power", "Close the old tank's valve all the way."),
        S("wrench", "Turn the regulator nut slowly to let pressure out."),
        S("check", "Put a chain on the new tank. Open its valve slowly."),
        S("check", "Check the fittings for leaks."),
        W("stop", "Get out and air out the room if CO2 leaks.", "CO2 displaces oxygen. If a CO2 gas leak is suspected, particularly in a small area, IMMEDIATELY ventilate the contaminated area. High CO2 can cause loss of consciousness and DEATH (Cornelius)."),
      ], { interval: "When the gauge is in the change zone (Cornelius)" }),
    ],
  },
  {
    id: "iced-tea-brewer", name: "Iced tea brewer", category: "coffee", icon: "tea-brewer", homes: false, business: true, surfaceId: "beverage-nozzles",
    aliases: ["iced tea brewer", "iced tea", "tea brewer", "tea dispenser", "bunn tb3"],
    ops: [
      op("tea.open", "Turn on the tea brewer", "operate", "maker", OO("EQ17"), [
        S("power", "Turn the brewer on."),
        S("wait", "Wait about 20 minutes for the water to heat.", { timerSec: 1200 }),
      ], { phase: "open", interval: "At opening" }),
      op("tea.brew", "Brew iced tea", "operate", "maker", OO("EQ18", "EQ19"), [
        S("check", "Start with a clean, empty funnel and dispenser."),
        S("food", "Put a filter and 3 to 5 ounces of tea in."),
        S("power", "Start the brew."),
        W("stop", "Take the funnel out slowly after it drips.", "CAUTION - The funnel contains hot liquids. Remove funnel slowly (BUNN)."),
        S("power", "Turn the switch off so it can't start by mistake."),
      ], { phase: "run" }),
      op("tea.close", "Clean the tea brewer at close", "clean", "law", OO("FC21", "EQ16", "EQ20", "EQ21", "EQ22"), [
        W("stop", "Never keep brewed iced tea overnight.", "CAUTION - DO NOT KEEP BREWED ICED TEA OVERNIGHT. THE SERVER MUST BE CLEANED DAILY (BUNN). Iced tea dispensers are cleaned at least every 24 hours (Food Code 4-602.11(E)(2))."),
        S("trash", "Empty the tea dispenser."),
        S("soap", "Wash, rinse and sanitize the dispenser."),
        S("soap", "Take apart, wash and sanitize the brew funnel."),
        S("water", "Turn the switch off and rinse the sprayhead."),
        S("clean", "Push the deliming spring in and out 5 or 6 times."),
        S("clean", "Wipe the outside with a damp cloth."),
      ], { phase: "close", interval: "Daily (BUNN); at least every 24 hours (Food Code 4-602.11)" }),
      op("tea.sweet", "Flush the sweetener line", "maintain", "maker", OO("EQ23"), [
        S("water", "Put the sweetener line in a bucket of 120 to 130°F water."),
        S("power", "Run three brew cycles in a row."),
      ], { interval: "Weekly, on sweetener models (BUNN)" }),
    ],
  },
  {
    id: "coffee-urn", name: "Coffee urn", category: "coffee", icon: "urn", homes: false, business: true,
    aliases: ["coffee urn", "urn", "percolator", "west bend", "100 cup coffee maker", "hot water urn"],
    ops: [
      op("urn.brew", "Make coffee in the urn", "operate", "maker", OO("EQ24", "EQ26", "EQ27"), [
        S("water", "Fill it with water for 40 to 100 cups."),
        S("food", "Put 6¼ cups of ground coffee in the basket for 100 cups."),
        S("power", "Put the lid on and turn it on."),
        S("wait", "Wait for the light in the base to glow."),
        S("gloves", "Take out the basket and stem with a hot pad."),
        W("stop", "Never take the lid off while it brews.", "No part of the appliance should be removed during the brewing cycle as scalding could occur. Scalding may occur if the lid is removed during the brewing cycle (West Bend)."),
      ], { phase: "open", params: [{ key: "coffee", label: "Ground coffee for 100 cups", unit: "cups", default: "6¼ (West Bend 100-cup urn)", defaultSource: "object-operations#EQ26" }] }),
      op("urn.close", "Turn off the urn", "operate", "maker", OO("EQ25", "EQ28"), [
        S("power", "Turn it off and unplug it when about 3 cups are left."),
        W("stop", "Keep liquid over the bottom while it is on.", "Liquid must always cover the inside bottom of the appliance at all times (West Bend)."),
        S("wait", "Let it cool."),
      ], { phase: "close" }),
      op("urn.clean", "Clean the urn", "clean", "maker", OO("EQ25", "EQ29", "EQ30"), [
        S("power", "Unplug it and let it cool."),
        S("water", "Drain out the coffee."),
        S("soap", "Wash the basket, stem and lid in warm soapy water."),
        S("soap", "Fill it one-third with warm water and dish soap."),
        S("clean", "Scrub the heating well inside."),
        W("stop", "Never use baking soda or metal pads.", "Do not use abrasive metal scouring pads or cleansers on the outside. DO NOT USE BAKING SODA TO CLEAN THE COFFEEMAKER BECAUSE IT CAN DISCOLOR AND PIT THE METAL (West Bend)."),
      ], { phase: "close", interval: "After each use (West Bend)" }),
      op("urn.delime", "Delime the urn", "maintain", "maker", OO("EQ31", "EQ32"), [
        S("chemical", "Fill it with 1 quart water and 1 quart white vinegar."),
        S("power", "Run a full brew cycle."),
        S("wait", "Let it stand 20 minutes.", { timerSec: 1200 }),
        S("clean", "Clean the faucet and gauge tube with a small brush."),
      ], { interval: "Every 2 weeks if used daily; every week if used for hot water (West Bend)" }),
    ],
  },
  {
    id: "syrup-pumps", name: "Syrup pumps", category: "coffee", icon: "syrup-pump", homes: false, business: true, surfaceId: "syrup-pumps",
    aliases: ["syrup pump", "syrup pumps", "monin", "torani", "flavor syrup", "sauce pump"],
    ops: [
      op("syrup.open", "Uncover the syrup pumps", "operate", "maker", OO("EQ33", "EQ34"), [
        S("box", "Take the covers off the pump tips."),
        S("check", "Check each pump is on its own flavor."),
      ], { phase: "open", interval: "At opening" }),
      op("syrup.close", "Cover the syrup pumps", "operate", "maker", OO("EQ33"), [
        S("box", "Cover each pump tip with its cap or plastic wrap.", { detail: "Monin suggests pump openings are covered to protect the product from insects, fermentation and mold." }),
      ], { phase: "close", interval: "At close" }),
      op("syrup.clean", "Keep the pumps clean", "clean", "maker", OO("EQ34", "FC20", "FC33"), [
        S("check", "Keep each pump on the same flavor."),
        S("clean", "Wipe sticky drips off the pumps and bottles."),
        W("stop", "Dry a washed pump fully before it goes back.", "Monin does not suggest washing pumps, as this could contaminate the product with detergent or water if the pumps are not completely dried. Syrup dispensing parts are cleaned as often as the maker says (Food Code 4-602.11(E))."),
      ], { phase: "close" }),
      op("syrup.date", "Date the open syrups", "maintain", "maker", OO("EQ35"), [
        S("write", "Write the open date on each bottle."),
        S("check", "Keep syrups in a dry spot at 65 to 80°F."),
        S("trash", "Use it within 30 to 60 days of opening."),
      ]),
    ],
  },
  {
    id: "whipped-cream-dispenser", name: "Whipped cream dispenser", category: "coffee", icon: "whipped-cream", homes: false, business: true,
    aliases: ["whipped cream", "whipper", "isi", "cream whipper", "nitrous charger", "whip cream can"],
    ops: [
      op("whip.fill", "Fill and charge the whipper", "setup", "maker", OO("EQ38", "EQ39", "EQ40", "EQ42", "FC25"), [
        S("fridge", "Pour cold cream in, below the fill line."),
        S("check", "Put the head on straight and tight."),
        W("stop", "Put on one charger with your face turned away.", "Only use original iSi cream chargers. Keep face away from the whipper; do not bend over the whipper. Never use more chargers than specified: 1 charger for 0.5 L (1 US pint), 2 for 1 L (1 US quart) (iSi)."),
        S("check", "Shake it hard 6 times."),
        S("fridge", "Keep it in the fridge at 41°F or colder.", { detail: "Cold food is held at 41°F or colder (Food Code 3-501.16). iSi: do not store in a freezer." }),
      ], { phase: "open" }),
      op("whip.run", "Use the whipper safely", "operate", "maker", OO("EQ38", "EQ43"), [
        S("clean", "Clean the tip every 1 to 2 hours."),
        W("stop", "Never unscrew the head while it has pressure.", "The whipper is a pressure equipment. Never unscrew the head when the whipper is pressurized! If it is dropped or damaged (e.g., cracked or dented), do not use again (iSi)."),
        S("stop", "Stop using a whipper that was dropped or dented."),
      ], { phase: "run" }),
      op("whip.clean", "Clean the whipper at close", "clean", "maker", OO("EQ44", "EQ45"), [
        S("check", "Press the lever until it is empty with no pressure."),
        S("box", "Take off the head, tip and head gasket."),
        S("soap", "Wash the parts in warm soapy water and brush the valve."),
        S("stop", "Never use scouring pads or rough sponges."),
        S("wait", "Let the parts air-dry apart."),
        S("box", "Store it apart, never with the head on."),
      ], { phase: "close", interval: "At close" }),
    ],
  },
  {
    id: "milk-frother", name: "Milk frother", category: "coffee", icon: "frother", homes: true, business: true,
    aliases: ["milk frother", "frother", "aeroccino", "milk cafe", "autofoamer", "foamer"],
    ops: [
      op("frother.open", "Set up the frother", "operate", "maker", OO("EQ49"), [
        S("check", "Check the jug and disc are clean."),
        S("box", "Put the jug on the base."),
      ], { phase: "open" }),
      op("frother.froth", "Froth milk", "operate", "maker", OO("EQ46", "EQ47", "EQ48"), [
        S("fridge", "Pour cold milk between the MIN and MAX lines."),
        W("stop", "Never run it empty.", "Do not operate the appliance without liquid. Always fill to at least the MIN marking and no more than the MAX marking (Breville)."),
        S("wait", "Start it and wait 2 to 7 minutes."),
        W("gloves", "Lift the jug off carefully. It is hot.", "Carefully remove the milk jug from the power base as the milk jug and contents will be hot; the jug and hot milk may scald (Breville)."),
        S("wait", "Let the jug cool about 5 minutes before the next batch."),
      ], { phase: "run" }),
      op("frother.clean", "Clean the frother", "clean", "maker", OO("EQ49", "EQ50"), [
        S("power", "Unplug it and let it cool."),
        S("soap", "Wash the jug, lid and disc in warm soapy water."),
        S("clean", "Wipe the base with a damp cloth."),
        W("stop", "Never put the base in water.", "Never immerse the power base in water. Do not use alkaline or abrasive cleaning agents, steel wool or scouring pads (Breville)."),
      ], { phase: "close", interval: "Right after each use (Breville)" }),
    ],
  },
  {
    id: "keg-tap", name: "Draft and nitro taps", category: "cold", icon: "tap", homes: false, business: true,
    aliases: ["draft tap", "draught", "beer tap", "keg", "kegerator", "nitro cold brew", "nitro tap", "beer lines", "beer cooler"],
    ops: [
      op("keg.check", "Check the kegs", "operate", "maker", OO("EQ59", "EQ65"), [
        S("thermo", "Check kegs are kept at 34 to 38°F."),
        S("thermo", "Check a nitro cold brew pour is 35 to 45°F.", { ask: { kind: "number", label: "Nitro drink temperature", unit: "°F" } }),
      ], { phase: "open", interval: "At opening" }),
      op("keg.close", "Rinse the faucets at close", "clean", "maker", OO("EQ56"), [
        S("water", "Rinse the faucets and drip trays with fresh water."),
        S("wait", "Let the faucets air-dry."),
      ], { phase: "close", interval: "At the close of business each day (Brewers Association)" }),
      op("keg.lines", "Clean the beer lines", "clean", "maker", OO("EQ51", "EQ53", "EQ54", "EQ55", "EQ57"), [
        S("people", "Let only trained staff clean the lines."),
        W("gloves", "Put on rubber gloves and eye protection.", "Rubber gloves and eye protection should be used whenever handling line-cleaning chemicals; always add the chemical to water and never add water to the chemical (Brewers Association)."),
        S("chemical", "Mix caustic line cleaner as its label says.", { detail: "At least 2% caustic (3% for problem systems), water between 80°F and 110°F (Brewers Association)." }),
        W("stop", "Never use chlorine to clean beer lines.", "Never use solutions that contain any amount of chlorine for regular system maintenance. Caustic and acid should always be separate and never come into contact with each other (Brewers Association)."),
        S("wait", "Leave it in the lines 15 to 20 minutes.", { detail: "At least 15 minutes when the solution is recirculated, 20 minutes when static (Brewers Association)." }),
        S("clean", "Take apart and clean every faucet."),
        S("clean", "Scrub the keg couplers."),
      ], { interval: "Every two weeks (14 days) (Brewers Association)" }),
      op("keg.acid", "De-stone the beer lines", "maintain", "maker", OO("EQ52", "EQ54"), [
        S("chemical", "Clean the lines with an acid line cleaner."),
        S("stop", "Keep acid and caustic cleaners apart."),
        S("clean", "Take apart and clean the couplers every six months."),
      ], { interval: "Quarterly, every three months; couplers every six months (Brewers Association)" }),
      op("keg.nitro", "Clean the nitro cold brew lines", "clean", "maker", OO("EQ60", "EQ61", "EQ62", "EQ63", "EQ64"), [
        S("soap", "Wash the nozzles and mixing chambers with mild soap."),
        W("stop", "Never soak nozzles in sanitizer over 2 minutes.", "DO NOT wash nozzles or mixing chambers in a dish washer. Do not soak them in sanitizing solution longer than 2 minutes (Cornelius)."),
        S("chemical", "Soak the lines in cleaner for 5 minutes.", { timerSec: 300 }),
        S("chemical", "Soak the lines in sanitizer for 20 minutes.", { timerSec: 1200, detail: "Cornelius: one 1 oz. packet of Kay-5 in 0.75 gallons of 75-95°F tap water gives 330 PPM available chlorine." }),
      ], { interval: "Weekly, or as local code requires (Cornelius)" }),
      op("keg.co2", "Keep the gas tanks safe", "operate", "maker", OO("EQ14", "EQ58", "EQ11"), [
        W("stop", "Keep every gas tank upright and chained.", "Cylinders should be stored upright, with valve protection cap in place, and firmly secured; cylinder temperatures should not exceed 125°F (Brewers Association). Always secure the CO2 cylinder with a safety chain (Cornelius)."),
        W("stop", "Never keep CO2 tanks in the walk-in.", "CO2 tanks should never be located in the cooler. Breathing high concentrations of CO2 can be deadly; CO2 warning alarms are recommended for walk-in coolers (Brewers Association)."),
      ], { phase: "open" }),
    ],
  },
  {
    id: "water-filter-system", name: "Water filter system", category: "other", icon: "water-filter", homes: false, business: true,
    aliases: ["water filter", "water filtration", "everpure", "filter cartridge", "coffee water filter", "ice machine filter"],
    ops: [
      op("wfilter.check", "Check the water filter", "maintain", "maker", OO("EQ66", "EQ67"), [
        S("check", "Read the pressure gauge on the filter."),
        S("wrench", "Change the cartridge if the needle is in the red."),
        S("write", "Change it every 6 months or at its gallon rating."),
        S("write", "Change the prefilter every three months."),
      ], { interval: "Every 6 months, the label's gallon rating, or a red gauge, whichever is first (Everpure)" }),
      op("wfilter.change", "Change the filter cartridge", "maintain", "maker", OO("EQ68", "EQ69"), [
        S("water", "Turn off the water to the filter."),
        S("water", "Open the flush valve to let out pressure."),
        S("wrench", "Turn the old cartridge a quarter turn left and pull it down."),
        S("wrench", "Push the new one up and turn it a quarter turn right."),
        S("water", "Flush it to the drain for the time on its label."),
        S("check", "Shut the flush valve."),
      ]),
      op("wfilter.boil", "After a boil water order", "maintain", "maker", OO("EQ70"), [
        S("phone", "Call your filter service to sanitize the system."),
        S("wait", "Leave the sanitizer in at least 30 minutes."),
        S("wrench", "Put in new filters after."),
      ], { interval: "After a boil water order (Everpure)", service: { who: "Your water filter service" } }),
    ],
  },
  /* ═════════════ RESTAURANT LINE ═════════════ */
  {
    id: "charbroiler", name: "Charbroiler", category: "cooking", icon: "charbroiler", homes: false, business: true, surfaceId: "grill-grates",
    aliases: ["charbroiler", "char broiler", "grill", "broiler grates", "radiant broiler", "vulcan charbroiler", "star charbroiler"],
    ops: [
      op("cb.light", "Light and preheat", "operate", "maker", OO("EQ201", "EQ202", "EQ203", "EQ210"), [
        S("check", "Check your charbroiler's manual for its lighting steps."),
        S("power", "Turn all gas valves off and wait 5 minutes.", { timerSec: 300 }),
        S("power", "Turn the main gas on and light the pilots."),
        W("stop", "Turn everything off if a burner won't light.", "If the burners fail to light, turn off all gas valves and call a service agency (Vulcan). Wait 5 minutes to clear gas before lighting again (Star)."),
        S("wait", "Preheat it for 30 minutes.", { timerSec: 1800 }),
        S("food", "Wipe the grates with cooking oil."),
      ], { phase: "open", interval: "At opening" }),
      op("cb.run", "Broil safely", "operate", "maker", OO("EQ200", "EQ204", "EQ205", "EQ206", "EQ209", "EQ212"), [
        S("water", "Keep water in the water pan, if it has one.", { detail: "Star radiant models: 1/4\" of water in the water pan, added as necessary; it helps prevent flare ups and catches grease. Vulcan VCCB models have crumb trays instead." }),
        S("clean", "Scrape the grates with a wire brush as you cook."),
        W("stop", "Never move the grates while food cooks.", "NEVER ATTEMPT TO CHANGE THE GRATE POSITION WHILE FOOD PRODUCTS ARE COOKING. FLARE UPS CAN OCCUR UNEXPECTEDLY (Star)."),
        W("stop", "Never cover it with pans to burn off food.", "Never cover surface of unit with pans or other objects in attempt to burn off or clean debris from unit (Vulcan)."),
        W("phone", "Shut the main gas off if you smell gas.", "IN THE EVENT A GAS ODOR IS DETECTED, SHUT DOWN UNITS AT MAIN SHUTOFF VALVE AND CONTACT THE LOCAL GAS COMPANY OR GAS SUPPLIER FOR SERVICE (Vulcan)."),
      ], { phase: "run" }),
      op("cb.close", "Shut down at close", "operate", "maker", OO("EQ208", "EQ215"), [
        S("power", "Turn every burner valve off."),
        S("power", "Turn the main gas valve off."),
      ], { phase: "close", interval: "At close" }),
      op("cb.clean", "Clean the charbroiler", "clean", "maker", OO("EQ204", "EQ205", "EQ213", "FC22"), [
        S("wait", "Let it cool."),
        S("trash", "Empty the crumb trays or water pan."),
        S("soap", "Soak the grates overnight in grill cleaner."),
        S("water", "Rinse them hot in the morning, then dry and oil them."),
        S("clean", "Wipe the outside with detergent and a cloth."),
      ], { phase: "close", interval: "Daily (Vulcan); at least every 24 hours (Food Code 4-602.12)" }),
      op("cb.hood", "Get the hood checked", "maintain", "maker", OO("EQ207"), [
        S("phone", "Get the exhaust hood checked and cleaned."),
        S("phone", "Call a trained tech if a gas valve sticks."),
      ], { interval: "At least twice a year (Vulcan)" }),
    ],
  },
  {
    id: "salamander", name: "Salamander broiler", category: "cooking", icon: "salamander", homes: false, business: true,
    aliases: ["salamander", "cheesemelter", "cheese melter", "overhead broiler", "upright broiler"],
    ops: [
      op("sal.light", "Light the salamander", "operate", "maker", OO("EQ216"), [
        S("power", "Turn the burner valve off and wait 5 minutes.", { timerSec: 300 }),
        S("power", "Turn on and light the pilots."),
        S("power", "Turn the burners on."),
        W("stop", "Turn it off and wait 5 minutes if it won't light.", "If the burners fail to light, turn off all valves, wait 5 minutes, and repeat the lighting steps (Vulcan)."),
      ], { phase: "open", interval: "At opening" }),
      op("sal.run", "Broil safely", "operate", "maker", OO("EQ217", "EQ218"), [
        S("check", "Set the rack lower for less heat.", { detail: "Vulcan: 2 burners on, rack at highest position, valves on High – 800°F; 1 burner on, rack at lowest position – 250°F. Infrared models must use the full gas setting." }),
        W("trash", "Empty the drip tray when it is three-quarters full.", "DO NOT allow the drip tray to overflow. Empty the drip tray when three-quarters full to reduce the possibility of spillage (Vulcan)."),
      ], { phase: "run" }),
      op("sal.close", "Shut down at close", "operate", "maker", OO("EQ220"), [
        S("power", "Turn the burner valves off."),
      ], { phase: "close", interval: "At close" }),
      op("sal.clean", "Clean the salamander", "clean", "maker", OO("EQ219", "FC22"), [
        S("wait", "Let it cool."),
        S("box", "Take out the rack and drip tray."),
        S("soap", "Wash the drip tray and bottom pan with soap and water."),
        S("clean", "Wipe the steel with mild detergent."),
      ], { phase: "close", interval: "At least every 24 hours (Food Code 4-602.12)" }),
      op("sal.flue", "Check the flue", "maintain", "maker", OO("EQ220"), [
        S("wait", "Let the broiler get cool."),
        S("check", "Check the flue and clear anything blocking it."),
      ], { interval: "Annually (Vulcan)" }),
    ],
  },
  {
    id: "combi-oven", name: "Combi oven", category: "cooking", icon: "oven", homes: false, business: true, surfaceId: "oven",
    aliases: ["combi oven", "combi", "rational", "icombi", "combination oven", "steam oven"],
    ops: [
      op("combi.on", "Turn on the combi oven", "operate", "maker", OO("EQ221"), [
        S("power", "Press and hold the power button until it glows green."),
      ], { phase: "open", interval: "At opening" }),
      op("combi.safe", "Cook safely in the combi", "operate", "maker", OO("EQ222"), [
        W("stop", "Keep the door shut if a fire starts inside.", "If a fire breaks out, leave the cooking cabinet door closed in order to cut off the fire's oxygen supply. Clean the unit daily: grease left inside is a fire hazard (Rational)."),
        S("gloves", "Use oven mitts to move hot pans."),
      ], { phase: "run" }),
      op("combi.clean", "Run the automatic clean", "clean", "maker", OO("EQ222", "EQ223", "EQ224", "EQ225", "EQ226", "EQ228"), [
        S("box", "Take all the pans out."),
        S("check", "Pick the cleaning program on the screen."),
        W("gloves", "Wear safety glasses and chemical gloves.", "Wear safety eyewear and chemical-resistant gloves. Cleaner on skin: rinse with running water for 10 minutes; in the eyes: 15 minutes (Rational)."),
        S("chemical", "Put in the cleaner tabs where the screen shows."),
        W("stop", "Keep the door shut while it cleans.", "Keep the cooking cabinet door closed during automatic cleaning. Otherwise, hot steam and corrosive cleaning chemicals could escape (Rational)."),
        S("stop", "Never use red cleaners or oven cleaner in it."),
        S("wait", "Wait for the tone when it is done."),
      ], { phase: "close", interval: "Daily (Rational)" }),
      op("combi.wipe", "Wipe the door and seal", "clean", "maker", OO("EQ229", "EQ230", "EQ231"), [
        S("power", "Turn the power off to clean by hand."),
        S("clean", "Wipe the door, door seal and drip pan."),
        S("clean", "Wipe the core probe."),
        S("soap", "Use lukewarm water, mild soap and a soft cloth."),
        S("stop", "Never use scouring pads or grill cleaner."),
      ], { phase: "close", interval: "Daily (Rational)" }),
      op("combi.off", "Turn off the combi oven", "operate", "maker", OO("EQ221"), [
        S("power", "Press and hold the power button until it glows orange."),
        S("check", "Tap to confirm on the screen."),
      ], { phase: "close", interval: "At close" }),
      op("combi.descale", "Descale the steam nozzle", "maintain", "maker", OO("EQ229", "EQ232"), [
        S("power", "Turn the oven off."),
        S("wrench", "Take out the steam nozzle with the hex key."),
        W("gloves", "Wear a mask and chemical gloves.", "Wear a mask and chemical protective gloves for reliable protection when handling descaler (Rational)."),
        S("chemical", "Soak the nozzle in descaler until the scale is gone."),
        S("water", "Rinse it, dry it and put it back."),
      ], { interval: "Monthly (Rational)" }),
    ],
  },

  /* ═════════════ PREP ═════════════ */
  {
    id: "prep-table", name: "Refrigerated prep table", category: "cold", icon: "prep-table", homes: false, business: true,
    aliases: ["prep table", "sandwich prep table", "pizza prep table", "salad prep", "prep fridge", "make table", "sandwich unit", "tssu"],
    ops: [
      coldCheck("prep.temp", "prep table", "41"),
      // The rail (the pans on top) holds food out of the cabinet's air: its own check, of the food (room walk 2026-10-11).
      op("prep.rail", "Check the food in the rail", "operate", "law", OO("FC25", "FC37"), [
        S("thermo", "Check the food in each rail pan with a probe thermometer.", { ask: { kind: "number", label: "Food temperature", unit: "°F" } }),
        S("check", "Check it reads 41°F or colder."),
        W("phone", "Tell the person in charge if it is too warm.", TOO_WARM),
      ], { phase: "open", interval: "At opening and during the day" }),
      op("prep.new", "Start a new prep table", "setup", "maker", OO("EQ255", "EQ256"), [
        S("box", "Put in all the pans and baffles first."),
        S("wait", "Run it empty for 24 hours."),
        W("stop", "Never set it above 41°F for cold food.", "Appliance is not for the storage and/or display of potentially hazardous foods when the temperature control is set above 41˚F (True). Cold food is held at 41°F or colder (Food Code 3-501.16)."),
      ], { interval: "When new or moved" }),
      op("prep.load", "Load the prep table", "operate", "maker", OO("EQ257", "EQ258"), [
        S("box", "Keep food 4 inches from the cold air housing."),
        S("stop", "Don't move the baffles."),
      ], { phase: "run" }),
      op("prep.close", "Clean the prep table at close", "clean", "maker", OO("EQ259", "FC16"), [
        S("box", "Take out the rail pans."),
        S("soap", "Wash, rinse and sanitize the pans."),
        S("soap", "Clean the gaskets with warm soapy water."),
        S("clean", "Wipe the inside with baking soda and water."),
        W("stop", "Never hose it or use steel wool on it.", "DO NOT clean appliance with a pressure washer or hose. No steel wool or chlorine products on stainless (True)."),
      ], { phase: "close", interval: "At close" }),
      op("prep.coil", "Clean the condenser coil", "maintain", "maker", OO("EQ260"), [
        S("power", "Unplug it."),
        S("clean", "Clean the dust off the condenser coil with a brush."),
        S("check", "Use a flashlight to check it is clear."),
      ], { interval: "Check monthly (True)" }),
    ],
  },
  {
    id: "food-processor", name: "Food processor", category: "cooking", icon: "food-processor", homes: true, business: true,
    aliases: ["food processor", "robot coupe", "cuisinart", "chopper", "cutter mixer"],
    ops: [
      op("fp.use", "Use the food processor safely", "operate", "maker", OO("EQ233"), [
        W("stop", "Never get around the lid lock.", "Never try to remove the locking and safety systems (Robot Coupe)."),
        S("check", "Put the lid on before you start it."),
      ], { phase: "run" }),
      op("fp.clean", "Clean the food processor", "clean", "law", OO("FC19", "FC16", "FC33"), [
        S("power", "Unplug it."),
        S("box", "Take off the lid, blade and bowl."),
        S("soap", "Wash, rinse and sanitize them."),
        S("clean", "Wipe the motor base with a damp cloth."),
        S("wait", "Let the parts air-dry."),
      ], { phase: "close", interval: "At least every 4 hours in use, and at close (Food Code 4-602.11)" }),
      op("fp.seal", "Check the lid seal", "maintain", "maker", OO("EQ234"), [
        S("check", "Check the lid seal for cracks."),
        S("wrench", "Replace a worn seal."),
      ], { interval: "Every 6 months (Robot Coupe)" }),
    ],
  },
  {
    id: "immersion-blender", name: "Immersion blender", category: "cooking", icon: "immersion-blender", homes: true, business: true,
    aliases: ["immersion blender", "stick blender", "hand blender", "boat motor", "robot coupe mp"],
    ops: [
      op("ib.use", "Blend safely", "operate", "maker", OO("EQ235", "EQ238"), [
        W("stop", "Run it only with the blade fully in the food.", "Do not use the mixer unless the blade or whisk is fully immersed (Robot Coupe)."),
        W("stop", "Never use it if the motor fell in liquid.", "Do not use the appliance after the motor unit has been immersed in liquid (Robot Coupe)."),
      ], { phase: "run" }),
      op("ib.rinse", "Rinse after each use", "clean", "maker", OO("EQ236"), [
        S("water", "Run the blade in very hot water for 30 seconds.", { timerSec: 30, detail: "Robot Coupe: water at 70°C or hotter, 30 sec." }),
        S("power", "Unplug it."),
      ], { phase: "run", interval: "After each use (Robot Coupe)" }),
      op("ib.clean", "Deep clean at day's end", "clean", "maker", OO("EQ237", "FC16"), [
        S("water", "Run the blade in very hot water for 30 seconds.", { timerSec: 30 }),
        S("power", "Unplug it."),
        S("box", "Take the foot off the motor."),
        S("soap", "Soak the parts at least 5 minutes.", { timerSec: 300 }),
        S("spray", "Sanitize the foot and blade."),
        S("check", "Check the seal. Replace it if broken."),
        S("stop", "Never dip the motor in water."),
      ], { phase: "close", interval: "At the end of the day (Robot Coupe)" }),
      op("ib.parts", "Replace worn parts", "maintain", "maker", OO("EQ239"), [
        S("wrench", "Replace the bearing, blade and seal when due."),
      ], { interval: "Every 400 hours or 1 year, or sooner (Robot Coupe)" }),
    ],
  },
  {
    id: "floor-mixer", name: "Floor mixer", category: "cooking", icon: "mixer", homes: false, business: true,
    aliases: ["floor mixer", "hobart mixer", "legacy mixer", "dough mixer", "planetary mixer", "20 quart mixer"],
    ops: [
      op("fmixer.use", "Mix safely", "operate", "osha", OO("EQ249", "EQ250", "EQ240", "EQ241", "EQ242", "EQ244"), [
        W("people", "Let only workers 18 and older use it.", "Workers under 18 may not operate, assist to operate, set up, adjust, repair, oil or clean power-driven dough or batter mixers (U.S. Department of Labor bakery machine rule). Small countertop mixers that are not hardwired, 1/2 horsepower or less, with a bowl of five quarts or less, are the exception."),
        W("stop", "Never mix with the bowl guard open.", "Do not use the mixer without the interlocked bowl wire cage in place. The wire cage must be closed to the front-center position or the mixer will not operate (Hobart)."),
        S("check", "Start floury mixes on the lowest speed."),
        S("stop", "Stop the mixer before you add more."),
        W("stop", "Use the STOP button to stop it.", "Use the STOP button to stop the mixer. Never open the wire cage or use the bowl lift to stop the mixer (Hobart)."),
        S("stop", "Don't wear loose clothes near it."),
      ], { phase: "run" }),
      op("fmixer.clean", "Clean the mixer", "clean", "maker", OO("EQ245", "EQ246", "FC16"), [
        S("power", "Unplug it or lock out the power."),
        S("clean", "Wipe the mixer with a clean damp cloth."),
        W("stop", "Never hose down the mixer.", "DO NOT use a hose to clean the mixer; it should be washed with a clean, damp cloth. No scouring powder or pads. No aluminum agitators in the dishwasher (Hobart)."),
        S("soap", "Wash the bowl guard in the sink, rinse and dry it."),
        S("soap", "Wash, rinse and sanitize the bowl and beater."),
      ], { phase: "close", interval: "Daily (Hobart)" }),
      op("fmixer.service", "Get the mixer serviced", "maintain", "maker", OO("EQ247", "EQ248"), [
        S("phone", "Get it serviced twice a year."),
        S("wrench", "Oil the slideways about twice a year."),
      ], { interval: "At least twice a year (Hobart)" }),
    ],
  },
  {
    id: "sous-vide", name: "Sous vide cooker", category: "cooking", icon: "sous-vide", homes: true, business: true,
    aliases: ["sous vide", "immersion circulator", "anova", "joule", "water bath"],
    ops: [
      op("sv.use", "Cook sous vide", "operate", "maker", OO("EQ251", "FC62"), [
        S("water", "Fill water between the min and max lines."),
        S("power", "Plug it in and set the temperature."),
        W("stop", "Ask before you bag and chill food for later.", "A food business that packages food that must stay cold using reduced oxygen packaging (vacuum bags) needs a HACCP plan or a variance (Food Code 3-502.12). Cooking and serving right away does not."),
      ], { phase: "run" }),
      op("sv.clean", "Clean the sous vide cooker", "clean", "maker", OO("EQ252", "EQ254"), [
        S("power", "Unplug it."),
        S("soap", "Wash the skirt and end cap with dish soap."),
        S("clean", "Scrub the inside parts with a toothbrush and soap."),
        S("stop", "Don't bend the shaft inside."),
      ], { interval: "After use" }),
      op("sv.descale", "Descale the cooker", "maintain", "maker", OO("EQ253"), [
        S("chemical", "Mix equal parts water and white vinegar in a pot."),
        S("thermo", "Heat it to 140°F with the cooker."),
        S("wait", "Let it cool, then pour it out."),
      ], { interval: "When minerals build up (Anova)" }),
    ],
  },
  {
    id: "display-case", name: "Pastry display case", category: "cold", icon: "display-case", homes: false, business: true, surfaceId: "pastry-display-case",
    aliases: ["display case", "pastry case", "bakery case", "deli case", "grab and go case", "curved glass case"],
    ops: [
      coldCheck("dcase.temp", "display case", "41"),
      op("dcase.load", "Stock the case", "operate", "law", OO("FC60", "EQ270"), [
        S("food", "Keep food behind the glass, covered or wrapped."),
        S("box", "Keep food 4 inches from the cold air housing."),
      ], { phase: "open" }),
      fromTask("dcase.time", "fourhour", "operate", "law", { phase: "run" }),
      op("dcase.clean", "Clean the case", "clean", "maker", OO("EQ272", "FC16"), [
        S("power", "Unplug the case."),
        S("box", "Take out the food and trays."),
        S("soap", "Wash, rinse and sanitize the trays."),
        S("clean", "Wipe the inside with mild soap and water."),
        S("stop", "Never use steel wool or chlorine on the steel."),
      ], { phase: "close" }),
      op("dcase.coil", "Clean the condenser coil", "maintain", "maker", OO("EQ271", "EQ270"), [
        S("power", "Unplug the case."),
        S("clean", "Clean the condenser coil with a brush or vacuum."),
        S("wait", "Wait 5 minutes before you turn it back on."),
      ], { interval: "Every 30 days or as needed (True)" }),
    ],
  },
  {
    // Room walk kinds (2026-10-11): a case that keeps food hot. Food Code only: the hot-holding limit (FC25), its thermometer
    // in the coolest part (FC28), a food thermometer (FC37), reheating before it goes in (FC26), protected on display (FC60).
    id: "heated-display-case", name: "Heated display case", category: "hot_holding", icon: "warmer", homes: false, business: true,
    aliases: ["heated case", "heated display case", "hot case", "warming case", "heated merchandiser", "hot display"],
    ops: [
      op("hcase.temp", "Check the temperature", "operate", "law", OO("FC25", "FC26", "FC28", "FC37"), [
        S("thermo", "Check the food in the case with a probe thermometer.", { ask: { kind: "number", label: "Food temperature", unit: "°F" } }),
        S("check", "Check it reads 135°F or hotter."),
        W("phone", "Tell the person in charge if it is colder.", `Hot food is held at 135°F or above (Food Code 3-501.16). ${HOT_REHEAT}`),
      ], { phase: "open", interval: "At opening and during the day" }),
      op("hcase.load", "Stock the case", "operate", "law", OO("FC26", "FC60"), [
        W("stop", "Put in only food that is already hot.", HOT_REHEAT),
        S("food", "Keep food behind the glass, covered or wrapped."),
      ], { phase: "open" }),
      op("hcase.clean", "Clean the case", "clean", "law", OO("FC16", "FC24", "FC33"), [
        S("power", "Turn off the case."),
        S("box", "Take out the food and trays."),
        S("soap", "Wash, rinse and sanitize the trays."),
        S("clean", "Wipe the inside and the glass."),
      ], { phase: "close" }),
    ],
  },
  /* EQUIPMENT_BODY */
];
/* EQUIPMENT_END */

/** Every object guide for a state: the generic ones plus that state's equipment words (cached). Utah's is OBJECT_TYPES. */
const TYPES_BY_STATE = new Map<UsState, ObjectType[]>();
export function objectTypesFor(state?: UsState | string | null): ObjectType[] {
  const st = stateOrDefault(state);
  let list = TYPES_BY_STATE.get(st);
  if (!list) { list = [...COFFEE, ...KITCHEN, ...DISH_AND_HOUSE, ...buildEquipment(objectopsWordsFor(st))]; TYPES_BY_STATE.set(st, list); }
  return list;
}
export const OBJECT_TYPES: ObjectType[] = objectTypesFor("UT");

/**
 * "With what" for the product catalog (products.ts PRODUCT_PURPOSES, built in parallel on feat/buy-anywhere): op id → the
 * purposes of the products its steps use, in the same snake_case as withwhat.ts ProductPurpose. The catalog maps each
 * purpose to products and buy links; this file never names a product to buy. Only the ops added with the equipment
 * library (2026-09-30) are listed; the rest are named by surfaces.ts `products`. Every id here resolves to a catalog
 * purpose, directly or by products.ts PURPOSE_ALIASES (P8, test/op-purposes-catalog.test.ts). Four rows name what their
 * steps' sources say and the first list missed: fwtank.fill "Put the caps back on" (Food Code 5-304.13), keg.nitro
 * "mild dish soap" (Cornelius EQ60), cb.clean "detergent and a cloth" (Star EQ213), dcase.clean "mild soap", no chlorine
 * on the steel (True EQ272).
 *
 * Clean ops that named no supply (measured 2026-10-10 on main 3db1f744: 15 of 91 business clean ops) got the rows their
 * steps' own words and sources name, and nothing else: a "wipe" or "dry it" step uses wiping cloths (Food Code 3-304.14 is the
 * catalog rule for cloths used on equipment); the La Marzocco wand row says "a damp cloth" (OC18), the barista row "a
 * microfiber towel" (CL3), the Breville home row "a towel" (A3); Vulcan names "a griddle scraper" (OK45) and Star washes the
 * grease drawer "in the same way as an ordinary cooking utensil" (OK40), which Food Code 4-603.14(A) does with detergent
 * (TG9), as it does the sinks and drainboards; the floor guide's own list is "Mop and bucket" (task guide close-floors) and the
 * mopping row puts out a wet-floor sign (HK17); "Sweep the pad" is a broom. No chemical was added that a source does not
 * name. The ones whose sources name no supply at all are NO_SUPPLY_OPS, each with its reason.
 */
export const OP_PURPOSES: Record<string, string[]> = {
  "espresso.purge-group": ["wiping_cloths"],
  "espresso.wand-purge": ["wiping_cloths"],
  "espresso.home-rinse": ["dish_towels"],
  "freezer.clean": ["wiping_cloths"],
  "griddle.close": ["griddle_scraper", "dish_detergent"],
  "sink3.close": ["dish_detergent"],
  "mop.floors": ["kitchen_mop", "wet_floor_sign"],
  "dumpster.clean": ["broom_dustpan"],
  "syrup.clean": ["wiping_cloths"],
  "glass.clean": ["cooktop_cleaner", "cooktop_scraper"],
  "induction.clean": ["cooktop_cleaner", "cooktop_scraper"],
  "gas.clean": ["grate_cleaner"],
  "floor.vinyl": ["neutral_floor_cleaner"],
  "espresso.backflush-water": ["blind_basket"],
  "espresso.backflush-cleaner": ["blind_basket", "espresso_cleaner"],
  "espresso.soak-baskets": ["espresso_cleaner"],
  "espresso.wand-soak": ["wand_cleaner"],
  "espresso.screen": ["espresso_cleaner", "group_brush"],
  "espresso.descale": ["descaler"],
  "espresso.water": ["water_hardness_strips"],
  "grinder.tablets": ["grinder_tablets"],
  "batch.sprayhead": ["brewer_cleaner"],
  "ice.clean": ["ice_machine_cleaner", "ice_machine_sanitizer"],
  "griddle.deep": ["griddle_cleaner"],
  "coven.clean": ["oven_cleaner"],
  "probe.clean": ["dish_detergent", "food_contact_sanitizer"],
  "boards.clean": ["dish_detergent", "food_contact_sanitizer"],
  "boards.check": ["cutting_board"],
  "canopener.clean": ["dish_detergent", "food_contact_sanitizer", "can_opener_brush"],
  "canopener.blade": ["can_opener_blade"],
  "dumpster.close": ["trash_bags"],
  "msink.clean": ["all_purpose_cleaner"],
  "fwtank.fill": ["drinking_water_hose", "hose_caps"],
  "fwtank.caps": ["drinking_water_hose", "hose_caps"],
  "fwtank.sanitize": ["water_tank_sanitizer"],
  "gen.oil": ["small_engine_oil"],
  "gen.fuel": ["fuel_can"],
  "grease.daily": ["waste_oil_container"],
  "walkin.clean": ["mild_detergent"],
  "wfreezer.clean": ["mild_detergent"],
  "wfreezer.door": ["strip_curtain"],
  "walkin.door": ["strip_curtain"],
  "hhc.clean": ["mild_detergent"],
  "soup.close": ["mild_detergent", "nylon_scrub_pad"],
  "soup.descale": ["white_vinegar"],
  "lamp.clean": ["mild_detergent"],
  "skettle.clean": ["dish_detergent", "nylon_brush", "food_contact_sanitizer"],
  "roaster.clean": ["dish_detergent", "nylon_scrub_pad"],
  "chafer.setup": ["chafing_fuel", "long_lighter"],
  "chafer.fuel": ["chafing_fuel"],
  "chafer.close": ["chafing_fuel_snuffer"],
  "chafer.clean": ["dish_detergent", "food_contact_sanitizer"],
  "rice.clean": ["dish_detergent"],
  "waffle.use": ["cooking_spray"],
  "waffle.clean": ["dish_detergent"],
  "panini.open": ["salt_free_release_agent"],
  "panini.season": ["salt_free_release_agent"],
  "panini.close": ["mild_detergent"],
  "panini.carbon": ["carbon_remover_aluminum_safe"],
  "ctoast.clean": ["mild_detergent"],
  "soda.close": ["dish_detergent", "food_contact_sanitizer"],
  "soda.lines": ["beverage_line_sanitizer"],
  "soda.co2": ["cylinder_safety_chain"],
  "tea.brew": ["iced_tea_filter"],
  "tea.close": ["dish_detergent", "food_contact_sanitizer", "deliming_spring"],
  "urn.clean": ["dish_detergent"],
  "urn.delime": ["white_vinegar", "urn_faucet_brush"],
  "syrup.close": ["syrup_pump_caps"],
  "syrup.date": ["date_labels"],
  "whip.fill": ["cream_chargers"],
  "whip.clean": ["dish_detergent", "whipper_cleaning_brush"],
  "frother.clean": ["dish_detergent"],
  "keg.lines": ["caustic_line_cleaner", "chemical_gloves", "safety_glasses"],
  "keg.acid": ["acid_line_cleaner"],
  "keg.nitro": ["mild_detergent", "beverage_line_cleaner", "beverage_line_sanitizer"],
  "keg.co2": ["cylinder_safety_chain"],
  "wfilter.check": ["water_filter_cartridge", "water_prefilter"],
  "wfilter.change": ["water_filter_cartridge"],
  "cb.run": ["grill_brush"],
  "cb.clean": ["grill_cleaner", "dish_detergent"],
  "sal.clean": ["dish_detergent", "mild_detergent"],
  "combi.clean": ["combi_cleaner_tabs", "combi_care_tabs", "chemical_gloves", "safety_glasses"],
  "combi.wipe": ["mild_detergent"],
  "combi.descale": ["descaler", "chemical_gloves"],
  "prep.close": ["dish_detergent", "food_contact_sanitizer", "baking_soda"],
  "prep.coil": ["coil_brush"],
  "dcase.clean": ["dish_detergent", "food_contact_sanitizer", "mild_detergent"],
  "hcase.clean": ["dish_detergent", "food_contact_sanitizer", "wiping_cloths"],
  "dcase.coil": ["coil_brush"],
  "fp.clean": ["dish_detergent", "food_contact_sanitizer"],
  "ib.clean": ["dish_detergent", "food_contact_sanitizer"],
  "fmixer.clean": ["dish_detergent", "food_contact_sanitizer"],
  "fmixer.service": ["food_grade_lubricant"],
  "sv.clean": ["dish_detergent"],
  "sv.descale": ["white_vinegar"],
  "hsink.clean": ["hand_soap", "paper_towels"],
  "ksink.clean": ["dish_detergent", "food_contact_sanitizer"],
};

/**
 * Clean ops that rightly need no supply: their sources say water only, or a machine's own drain-and-dry steps, and name no
 * cleaner, cloth or tool (gate test/cleaning-guide-gaps.test.ts G1: every business clean op has a supply list or is here, and
 * every entry here really has none). Reason first, then the research rows it rests on. Adding a supply to one of these needs a
 * source that names it; then the entry comes out.
 */
export const NO_SUPPLY_OPS: Readonly<Record<string, { why: string; rows: string[] }>> = {
  "cblender.rinse": { why: "Water only: Vitamix says rinse the container right after use so nothing dries on.", rows: ["object-operations#OK83"] },
  "ib.rinse": { why: "Water only: Robot Coupe runs the blade in very hot water (70°C or hotter) for 30 seconds after each use.", rows: ["object-operations#EQ236"] },
  "keg.close": { why: "Water only: the Brewers Association rinses faucets and drip trays with fresh water at close and lets them air-dry.", rows: ["object-operations#EQ56"] },
  "wwtank.dump": { why: "Water only: the state's mobile food rule and the Food Code say flush and drain the waste tank at the servicing area; neither names a cleaner.", rows: ["object-operations#EQ313", "object-operations#FC57"] },
  "dmh.close": { why: "The machine's own steps: Hobart drains it, empties the strainer basket, pan and standpipe, and leaves the door open to dry; it names no cleaner.", rows: ["object-operations#OK90", "object-operations#OK91"] },
  "vac.filter": { why: "Water only: Hoover says rinse a dirty filter or replace it, then let it dry at least 24 hours; no cleaner.", rows: ["object-operations#OH93", "object-operations#OH74"] },
  "dml.close": { why: "The machine's own steps: Hobart drains it, empties the strainer basket, pan and standpipe, and leaves the door open to dry; it names no cleaner.", rows: ["object-operations#OK90", "object-operations#OK91"] },
};
